/**
 * Flying.
 *
 * ── Aim, hull, head ─────────────────────────────────────────────────────────
 *
 * Three things point in three directions and it matters that they are separate.
 *
 * **Aim** follows the mouse exactly, one to one. It is where you are asking the
 * ship to point, and the reticle draws it, so the control never feels laggy
 * even when the ship is.
 *
 * **Hull** chases the aim at whatever rate its thrusters afford. The gap
 * between the two *is* the handling — a heavy ship lags its reticle, a nimble
 * one sits on it — and it is the reason aim is drawn separately rather than the
 * crosshair simply being welded to the nose.
 *
 * **Head** is where you are looking. Normally it rides the hull. Holding the
 * middle mouse decouples it so you can look around without turning, and it then
 * stays where you left it: a look you cannot hold is not a look. `V` recentres
 * it, and only switches camera once it is already centred.
 *
 * ── The floating origin ─────────────────────────────────────────────────────
 *
 * The ship never moves. The world moves past it.
 *
 * A system is millions of units across, and every custom shader in this project
 * computes its world position as `modelMatrix * position` — which the GPU does
 * in single precision. At a million units a float32 resolves to about six
 * centimetres, so a stationary ship visibly trembled: the shake was the vertex
 * positions snapping between adjacent representable values as the numbers
 * ticked over.
 *
 * So `shipAt` holds the true position, and everything drawn lives under
 * `worldRoot`, which is translated by `-shipAt` each frame. In the frame that
 * actually reaches the GPU the ship sits at the origin and its surroundings are
 * a few hundred units away, where a float32 resolves to microns.
 *
 * The cost is a second coordinate system. Scene space is *shifted*; `shipAt`
 * and the saved flight state are *true*. `trueOf()` converts, and the rule is
 * that anything compared against `shipAt` goes through it.
 */

import {
  cargoWeight,
  stow,
  partCargo,
  FACTIONS,
  SHIP_DEPTH,
  stepOrientation,
  pointNose,
  rotateToward,
  angleBetween,
  quatFromAxisAngle,
  quatNormalise,
  quatMultiply,
  quatRotate,
  type OrientationState,
  type Quat,
  canDisengageWarp,
  canEngageWarp,
  commandedSpeed,
  fitsBerth,
  dockApproach,
  dockVolume,
  massLockStrength,
  boundsOf,
  computeShipPerformance,
  activeComponentTypes,
  drawScaleOf,
  flightTime,
  getStationGeometry,
  getSystem,
  getSystemSummary,
  handlingOf,
  stepHeat,
  heatTarget,
  heatDamage,
  radiantHeat,
  collapse,
  canBellyLand,
  mayDock,
  setDown,
  type SetDown,
  bellyLand,
  BELLY_LANDING_TOLL,
  type ComponentType,
  strafeAuthority,
  turnAuthority,
  warpHasStalled,
  MASS_LOCK_REACH,
  canEngageCruise,
  impactAt,
  contactLines,
  shipExtentOf,
  shipFootprint,
  stationImpactAt,
  damageHull,
  hullIntegrity,
  isHullLost,
  type Impact,
  cruiseArrived,
  cruiseHasStrayed,
  cruiseShouldWarp,
  cruiseStandoff,
  cruiseThrottle,
  wrapAngle,
  BINDINGS,
  CARD_ORDER,
  CONTROL_POST,
  Navigator,
  PAD_SIZES,
  STATION_SCALE,
  advanceTutorial,
  canEngageJump,
  dockingFee,
  smallestBerthFor,
  clusterJumpRangeFor,
  confirmProgress,
  jumpRangeFor,
  pilotHasControl,
  stepJump,
  JUMP_RUN_ACCELERATION,
  NO_JUMP,
  JUMP_CALIBRATION,
  LESSONS,
  isTutorialMission,
  JUMP_MAX_SPEED,
  JUMP_TUNNEL_SPEED,
  crossingVeil,
  runAcceleration,
  systemsPowered,
  inTunnel,
  readyToSwap,
  type JumpPhase,
  type JumpSequence,
  tutorialSpawnFacing,
  beatFor,
  tutorialIsOver,
  makeOfficial,
  Rng,
  type TutorialStep,
  isAction,
  keyLabel,
  pairLabel,
  type Action,
  type DockApproach,
  type ShipGraph,
  type SystemId,
  type ThrusterSet,
  type CargoItem,
  getCluster,
  asteroidHit,
} from '@interspacial/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

import { GAME_SCALE, buildSystemScene, padSurface, type MarkerKind } from './systemScene';
import { stationImpact } from './stationCollision';
import { createWreckage } from './wreckage';
import { buildShipMesh } from './shipMesh';
import { createRemoteShips, sceneLookups } from './remoteShips';
import {
  NOZZLE_DEPTH,
  THRUST_FLAME,
  TUNNEL_EXHAUST,
  TUNNEL_FLAME,
  WARP_FLAME,
  createThrusterPlume,
} from './thrusterPlume';
import { mountViewport } from './viewport';
import type { StationVolume } from '@interspacial/shared';
import { peerClock, type ChatLine, type PeerFeed } from '../net/peers';
import { createSpaceBackground } from './spaceBackground';
import { createSystemSky } from './systemSky';
import { createTargetMarkers, formatDistance, sightBlocked } from './targetMarkers';
import {
  FlightHud,
  HUD_BACKING,
  HUD_EDGE,
  HUD_ORANGE,
  HUD_TEXT,
  useConsoleClearance,
  type Contact,
  type NavReadout,
} from './FlightHud';
import { FlightRadio, type RadioChannel, type RadioMessage } from './FlightRadio';
import { createControlsCard } from './controlsCard';
import { RadioAlert, type Alert } from './RadioAlert';
import { useRadio } from './useRadio';
import { createJumpMarkers } from './jumpMarkers';
import { subscribeRoute } from './routeChannel';
import { readTarget } from './SearchPanel';
import { subscribePin } from './SearchPanel';
import { WarpGas } from './WarpTunnel';
import { createWarpStreaks } from './warpStreaks';
import { DamageVignette } from './DamageVignette';
import { HullContents, hullSummary, hullUrgent } from './HullPanel';
import { HoldContents, holdSummary, holdUrgent } from './CargoPanel';
import { ShipConsole, type ConsoleSection } from './ShipConsole';
import { TouchControls, coarsePointer } from './TouchControls';
import { SHOW_KEY_HINTS, tapProps } from './hudScale';
import { clearLog, note } from './shipLog';
import { powerStyle, powerVisible, reportPower, usePower } from './SystemsPower';
import { jumpTargets } from '../net/jumpTargets';
import type { FlightState, Triple } from '../flightState';

/**
 * What the cockpit card says.
 *
 * Read off the binding table rather than written out again. The card, this
 * loop and the tutorial all used to describe the controls independently, and
 * any two of them could disagree without anything failing — the card would
 * simply be lying. See `input.ts`.
 */
const CONTROLS = CARD_ORDER.map((action) => {
  // The paired controls are one line apiece, because nobody thinks of throttle
  // up and throttle down as two things.
  const pairs: Partial<Record<Action, [Action, string]>> = {
    throttleUp: ['throttleDown', 'throttle'],
    strafeLeft: ['strafeRight', 'strafe'],
    rollLeft: ['rollRight', 'roll'],
    up: ['down', 'up / down'],
  };

  const pair = pairs[action];
  return pair
    ? { key: pairLabel(action, pair[0]), action: pair[1] }
    : { key: keyLabel(action), action: BINDINGS[action].description };
});

const FOV = 70;

/**
 * How wide the nose cross is drawn, in pixels.
 *
 * Deliberately smaller than the aim ring. The ring is where the ship has been
 * *asked* to point and the cross is where it is actually pointing; the ring is
 * the thing being aimed and the cross is a reading, so the reading is the
 * quieter of the two.
 */
const NOSE_CROSS = 11;

/**
 * How far the aim may get ahead of the hull, in screen widths.
 *
 * A circle around where the ship is actually pointing, not around where the
 * camera is looking. One and a half widths is comfortably off the edge of the
 * view — far enough that ordinary flying never touches it — while keeping the
 * reticle a turn of the head away rather than somewhere behind you.
 */
const AIM_RADIUS = 1.5;

/**
 * The hull's own axes, as the mesh is built: nose forward, deck normal up,
 * starboard to the right. Every rotation the cockpit composes is about one of
 * these three, and naming them is the difference between reading the flight
 * loop and decoding it.
 */
const NOSE_AXIS = { x: 0, y: 1, z: 0 };
const DECK = { x: 0, y: 0, z: 1 };
const STARBOARD = { x: 1, y: 0, z: 0 };

/**
 * How far the head may turn in the seat, in radians.
 *
 * A neck rather than a singularity. The ship itself has no limit any more, but
 * looking is done *from* the hull and a head that could spin freely would let
 * the player lose track of which way the ship is pointing — which is the one
 * thing free look exists to avoid.
 */
const HEAD_LIMIT = Math.PI / 2;

/**
 * How far a finger turns the ship, per pixel dragged.
 *
 * Higher than the mouse's, because the gestures are not the same shape: a mouse
 * can be lifted and re-placed to keep turning, and a finger runs out of screen.
 * A drag across a phone should be worth most of a turn, or aiming becomes a
 * series of swipes.
 */
const TOUCH_SENSITIVITY = 0.0042;

/**
 * What a wheel's line and page units are worth in pixels.
 *
 * `WheelEvent.deltaMode` says which of the three a given event is counting in,
 * and the answer varies by browser and by device — Firefox reports lines for a
 * mouse where Chrome reports pixels. Roughly a text line and roughly a screen;
 * they only have to be close enough that a notch is not a whole turn.
 */
const WHEEL_LINE = 16;
const WHEEL_PAGE = 400;

/**
 * The heading these two angles describe, as a rotation.
 *
 * `Rz(yaw) · Rx(pitch)`, which is the `ZXY` Euler order the hull used to be
 * built with, minus the roll term. Kept as one function because it is the
 * boundary between the old representation and the new one: the mouse still
 * moves a yaw and a pitch — deliberately, so this change is only about how the
 * orientation is *carried* — and this is where that becomes a rotation.
 *
 * When the input moves to rotations too, this is what stops being needed.
 */
function headingFrom(yaw: number, pitch: number): Quat {
  return quatMultiply(
    quatFromAxisAngle({ x: 0, y: 0, z: 1 }, yaw),
    quatFromAxisAngle({ x: 1, y: 0, z: 0 }, pitch),
  );
}

/** How far the eye sits above a seat's own anchor. */
const EYE_HEIGHT = 1;

/** How far the external camera's pivot floats above the roof. */
const CHASE_LIFT = 1;

/** How far back the external camera sits, in ship depths. */
const CHASE_DISTANCE = 6;

/** How far the scanner reaches, in world units. */
const SCANNER_RANGE = 60_000;

/**
 * How near a station has to be before its berths are tested at all.
 *
 * Every berth costs a matrix inverse per frame, a busy station has twenty of
 * them, and a system has several stations. This is comfortably past the point
 * where a deck is more than a few pixels, so nothing is ever missed.
 */
const BERTH_SEARCH_RANGE = 20_000;

/** Radians of mouse travel per pixel. */
const SENSITIVITY = 0.0022;

/**
 * How far off a star's surface a jump puts you, in world units.
 *
 * Close, and meant to be. At this range the star spans between 79° and 113°
 * against a 55° field of view, so it does not sit in the view, it *is* the
 * view — which is the intent twice over: it is a fright, and in fiction it is
 * how the drive knows where to stop. Coming out of a jump nose-first into a sun
 * should not be comfortable.
 *
 * Measured from the surface rather than the centre because star radii run from
 * 1,742 to 5,074 units; from the centre this would be inside every star in the
 * galaxy.
 */
const ARRIVAL_STANDOFF = 1000;

/**
 * How long the run in from the edge takes, in seconds.
 *
 * Matched to the tunnel's fade-out, so the ship is still decelerating as the
 * streaks thin and has settled by the time they are gone. Appearing at rest and
 * then having the view uncovered was a *reveal*; this is an arrival, and the
 * difference is entirely that something was still moving when you could see
 * again.
 */
const ARRIVAL_GLIDE = 2.6;

/**
 * How fast the ship has to actually be going forward before the thrusters show
 * a plume, in world units a second.
 *
 * Low, because this is a gate rather than a fade: it exists to tell a drive that
 * is *working* from one that is merely switched on. Held against a mass lock the
 * throttle is wide open and nothing is moving, and a full plume there says the
 * ship is accelerating when it is not.
 *
 * Well under the precision set's ceiling (`CRUISE_SPEED * 0.12`, about 14) so
 * manoeuvring around a pad still lights the small nozzles, which is the one
 * place a plume is doing real work at walking pace.
 */
const PLUME_MIN_SPEED = 1.5;

/**
 * How far beyond the standoff the run in starts, in world units.
 *
 * Outside the system proper: the median system's outermost planet orbits at
 * about 44,000 units, so ninety thousand puts the arrival genuinely on the far
 * side of everything and closing. That is what makes it read as covering a
 * distance rather than as being placed.
 */
const ARRIVAL_ENTRY = 90_000;

/**
 * The radius of the black sphere a crossing puts you inside, in world units.
 *
 * Large enough to be a place the ship is *in* rather than a lens on the camera,
 * and small enough that everything in the system is outside it and therefore
 * hidden. Only the hull is within — you keep your own ship, which is the whole
 * conceit of the crossing.
 *
 * Nothing else can plausibly be nearer than this while it matters: warp cannot
 * be lit under mass lock, so the ship is never near a body during a run-up, and
 * the arrival is thousands of units off a star.
 */
const CROSSING_SHELL = 1200;

/** How far clear of the plate a release leaves you, in world units. */
const UNDOCK_LIFT = 0.5;

/**
 * Below this, a contact with a station is a parking accident, not a collision.
 *
 * The whole proportional response — undo the step, bounce back half of what came
 * in — assumes there was motion worth undoing and momentum worth halving. At a
 * drift there is neither: the step is a fraction of a unit, so a hull already
 * overlapping stays overlapping, and half of nothing does not separate it.
 */
const STATION_DRIFT_SPEED = 5;

/** Seconds of damage immunity after a collision. See `damageCooldown`. */
const DAMAGE_IFRAMES = 0.5;

/**
 * How quickly the autopilot pulls the aim back onto its target, per second.
 *
 * Blended rather than snapped, because the mouse has to be able to win. At this
 * rate a nudge moves the ship and is undone over about a second — you can look
 * around, and you can shove the nose off the line hard enough to trip the
 * thirty-degree abort if that is what you meant.
 */
const CRUISE_STEER_RATE = 1.2;

/**
 * How near the middle of the screen a body must be to be the one you mean.
 *
 * In pixels. Also what closes a target's bracket into a full square, so the
 * marker that fills is exactly the marker cruise would fly to — the HUD is
 * telling you what the button will do.
 */
const AIM_SNAP_PIXELS = 90;

/**
 * The controls card, on the console.
 *
 * A fifth the size it started at and much closer in. The first cut was a poster
 * hanging in the cockpit; this is a card taped to the panel. `BELOW` against
 * `AHEAD` puts it about fifty degrees under the eyeline — comfortably outside
 * half of a seventy-degree field of view, so it is out of frame looking forward
 * and there when you look down.
 */
const CARD_WIDTH = 0.68;
const CARD_AHEAD = 0.5;
const CARD_BELOW = 0.6;
const CARD_TILT = Math.PI / 12.7;

export type CameraMode = 'first' | 'external';

/**
 * A ship's pose, as everyone else needs it.
 *
 * Plain numbers rather than THREE types because this crosses a boundary — it
 * goes onto the wire, and the vectors it is read from are reused every frame by
 * the loop that owns them. Copying is the point.
 */
export interface Pose {
  /**
   * What the position is measured from, or empty for absolute.
   *
   * A mass-locked ship reports its offset from the body holding it, so the
   * orbit is resolved by each receiver at frame rate rather than carried by ten
   * samples a second. See the publish site for why that stops the jitter.
   */
  anchor: string;
  x: number;
  y: number;
  z: number;
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  vx: number;
  vy: number;
  vz: number;
  /** In the warp tunnel: between systems, and not in the game while it lasts. */
  hidden: boolean;
}

export interface FlightViewProps {
  seed: string;
  systemId: SystemId;
  stationId: string;
  padId: string;
  /** The hull as it currently stands — what is flown, and what takes damage. */
  design: ShipGraph;
  /** The design it was built to, which is what integrity is measured against. */
  blueprint: ShipGraph;
  /**
   * What other pilots in the system have said.
   *
   * Owned by the app rather than by either view, so moving between the cockpit
   * and a berth does not lose the conversation.
   */
  chat?: readonly ChatLine[];
  /** Say something. `/alert ...` spends a beacon and interrupts everybody. */
  onChat?: (channel: string, text: string) => void;
  /** This player's own uuid, for telling their lines from everyone else's. */
  uuid?: string;
  /** Handed the damaged hull after every collision. */
  onDamage: (hull: ShipGraph) => void;
  /**
   * Where the ship is, ten times a second, for anyone else in the system.
   *
   * Absolute — the same coordinates the room speaks in, not the shifted scene
   * space this view draws in. The floating origin is a rendering trick and stops
   * at this boundary; sending scene coordinates would put every remote ship at
   * an offset that changes as the sender moves.
   *
   * Optional, and called only if given: flight does not depend on anyone
   * listening, and with no realtime server nobody is.
   */
  onPose?: (pose: Pose) => void;
  /**
   * Everybody else in the system, or null when flying alone.
   *
   * Read every frame rather than subscribed to: peers arrive ten times a second
   * and belong to the scene, not to React. Null is an ordinary state — no
   * realtime server, or not joined yet — and means an empty sky.
   */
  peers?: PeerFeed | null;
  /** The hull is gone: it leaves service, and so does everything aboard it. */
  /** The ship is gone. Carries the station it is towed to, if there is one. */
  onHullLost: (towedTo?: string) => void;

  /**
   * Wipe the account and start again, introduction and all.
   *
   * Offered here because losing a first ship in the first ten minutes is exactly
   * when somebody wants to begin properly — and because gating the introduction
   * behind "new account" means the only people who can reach it are the people
   * who have already seen it.
   */
  onResetAccount?: () => void;

  /**
   * A feature has just been used, by name.
   *
   * The loop can see the action; only the account knows whether it is the first
   * time, and that lives above this view. Fired on every use — the decision is
   * the server's, and this asking twice is cheaper than this trying to remember.
   */
  onFeatureUsed?: (lessonId: string) => void;

  /**
   * Show a lesson in full, whether or not it has been seen.
   *
   * Separate from `onFeatureUsed`, which *asks* whether this is the first time
   * and shows nothing if it is not. This is the player deliberately going back
   * for something, so it always opens — including for somebody who has the loud
   * half switched off, which is the only way they can read one at all.
   */
  onOpenLesson?: (lessonId: string) => void;

  /**
   * How far through the introduction, or null when it is not running.
   *
   * Driven from here because every condition it turns on — distance, speed,
   * mass lock, thruster set, gear — is something this loop already computes.
   */
  tutorial?: TutorialStep | null;
  onTutorialStep?: (step: TutorialStep) => void;
  /** What Control calls the player. Their chaostree display id. */
  callsign?: string;
  /**
   * Where to be dropped, relative to the berth, on a tutorial start.
   *
   * Null for an ordinary launch, which begins on the pad.
   */
  spawnOffset?: { x: number; y: number; z: number } | null;
  /**
   * Overdrive cores in the hold.
   *
   * Only ever read, never spent here — the jump module uses it to say whether a
   * crossing is possible, and the crossing itself is the caller's to perform.
   */
  cores?: number;
  /** The hold, so it can be read without landing. Deliveries have clocks. */
  cargo?: readonly CargoItem[];
  /** Hold space, for the same panel. */
  capacity?: number;
  /**
   * Put something in the hold from flight.
   *
   * Only wreckage uses this: a collector sweeping up a part that came off the
   * ship. Nothing else in a flight adds to cargo.
   */
  onCargoChange?: (cargo: CargoItem[]) => void;
  /**
   * Whether the destination system is ready to be arrived in.
   *
   * The tunnel holds until this is true however long its minimum has run — the
   * minimum is a floor on how quick a crossing may *look*, not a promise that
   * the galaxy will have caught up by then.
   */
  jumpReady?: boolean;
  /** Every phase change of a crossing, so the caller can drive the transition. */
  onJumpPhase?: (phase: JumpPhase, systemId: string | null) => void;
  /**
   * The view has gone dark; change the world behind it.
   *
   * Fired once, part-way into the crossing rather than at the end of it. The
   * caller is expected to switch systems on this — which rebuilds this view's
   * entire scene mid-crossing, and is exactly why the sequence is held in a ref
   * that survives the rebuild.
   */
  onJumpSwap?: (systemId: string) => void;
  /**
   * Arrive beside this system's star rather than at a berth.
   *
   * A jump does not put you on a pad. It drops you in open space with the local
   * sun in front of you, which is both the only landmark that always exists and
   * the one that tells you at a glance where you have ended up.
   */
  /**
   * Something the app wants said on this view's radio.
   *
   * Pushed when its id changes, so the same notice arriving twice is announced
   * once. The app cannot reach the radio itself — each view keeps its own log —
   * so this is the way in.
   */
  notice?: Alert | null;
  arriveAtStar?: boolean;
  /** Given the berth actually flown into, so arriving somewhere new works. */
  onDock: (berth: {
    stationId: string;
    padId: string;
    fee: number;
    /** Set down without gear, because there was none. See `bellyLand`. */
    belly?: boolean;
  }) => void;
  /** Open the chart mid-flight. Consulting it, not travelling by it. */
  onOpenStarmap?: () => void;
  /**
   * Something is over the top of the cockpit — the chart, most often.
   *
   * The ship keeps flying, which is the point: cruise carries on, warp carries
   * on, and the configuration is still here when the overlay closes. What stops
   * is *input*. Keys typed at a map should not also fly the ship, and the mouse
   * belongs to whatever is on top.
   */
  suspended?: boolean;
  /**
   * Where the ship was, and where it is.
   *
   * Read once on mount and written continuously, so leaving for the chart and
   * coming back finds the ship where it was left rather than back on its pad.
   * A ref rather than state: this changes sixty times a second and nothing
   * should re-render because of it.
   */
  state: React.MutableRefObject<FlightState | null>;
}

/** The berth we are close enough to do something about. */
interface DockPrompt {
  stationId: string;
  stationName: string;
  padId: string;
  approach: Exclude<DockApproach, 'clear'>;
  /** What taking a berth this size costs a hull this size. Usually nothing. */
  fee: number;
  /**
   * How this ship would touch down, if it did.
   *
   * `raised` is the case that was missing entirely: gear fitted and stowed, which
   * is not a landing. Docking was decided on position alone, so that state
   * docked normally and for free.
   */
  how: SetDown;
  /**
   * Coming down without gear, because there is none left to come down on.
   *
   * Changes what the prompt offers rather than whether it appears — the landing
   * is allowed, it just costs the hull. See `bellyLand`.
   */
  belly: boolean;
}

/** What the top-left panel is looking at. */
interface LocalBody {
  name: string;
  kind: string;
  detail: string;
  distance: number;
  locked: boolean;
}

interface Gauges {
  speed: number;
  maxSpeed: number;
  throttle: number;
  heat: number;
  contacts: Contact[];
  massLock: string | null;
  /**
   * What the jump module is reporting, or null when it is a switch.
   *
   * Published rather than derived in the HUD so the caption and the autopilot
   * log show the same string — it is recomputed on a one-second tick, and two
   * callers deciding independently when that second elapsed would drift.
   */
  jumpCalibration: string | null;
  warp: boolean;
  warpBlocked: string | null;
  /** Why the jump module will not come up, when it will not. */
  jumpBlocked: string | null;
  /** The crossing under way, if any. Drives the commit dial and the tunnel. */
  crossing: JumpSequence;
  /** Whether the ship's electrics are running. False across a crossing. */
  powered: boolean;
  /** The destination under the reticle, named, when there is one. */
  jumpAim: string | null;
  /** Where you are going, and which way to look for it. */
  nav: NavReadout;
  dock: DockPrompt | null;
  local: LocalBody | null;
  /** What cruise is flying to, or would fly to. */
  target: string | null;
  /** Why it cannot be switched on, when it cannot. */
  cruiseBlocked: string | null;
  cruising: boolean;
  /** 1 when whole, 0 when the hull is gone. */
  integrity: number;
  /** What we last hit, and how hard. Cleared once it has been shown. */
  collision: {
    body: string;
    severity: Impact['severity'];
    speed: number;
    /** Counts up per charged hit, so the screen can tell two apart. */
    at: number;
  } | null;
}

export function FlightView({
  seed,
  systemId,
  stationId,
  padId,
  design,
  blueprint,
  onDamage,
  onPose,
  peers,
  chat,
  onChat,
  uuid,
  onHullLost,
  onResetAccount,
  onFeatureUsed,
  onOpenLesson,
  onDock,
  onOpenStarmap,
  suspended = false,
  state,
  tutorial = null,
  onTutorialStep,
  callsign = 'pilot',
  spawnOffset = null,
  cores = 0,
  cargo = [],
  capacity = 0,
  onCargoChange,
  jumpReady = false,
  onJumpPhase,
  onJumpSwap,
  notice = null,
  arriveAtStar = false,
}: FlightViewProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const labelLayerRef = useRef<HTMLDivElement>(null);
  const jumpLayerRef = useRef<HTMLDivElement>(null);
  const reticleRef = useRef<HTMLDivElement>(null);
  /** The cross on the nose. See where it is drawn. */
  const noseRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);

  const [mode, setMode] = useState<CameraMode>('first');
  const [seatIndex, setSeatIndex] = useState(0);
  const [locked, setLocked] = useState(false);

  /*
   * The hold, live.
   *
   * The flight loop is built once and runs for the whole flight, so it captures
   * whatever the hold was when it started. A collector sweeping a part into a
   * stale copy would write the hold back to what it was minutes ago and quietly
   * eat anything picked up since.
   */
  const hold = useRef({ cargo, capacity });
  hold.current = { cargo, capacity };
  /**
   * Whether this is a spawn into open space rather than a departure from a pad.
   *
   * A saved flight carries its own configuration; a `spawnOffset` means we are
   * being placed out in the black. Only the third case — neither — is actually
   * leaving a berth.
   */
  const inTheOpen = !state.current && spawnOffset !== null;

  // Undocking puts you on the precision set: you are inside a station with
  // metres of clearance, and the main drive's first tap would put you through
  // the deck next door. None of that is true three thousand units out, where
  // the precision set is a fourteen-unit-per-second speed limit and the reason
  // the first leg of the tutorial felt broken.
  const [set, setSet] = useState<ThrusterSet>(
    state.current?.set ?? (inTheOpen ? 'main' : 'precision'),
  );
  // Down when you undock, because you are standing on it — and up when you
  // spawn in open space, because there is nothing out there to stand on.
  const [gear, setGear] = useState(state.current?.gear ?? !inTheOpen);
  const [cruise, setCruise] = useState<string | null>(null);
  // The jump module, which is a chart rather than a drive: bringing it up draws
  // where you could go, and going there is a separate act.
  const [jump, setJump] = useState(false);
  const [readout, setReadout] = useState({ system: '', operator: '' });
  /*
   * The log and the interrupt queue, with the room's traffic already folded in.
   *
   * Shared with the berth, which keeps a radio of its own for the same reasons
   * and used to keep an identical copy of the folding as well.
   */
  const {
    messages: radio,
    setMessages: setRadio,
    alerts,
    setAlerts,
  } = useRadio(chat, uuid);


  /**
   * A second hand, for anything in the HUD that counts down.
   *
   * The gauges publish off the render loop, which is far too often for a
   * deadline measured in minutes. One tick a second, owned here.
   */
  /** Whether the hold list is up. The switch for it lives on the console. */
  const [holdOpen, setHoldOpen] = useState(false);
  const [hullOpen, setHullOpen] = useState(false);

  /**
   * Bringing the console up when the view opens.
   *
   * A ship coming off a pad is starting its systems, so it gets the same tube
   * warming up that an arrival does. It costs nothing — the machinery is
   * already there for the crossing — and it means undocking begins with the
   * ship doing something rather than with a console that was simply always on.
   *
   * Only on mount: a jump does *not* remount this view, it rebuilds the scene
   * inside it, so the crossing's own blackout stays in charge of that case.
   */
  const [booting, setBooting] = useState(true);
  useEffect(() => {
    const timer = window.setTimeout(() => setBooting(false), 120);
    return () => window.clearTimeout(timer);
  }, []);





  const [clockTick, setClockTick] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setClockTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  /**
   * Who is on the other end of the radio.
   *
   * Seeded off the station, so the same controller is on shift every time you
   * come back to it — a voice that changed name between transmissions would be
   * a different person each time.
   */
  const controller = useMemo(
    () => makeOfficial(Rng.forEntity(seed, stationId, 'control'), CONTROL_POST),
    [seed, stationId],
  );

  /** Where Control is sitting, for the alert header. */
  const controlPost = useMemo(() => {
    const system = getSystem(seed, systemId);
    return system.stations.find((s) => s.id === stationId)?.name ?? system.name;
  }, [seed, systemId, stationId]);

  /**
   * The one way the landing gear moves.
   *
   * Raising it is always allowed. Lowering it under warp is not — `canEngageWarp`
   * refuses to *light* the drive with the gear down and always has, but nothing
   * stopped you dropping it once lit, so the ship could sit at eight hundred
   * units a second in a configuration it could not have reached. Both the key
   * and the console switch go through here so there is one answer.
   */
  const underWarp = useRef(false);
  /** Whether something is over the cockpit. Read by the loop and the handlers. */
  const covered = useRef(suspended);
  covered.current = suspended;
  /** Whether the scene teardown took pointer lock with it. See the rebuild. */
  const hadPointer = useRef(false);

  /**
   * Props the loop reads without wanting to be rebuilt when they change.
   *
   * The scene effect is expensive — it generates a system and builds every mesh
   * in it — so it depends on the things that define the *world*, not on things
   * that merely change while flying through it. A ref is how the loop reads the
   * latest of those without the effect listing them.
   */
  /**
   * The crossing, if one is under way.
   *
   * A ref at component level rather than a local in the scene effect, and that
   * is load-bearing: the destination is now swapped in *during* the tunnel, so
   * the scene the crossing began in is torn down and rebuilt while it is still
   * running. A local would have been reset to `idle` by that rebuild, ending
   * the crossing two seconds into a fifteen-second one.
   *
   * A ref rather than state because the loop advances it every frame off
   * elapsed time, and a re-render per frame to move a timer would be the most
   * expensive clock in the game. It reaches the HUD with the gauges, ten times
   * a second.
   */
  const crossing = useRef<JumpSequence>(NO_JUMP);

  const live = useRef({
    jumpReady,
    onJumpPhase,
    onJumpSwap,
    onPose,
    peers,
    onFeatureUsed,
    onOpenLesson,
  });
  live.current = {
    jumpReady,
    onJumpPhase,
    onJumpSwap,
    onPose,
    peers,
    onFeatureUsed,
    onOpenLesson,
  };

  /** The next hop, where the scene loop can see it without being rebuilt. */
  const route = useRef<string | null>(null);

  const routedBody = useRef<string | null>(null);
  /** The far end of the trip, named, for the console's second line. */
  const routeEnd = useRef<string | null>(null);
  const routeHopRef = useRef<{
    name: string;
    direction: { x: number; y: number; z: number };
  } | null>(null);

  /**
   * Reset the console on arriving somewhere new.
   *
   * The scene is rebuilt when the system changes, and that clears everything
   * the *loop* owns — velocity, warp, throttle all start again from nothing.
   * These three are React state and survive it, so a ship that jumped would
   * arrive with the module still up and a chart of a cluster it had left drawn
   * across the sky.
   *
   * Gear and thruster set are already where they need to be — warp cannot be
   * lit without them — but they are set anyway, because "arrive in a known
   * configuration" is a simpler promise than "arrive in whatever configuration
   * warp happened to require".
   */
  useEffect(() => {
    if (!arriveAtStar) return;

    setJump(false);
    setGear(false);
    setSet('main');
  }, [systemId, arriveAtStar]);

  useEffect(() => {
    if (!suspended) return;

    // Whatever was held goes with the focus, or the ship carries on turning
    // under a map nobody is flying from. Pointer lock goes too: the overlay
    // needs the cursor, and it is not ours to keep while it is up.
    releaseControls.current?.();
  }, [suspended]);

  /** Set by the scene, so the effect above can reach into the live loop. */
  const releaseControls = useRef<(() => void) | null>(null);

  const lowerGear = useCallback(() => {
    setGear((down) => {
      if (down) return false;
      if (underWarp.current) return false;
      return true;
    });
  }, []);

  /**
   * The live tutorial step, mirrored into a ref.
   *
   * The loop advances it sixty times a second and only publishes on a change;
   * routing every frame through React would re-render the whole view for
   * nothing.
   */
  const tutorialStep = useRef<TutorialStep | null>(tutorial);
  tutorialStep.current = tutorial;

  /** What the player last typed, for the hail to read. */
  const lastReply = useRef<string | null>(null);

  /**
   * Say a line, on the popup and in the log.
   *
   * Both, always. The popup is how you notice; the log is how you check what it
   * said after you stopped reading to fly the ship.
   */
  const transmit = useCallback(
    (step: TutorialStep) => {
      const beat = beatFor(step, { callsign });
      if (!beat.line) return;

      const id = `control-${step}`;

      // Replaces rather than queues: the newest thing Control has said is the
      // only one worth the screen, and whatever it supersedes is still in the
      // log below. Guarded so a re-render on the same step does not restart the
      // dwell on a line already being read.
      setAlerts((queue) =>
        queue.length === 1 && queue[0]!.id === id
          ? queue
          : [
              {
                id,
                from: controller.signature,
                source: controlPost,
                text: beat.line!,
                awaitsReply: beat.awaitsReply,
              },
            ],
      );

      setRadio((log) => [
        ...log.slice(-60),
        { id, channel: 'station', from: controller.rank, text: beat.line! },
      ]);
    },
    [callsign, controller, controlPost],
  );

  /**
   * `transmit`, reachable from inside the render loop.
   *
   * The loop is built once and cannot see later closures, and one beat has to
   * be spoken from in there rather than from the prop that normally drives it —
   * see where `abandoned` is handled.
   */
  const say = useRef(transmit);
  say.current = transmit;

  /** The opening hail, and every step after it. */
  useEffect(() => {
    if (tutorial && !tutorialIsOver(tutorial)) transmit(tutorial);
    if (tutorial === 'done') transmit('done');
  }, [tutorial, transmit]);
  const [gauges, setGauges] = useState<Gauges>({
    speed: 0,
    maxSpeed: 0,
    throttle: 0,
    heat: 0,
    contacts: [],
    massLock: null,
    jumpCalibration: null,
    warp: false,
    warpBlocked: null,
    jumpBlocked: null,
    crossing: NO_JUMP,
    powered: true,
    jumpAim: null,
    nav: { system: '', cluster: '', target: null, heading: '', bearing: null, jump: false },
    dock: null,
    local: null,
    target: null,
    cruiseBlocked: null,
    cruising: false,
    integrity: 1,
    collision: null,
  });

  /**
   * The greeting after a crossing, shown for a few seconds and then gone.
   *
   * It uses the same panel the jump lock does, which is deliberate: that panel
   * is the one thing that stays lit through a blackout, so the first words back
   * come from the place the last words went. Naming the system is also the
   * answer to the only question an arrival raises.
   */
  const [welcome, setWelcome] = useState<string | null>(null);
  const wasPowered = useRef(true);

  useEffect(() => {
    const back = gauges.powered && !wasPowered.current;
    wasPowered.current = gauges.powered;

    if (!back || !readout.system) return;

    setWelcome(readout.system);
    const timer = window.setTimeout(() => setWelcome(null), 5000);
    return () => window.clearTimeout(timer);
  }, [gauges.powered, readout.system]);

  const consolePower = gauges.powered && !booting;

  /** The tube, for every instrument. One clock, many boxes. */
  const power = usePower(consolePower);
  const dark = powerStyle(power);
  const lit = powerVisible(power);

  /*
   * Told to the panels drawn outside the cockpit — the lesson card, chiefly.
   *
   * Restored on the way out rather than left where it lay: leaving flight while
   * the console is dark is exactly what a crossing does, and a card raised in
   * the berth on the far side would otherwise be collapsed by a blackout that
   * ended with a view that no longer exists.
   */
  useEffect(() => {
    reportPower(power);
  }, [power]);

  // On the way out only. Cleaning up in the effect above would fire on every
  // step of the collapse — resetting to lit and back down again ten times,
  // which React batches away but which is not what the code should say.
  useEffect(() => () => reportPower({ lit: 1, height: 1, width: 1 }), []);



  /**
   * Whether this device is flown with fingers.
   *
   * Read once and kept: the answer is about the hardware rather than about the
   * window, and a grid that appeared and vanished as the pointer changed would
   * be a control you cannot rely on being there.
   */
  const [touchFlying] = useState(coarsePointer);

  const [hullLost, setHullLost] = useState(false);

  /**
   * The crossing card outlasts the rest of the console.
   *
   * It is the thing *explaining* the shutdown, so it has to still be readable
   * while the shutdown happens — a console that went dark with the explanation
   * going dark first would be exactly the fault this is meant to avoid. It
   * follows a second and a half later, on its own tube.
   */
  const [cardLive, setCardLive] = useState(true);
  useEffect(() => {
    if (consolePower) {
      setCardLive(true);
      return;
    }

    const timer = window.setTimeout(() => setCardLive(false), 1500);
    return () => window.clearTimeout(timer);
  }, [consolePower]);

  const cardPower = usePower(cardLive);

  /*
   * Hand the mouse back when the ship is gone.
   *
   * The hull-lost screen is a button, and pointer lock means the cursor is not
   * on screen to press it with — so the game ended and left you holding controls
   * that no longer do anything, with no way to reach the only thing on screen.
   * Nothing else releases it: the loop keeps running, and the canvas keeps the
   * lock until something asks for it back.
   *
   * Keyed on the *latch* rather than on integrity, because integrity is only one
   * of the two ways to die. A structural kill — losing the compartment with the
   * seat in it — leaves plenty of hull at above zero, so this never fired and the
   * death screen appeared with the pointer still captured.
   */
  useEffect(() => {
    if (!hullLost) return;
    if (document.pointerLockElement) document.exitPointerLock();
  }, [hullLost]);

  /** Whether the reset has been asked for once. It is asked for twice. */
  const [resetArmed, setResetArmed] = useState(false);


  /*
   * Anything the app asked to have said, said once.
   *
   * Keyed on the id rather than the object, because the app rebuilds its state
   * on every action and an effect watching the notice itself would repeat the
   * same transmission on the next unrelated render.
   */
  const announced = useRef<string | null>(null);
  useEffect(() => {
    if (!notice || announced.current === notice.id) return;
    announced.current = notice.id;
    setAlerts((queue) => [...queue, notice]);
  }, [notice, setAlerts]);

  // On the parts list for the same reason as `performance` below — a hit must
  // not read as a different ship.
  const seats = useMemo(
    () => design.components.filter((component) => component.type === 'pilotSeat'),
    [design.components],
  );
  const seat = seats[Math.min(seatIndex, Math.max(0, seats.length - 1))];

  /*
   * Keyed on the parts list, not on the graph.
   *
   * `damageHull` returns a new `ShipGraph` every time anything is hit, with the
   * same `components` array inside it — a dent changes what a line can take, not
   * what is bolted to the ship. Memoising on the graph therefore minted a new
   * `performance` object on every collision, and `performance` is a dependency
   * of the flight effect: each hit tore down and rebuilt the whole system scene,
   * shaders, station clutter and all. That is a two-hundred-millisecond freeze
   * at the exact moment the ship needs to be responsive.
   */
  const performance = useMemo(
    // Active parts only — a thruster behind a bulkhead pushes against nothing.
    // See `activeComponents`; the editor draws the same rule.
    () => computeShipPerformance(activeComponentTypes(design)),
    [design.components],
  );
  const hasPrecision = performance.thrust.precision > 0;
  const hasMain = performance.thrust.main > 0;

  /**
   * The hull being gone, remembered outside the scene that noticed it.
   *
   * ── Why this cannot be read straight off `gauges` ───────────────────────────
   *
   * Losing a hull is the one kind of damage that changes the *parts list*:
   * components in a compartment that fails are set adrift, so `design.components`
   * comes back a different array. `seats` and `performance` are memoised on that
   * array and both are dependencies of the flight effect — so the moment a ship
   * is destroyed the entire scene is torn down and rebuilt, and `gauges` goes
   * back to its initial value with `integrity: 1`. The screen appeared and
   * vanished inside a frame, leaving the player holding a ship that could not fly
   * with no way to say so.
   *
   * The note above `performance` tells this story one step earlier — memoising on
   * the graph rebuilt the scene on every dent — and the fix there keyed on the
   * parts list, which is exactly what a *loss* changes. So: the death of a ship
   * is a fact about the session rather than about the scene, and it is latched
   * here where a rebuild cannot reach it.
   *
   * ── Two ways to be dead, and structure is only one of them ──────────────────
   *
   * `integrity` measures how much hull is left, which misses the other half of
   * the design's rule entirely: a ship needs a pilot seat, an engine and a
   * thruster, and losing the compartment holding the seat leaves plenty of intact
   * hull attached to nothing that flies. That is not a hypothetical — it is what
   * a structural kill *is*, and it survived a reload as a ship with thrusters and
   * no way to use them, respawning into itself with no screen to say why.
   *
   * `flyable` is the same `meetsRequirements` check the builder uses to refuse a
   * launch, so a ship that could not have taken off cannot be flown after a
   * rebuild either. Checked reactively rather than at spawn: a hull that loses
   * its last engine mid-flight is destroyed the moment it does, which is what the
   * design doc says should happen and what `result.destroyed` cannot be relied on
   * to have caught.
   */
  useEffect(() => {
    if (gauges.integrity <= 0 || !performance.flyable) setHullLost(true);
  }, [gauges.integrity, performance.flyable]);

  /*
   * No card for losing a hull.
   *
   * `hull-lost` is still a lesson and still carries the text, but the death
   * screen renders it directly — see below. Firing the card as well would draw
   * the same paragraph twice, once on top of the other's buttons.
   */

  /**
   * The next system on the planned route, if one is set.
   *
   * The chart's navigation panel publishes a whole path; the cockpit only ever
   * needs the very next hop of it. Anything further is a fact about the trip
   * rather than about which way to point, and marking three systems as "your
   * route" would leave the player picking between them.
   */
  /**
   * What the console is showing, top to bottom.
   *
   * Built here rather than inside the console because every fact in it belongs
   * to the flight view — the console's job is printing, not knowing. A section
   * left out of this list is not hidden, it is *withdrawn*, and takes itself off
   * the screen a character at a time.
   *
   * Memoised on what the text actually depends on. Rebuilding the array every
   * render would hand the console a new object each frame, and the typewriter
   * would spend its life deciding whether anything had changed.
   */
  const consoleSections = useMemo<ConsoleSection[]>(() => {
    const sections: ConsoleSection[] = [];

    /*
     * Where you are, but only while something has hold of you.
     *
     * Mass lock rather than "nearest body": at flight speeds the nearest thing
     * changes constantly and a card of numbers about a moon you are not going to
     * is noise. Being held is the one relationship that is a fact about the ship.
     */
    if (gauges.local?.locked) {
      const local = gauges.local;
      const detail = local.detail ? ` · ${local.detail}` : '';

      sections.push({
        id: 'local',
        title: `local · ${readout.system}`,
        text: `${local.name}
${local.kind}${detail}
${formatDistance(local.distance)}`,
        present: true,
      });
    }

    if (holdOpen) {
      sections.push({
        id: 'hold',
        title: 'hold',
        text: holdSummary(cargo, capacity),
        // Deliveries keep their countdown and their pin button: a job is the one
        // thing in the hold you might change course over, and a countdown that
        // typed itself out every second would be unreadable.
        extra: <HoldContents cargo={cargo} now={clockTick} />,
        present: true,
      });
    }

    if (hullOpen) {
      sections.push({
        id: 'hull',
        title: 'structure',
        text: hullSummary(design, blueprint),
        // The picture is the useful part and cannot be typed.
        extra: <HullContents hull={design} blueprint={blueprint} />,
        present: true,
      });
    }

    return sections;
  }, [
    gauges.local?.locked,
    gauges.local?.name,
    gauges.local?.kind,
    gauges.local?.detail,
    gauges.local?.distance,
    readout.system,
    holdOpen,
    hullOpen,
    cargo,
    capacity,
    clockTick,
    design,
    blueprint,
  ]);

  const [routeNext, setRouteNext] = useState<string | null>(null);


  /**
   * The pinned destination, when it is a body in *this* system.
   *
   * The two halves of the indicator answer the same question at different
   * ranges: out of system it is the next hop on the sky, in system it is the
   * body itself. Only one of them is ever the answer, which is why they are
   * separate values rather than one target passed around.
   */
  const [routeBody, setRouteBody] = useState<string | null>(null);

  /** What the whole trip is for, named, so the console can say where it ends. */
  const [routeLabel, setRouteLabel] = useState<string | null>(null);

  /**
   * The next hop, as a name and a direction, worked out from the galaxy itself.
   *
   * It used to be looked up in `jumpable`, which is the *marker* list — capped
   * at a dozen nearby systems. Any hop outside that (most of them, on a long
   * route) found nothing, so the console fell through to naming the local star
   * and pointing at it: no destination, no next target, and an arrow that sat
   * still because a star barely moves. The bearing is a fact about two points
   * in the galaxy and does not need a marker to exist.
   */
  const [routeHop, setRouteHop] = useState<{
    name: string;
    direction: { x: number; y: number; z: number };
  } | null>(null);

  route.current = routeNext;
  routedBody.current = routeBody;
  routeEnd.current = routeLabel;
  routeHopRef.current = routeHop;


  /**
   * Where this hull could jump to from here.
   *
   * Derived once per system and hull rather than per frame: it walks the
   * cluster and, for a crossing, every system in the nearest ten — cheap enough
   * to do on arrival, far too dear to do sixty times a second, and none of its
   * inputs change while you are flying around inside one system.
   */
  const jumpReach = useMemo(() => jumpRangeFor(performance), [performance]);
  const clusterReach = useMemo(() => clusterJumpRangeFor(performance), [performance]);

  /**
   * Work the route out here, rather than waiting to be told.
   *
   * It used to arrive only on `routeChannel`, which is published by the chart's
   * navigation panel — so a destination pinned anywhere else, a mission board
   * most obviously, changed nothing until the player opened the starmap. The
   * cockpit would sit pointing at the local star while carrying a crate it knew
   * the address of.
   *
   * The plan is cheap and the inputs rarely change, so it is computed from the
   * pinned target directly. The channel is still listened to, because the chart
   * *also* publishes plans for targets set there and it is the faster path when
   * the panel is open.
   */
  const replan = useCallback(() => {
    const target = readTarget();

    if (!target) {
      setRouteNext(null);
      setRouteBody(null);
      setRouteLabel(null);
      setRouteHop(null);
      return;
    }

    setRouteLabel(target.label);

    // Already here: the destination is something in this system, not another
    // system. A station is a body in the scene, so this is the same path a
    // pinned moon takes.
    if (target.systemId === systemId) {
      setRouteNext(null);
      setRouteHop(null);
      setRouteBody(target.bodyName ?? null);
      return;
    }

    setRouteBody(null);

    const navigator = new Navigator(seed);
    const plan = navigator.planRoute(
      systemId as SystemId,
      target.systemId,
      jumpReach,
      clusterReach,
    );

    const next = plan?.path[1] ?? null;
    setRouteNext(next);

    if (!next) {
      setRouteHop(null);
      return;
    }

    const here = getSystemSummary(seed, systemId);
    const there = getSystemSummary(seed, next);

    const dx = there.galacticPosition.x - here.galacticPosition.x;
    const dy = there.galacticPosition.y - here.galacticPosition.y;
    const dz = there.galacticPosition.z - here.galacticPosition.z;
    const span = Math.hypot(dx, dy, dz) || 1;

    setRouteHop({ name: there.name, direction: { x: dx / span, y: dy / span, z: dz / span } });
  }, [seed, systemId, jumpReach, clusterReach]);

  // On arrival, on a change of drive, and whenever a destination is pinned from
  // anywhere at all.
  useEffect(() => {
    replan();
    return subscribePin(replan);
  }, [replan]);

  // The chart's own plan, when it has one. Same answer, sooner.
  useEffect(() => subscribeRoute(() => replan()), [replan]);


  const jumpable = useMemo(
    () =>
      jumpTargets({
        seed,
        from: systemId,
        range: jumpReach,
        clusterRange: clusterReach,
        cores,
        // Whatever the plan says to jump to next is always drawn, cap or no cap.
        include: routeNext as SystemId | null,
      }),
    [seed, systemId, jumpReach, clusterReach, cores, routeNext],
  );

  /**
   * The marker list, where the loop can see it change.
   *
   * `jumpable` is recomputed whenever the route moves — `include: routeNext`
   * makes sure the next hop is drawn, cap or no cap — but the markers are built
   * inside the flight effect, and `jumpable` is deliberately *not* one of its
   * dependencies. Making it one would tear down and rebuild the entire system
   * scene every time a destination was pinned, which is the two-hundred
   * millisecond freeze the `performance` memo above exists to avoid.
   *
   * So the new list is handed over the same way the route itself is: through a
   * ref the loop reads. See where the markers are rebuilt.
   */
  const jumpableRef = useRef(jumpable);
  jumpableRef.current = jumpable;



  /**
   * Fall back to whichever set the hull actually has.
   *
   * A ship built only with precision thrusters sat at zero throttle with a
   * working speedometer reading nothing — technically right and useless.
   * Undocking should leave you able to move.
   */
  useEffect(() => {
    if (!hasMain && hasPrecision) setSet('precision');
    if (!hasPrecision && hasMain) setSet('main');
  }, [hasMain, hasPrecision]);

  /**
   * Live controls, in a ref rather than state.
   *
   * The flight loop reads these sixty times a second; routing them through
   * React would re-render the whole view on every keystroke for no benefit.
   */
  const controls = useRef({
    mode,
    set,
    gear,
    recentre: false,
    offCentre: false,
    toggleWarp: false,
    toggleCruise: false,
    jump: false,
    toggleJump: false,
    /**
     * Actions held down on the touch grid.
     *
     * Beside the keyboard's `held` rather than folded into it: that set holds
     * *keys*, and a thumb has not pressed one. Both are consulted wherever a
     * continuous control is read, so a cell and its key are the same control by
     * two names — which is what lets a tablet with a keyboard use either.
     */
    touch: new Set<Action>(),
  });
  controls.current.mode = mode;
  controls.current.set = set;
  controls.current.gear = gear;
  controls.current.jump = jump;

  useEffect(() => {
    const mount = mountRef.current;
    const labelLayer = labelLayerRef.current;
    const jumpLayer = jumpLayerRef.current;
    if (!mount || !labelLayer || !jumpLayer) return;

    const system = getSystem(seed, systemId);
    /** The cluster holding it, named, for the console's first line. */
    const clusterName = getCluster(seed, system.clusterId).name;

    /*
     * The berth we launched from, if there is one.
     *
     * Three in ten systems have no station at all, and a jump can drop you in
     * one of them. This used to bail out of the whole scene build when it could
     * not find a pad — so arriving somewhere uninhabited gave a black screen
     * rather than a system. A berth is where a flight *starts*, and that is not
     * the same thing as where it can happen.
     */
    const station = system.stations.find((s) => s.id === stationId) ?? system.stations[0] ?? null;
    const geometry = station ? getStationGeometry(seed, station) : null;
    const pad = geometry
      ? (geometry.pads.find((p) => p.padId === padId) ?? geometry.pads[0] ?? null)
      : null;

    // Nowhere to launch from *and* nowhere to arrive: nothing to draw.
    if (!pad && !arriveAtStar && !state.current) return;

    setReadout({
      system: system.name,
      operator: station?.allegiance ? FACTIONS[station.allegiance].name : 'Independent',
    });

    /**
     * What each body actually is, by the name the scene knows it under.
     *
     * The top-left panel used to state the station you launched from and go on
     * stating it while you flew to another world, which is worse than saying
     * nothing — a readout that is confidently wrong is one you learn to ignore.
     */
    const detail = new Map<string, string>();
    for (const planet of system.planets) {
      detail.set(planet.name, `${planet.type} · ${planet.moons.length} moons`);
      for (const moon of planet.moons) detail.set(moon.name, `${moon.type} moon of ${planet.name}`);
    }
    for (const other of system.stations) {
      const operator = other.allegiance ? FACTIONS[other.allegiance].name : 'Independent';
      detail.set(other.name, operator);
    }
    for (const anomaly of system.anomalies) detail.set(anomaly.name, 'uncharted signal');

    const scene = new THREE.Scene();
    const background = createSpaceBackground();
    scene.add(background.mesh);

    const sky = createSystemSky(seed, systemId);
    scene.add(sky.points);

    scene.add(new THREE.AmbientLight(0xffffff, 0.16));

    /**
     * Everything with a fixed place in the system.
     *
     * Shifted wholesale each frame so the ship stays at the origin. The star
     * light goes inside it for the same reason the planets do — it sits at the
     * system's centre, and that centre moves in the shifted frame.
     */
    const worldRoot = new THREE.Group();
    scene.add(worldRoot);
    worldRoot.add(new THREE.PointLight(new THREE.Color(system.starColor), 2.4, 0, 0));

    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const camera = new THREE.PerspectiveCamera(FOV, width / height, 0.1, 4_000_000);
    camera.up.set(0, 0, 1);

    const view = mountViewport(
      mount,
      (w, h) => {
        width = w;
        height = h;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      },
      // Transparent, because the crossing gas is a canvas *underneath* this one
      // and the shell erases down to it. See `shell`.
      { logarithmicDepthBuffer: true, alpha: true },
    );
    const renderer = view.renderer;

    const built = buildSystemScene(seed, systemId);
    worldRoot.add(built.group);

    /*
     * One line per scene build, saying what is loaded.
     *
     * Collisions were debugged for a while against a bundle that predated the
     * fix, because nothing on screen distinguishes "the new code is wrong" from
     * "the new code is not running". Two things cause that: the flight loop is a
     * `requestAnimationFrame` chain that lives for the whole flight, so a hot
     * update can replace this module while the *old* loop keeps going; and the
     * game is usually played from a built bundle, which is only as new as the
     * last `npm run build`.
     *
     * So this is available in a production build too, behind `?debug` — a
     * dev-only diagnostic is no use in the place the problem actually showed up.
     */
    if (import.meta.env.DEV || new URLSearchParams(location.search).has('debug')) {
      const volumes = built.stationHulls.reduce((n, s) => n + s.volumes.length, 0);
      console.info(
        `[flight] ${systemId}: ${built.stationHulls.length} stations, ${volumes} collision volumes`,
      );
    }

    /**
     * The hull, twice: gear up and gear down.
     *
     * Landing gear already has a deployed model, so this is real geometry rather
     * than a flag — you can see it come down. Built once each and swapped by
     * visibility, because rebuilding a hull mesh on a toggle would mean tearing
     * down and re-creating the whole scene every time you came in to land.
     */
    let ship = buildShipMesh(design, { landed: false });
    let shipLanded = buildShipMesh(design, { landed: true });

    // Measured before the hull is parented, so this is the ship's own box rather
    // than one that happens to include wherever it is currently pointing. Taken
    // with the gear down, which is the configuration that has to fit a berth.
    const shipBox = new THREE.Box3().setFromObject(shipLanded.group);

    const wreckage = createWreckage();
    scene.add(wreckage.group);


    /*
     * Rebuild the ship, because it is a different ship.
     *
     * Damage removes walls, joints and parts from the hull outright, so what is
     * left is a new graph — and the honest way to draw a new graph is to build
     * it. The version that hid pieces instead was a second, quieter copy of the
     * ship living in mesh visibility, and it lost an argument with the cutaway
     * every frame: `setSkinVisible` runs continuously and put every destroyed
     * wall back, sixty times a second.
     *
     * The collision outline comes from the same graph in the same breath, so the
     * shape you can see and the shape you can hit cannot disagree.
     *
     * Not cheap, and it does not need to be: a hull loses walls a handful of
     * times in its life, and the alternative was a bookkeeping problem that had
     * already been got wrong three times.
     */
    let footprint = shipFootprint(design, SHIP_DEPTH, SHIP_DEPTH / 2);

    const rebuild = (current: ShipGraph) => {
      const gearWasDown = shipLanded.group.visible;

      hullGroup.remove(ship.group, shipLanded.group);
      ship.dispose();
      shipLanded.dispose();

      ship = buildShipMesh(current, { landed: false });
      shipLanded = buildShipMesh(current, { landed: true });

      ship.group.visible = !gearWasDown;
      shipLanded.group.visible = gearWasDown;
      hullGroup.add(ship.group, shipLanded.group);

      footprint = shipFootprint(current, SHIP_DEPTH, SHIP_DEPTH / 2);
    };

    const hullGroup = new THREE.Group();
    hullGroup.add(ship.group, shipLanded.group);
    scene.add(hullGroup);

    /*
     * Everyone else.
     *
     * Built with the scene and torn down with it, so a jump — which rebuilds
     * this whole effect — starts the new system with nobody in it and fills up
     * again from the new room, rather than carrying the old system's traffic
     * across.
     */
    const others = createRemoteShips(scene);

    /*
     * Exhaust.
     *
     * Held in absolute coordinates rather than parented to the hull, so a plume
     * is left behind in the world instead of travelling with the ship. Scene
     * space would not do it: this view rebases onto the ship every frame, so
     * scene space *is* ship space. See `thrusterPlume`.
     */
    /*
     * A fresh system gets a fresh log.
     *
     * The scene is torn down and rebuilt on a crossing, and carrying the old
     * one's entries across would have the ship reporting a mass lock on a body
     * in a system it has left.
     */
    clearLog();

    /**
     * The speed the jump run began at, and the last thing the module said.
     *
     * The sequence measures from wherever the ship happened to be when it
     * committed, so the starting point has to be caught on the frame the run
     * begins — it is not on the crossing, which carries only a phase and a
     * clock.
     */
    let runStartSpeed: number | null = null;
    let calibrationSaid: string | null = null;
    let calibrationAt = 0;

    /*
     * Where every channel starts, said silently.
     *
     * `note` logs a channel whose state has *changed*, and after `clearLog` every
     * channel has no state at all — so the first frame would find all of them
     * changed and open the log with six lines of default position: gear up,
     * thrusters main, module stowed, no lock, no cruise, no warp. True, and the
     * exact opposite of a log, which is for things that happened.
     *
     * An empty message records the state without announcing it. From here on
     * only a real change speaks.
     */
    for (const [channel, state] of [
      ['lock', ''],
      ['docking', ''],
      ['cruise', ''],
      ['warp', ''],
      ['hold', ''],
      ['heat', ''],
      ['crossing', crossing.current.phase],
      ['gear', controls.current.gear ? 'down' : 'up'],
      ['set', controls.current.set],
      ['jumpmodule', controls.current.jump ? 'on' : 'off'],
      ['jumpcal', ''],
      ['shutdown', ''],
    ] as const) {
      note(channel, state, '');
    }

    const plume = createThrusterPlume(scene, THRUST_FLAME);

    /*
     * Stars going past, in the scene rather than on a canvas over it.
     *
     * Which is the whole point of it being here: a canvas has no depth, so the
     * streaks painted over the hull that was passing them. As geometry the ship
     * occludes them, because it is opaque and nearer. See `warpStreaks`.
     */
    const streaks = createWarpStreaks(scene);
    /** A nozzle's place and facing this frame. Reused; never escapes. */
    const nozzleAt = new THREE.Vector3();
    const nozzleBack = new THREE.Vector3();

    /*
     * Where a pad is, for drawing somebody parked on it.
     *
     * Docked ships are broadcast as a berth rather than a position, because a
     * station orbits and a remembered landing point drifts away from the plate
     * it was taken on. This resolves one against the scene, which is moving in
     * the same way -- so a parked ship stays parked.
     *
     * Scene space, because the deck's world matrix already carries the floating
     * origin. `remoteShips` uses it without the usual subtraction.
     */
    const padScratch = new THREE.Vector3();
    const padSpin = new THREE.Quaternion();

    /** Where the pose is measured from this frame. Reused; never escapes. */
    const poseFrame = new THREE.Vector3();
    /** A contact's world position, read once per contact. Reused. */
    const contactAt = new THREE.Vector3();
    /** The ship, in a belt's own frame. Reused; never escapes the test. */
    const shipInField = new THREE.Vector3();

    /*
     * Pads somebody else is sitting on.
     *
     * Rebuilt from the feed each frame rather than tracked, because a peer
     * landing or leaving is a change to the same list and there is nothing to
     * keep in step. A handful of ships in a system makes this a handful of
     * string joins.
     *
     * This is the client's own opinion, and two clients approaching one empty
     * pad will both believe it is free until one of them lands. Deciding that
     * properly means the server granting berths, which is step 5 of
     * `multiplayer-plan.md`; until then the race exists and is small.
     */
    const takenPads = new Set<string>();

    /*
     * Parked ships, as things to run into.
     *
     * A ship on a pad is a box standing on the deck's top face, which is exactly
     * what a `PropVolume` is -- so a docked player becomes one more solid part of
     * the station and goes through the collision path that already exists. No
     * second kind of collision, and no second set of coordinate conversions to
     * get wrong.
     *
     * ── One-sided, and that is free ───────────────────────────────────────────
     *
     * Only the ship that is flying runs this test. The docked player is not
     * simulating anything about you and cannot be hurt by the result, which is
     * the rule asked for -- it falls out of who owns the check rather than
     * needing to be enforced.
     */
    const parkedVolumes = new Map<THREE.Object3D, StationVolume[]>();

    const readParked = () => {
      parkedVolumes.clear();

      for (const ship of others.parked()) {
        const berth = built.berths.find(
          (candidate) =>
            candidate.stationId === ship.stationId && candidate.padId === ship.padId,
        );
        if (!berth) continue;

        // Into the deck's own frame, which is in generator units while the hull
        // is measured in world ones -- the same conversion `localExtent` makes,
        // and the same one that has been got wrong here before.
        const plan = ship.extent.plan / GAME_SCALE;
        const depth = ship.extent.depth / GAME_SCALE;

        const volumes = parkedVolumes.get(berth.deck) ?? [];
        volumes.push({
          kind: 'prop',
          position: { x: berth.local.x, y: berth.local.y },
          // A reach from the ship's origin, so the box is twice it. See
          // `shipExtentOf` for why that is a reach and not a half-span.
          size: { x: plan * 2, y: plan * 2, z: depth * 2 },
          rotation: 0,
          // The pad is on the deck's top face, and so is the ship on it.
          deckTop: berth.local.z,
        });
        parkedVolumes.set(berth.deck, volumes);
      }
    };

    /**
     * The station, plus whoever is parked on it.
     *
     * Returns the scene's own array untouched when nobody is docked, so a system
     * with no traffic collides exactly as it did before this existed -- and pays
     * nothing for the feature.
     */
    const solidStations = () => {
      if (parkedVolumes.size === 0) return built.stationHulls;

      return built.stationHulls.map((hull) => {
        const extra = parkedVolumes.get(hull.deck);
        return extra ? { ...hull, volumes: [...hull.volumes, ...extra] } : hull;
      });
    };

    /**
     * How much room the hull needs, both over a plate and against a deck.
     *
     * Its own reach rather than a bounding sphere. A sphere is larger than a
     * square hull by √2, which was enough to declare the starting ship too big
     * for the starting station's pads while it still had two and a half units of
     * clearance on every side.
     *
     * The floor is half the hull's thickness — the one dimension every ship has
     * whether or not there is any geometry to measure yet.
     */
    const shipExtent = shipExtentOf(shipBox, SHIP_DEPTH / 2);


    /*
     * Whether this hull has to lower gear to be allowed down.
     *
     * Read from the hull in service, not the blueprint: legs are components, and
     * a compartment coming apart takes whatever was in it. A ship with none left
     * cannot satisfy a gear-down check however well it is flown, and without an
     * exemption it is locked out of every berth in the galaxy — which is a dead
     * end rather than a difficulty. See `canBellyLand`.
     */
    const onItsBelly = () => canBellyLand(hull_.components.map((c) => c.type));

    /*
     * Which walls were in the way, from the direction the shove came from.
     *
     * `away` points out of the surface, so the ship met it on the *opposite*
     * side: turned into the hull's own frame and pushed out to the edge of the
     * footprint, that names a point on the ship, and the perimeter lines near
     * that point are the ones that took it.
     *
     * Approximate on purpose. The exact answer is the set of walls whose
     * segments cross the surface, which needs a contact manifold the collision
     * test does not produce and would not survive the hull being treated as its
     * convex outline anyway. The direction of the blow is what a player can
     * actually see, and it is enough to put the dent on the right side.
     */
    const struckLines = (away: THREE.Vector3): Set<string> => {
      contactLocal.copy(away).negate().applyQuaternion(facingInverse.copy(hullQuaternion).invert());

      const flat = Math.hypot(contactLocal.x, contactLocal.y);
      const at =
        flat < 1e-6
          ? { x: 0, y: 0 }
          : {
              x: (contactLocal.x / flat) * footprint.reach,
              y: (contactLocal.y / flat) * footprint.reach,
            };

      // Wide enough to catch the corner walls either side of a flat-on hit, so a
      // square blow does not land entirely on one arbitrary wall.
      // The hull in service, not the blueprint: a wall already broken is a hole,
      // and a hole cannot take another hit.
      return contactLines(hull_, at, SHIP_DEPTH);
    };

    // Undocked just clear of the pad. Once released the ship keeps its own
    // position — it has stopped caring where it came from.
    const deck = station ? built.stationNodes.get(station.id) : null;
    const start = new THREE.Object3D();
    // Resting on the plate, not hovering over it. Releasing the ship four hull
    // depths into the air meant undocking began with a jump you did not ask for
    // and were not flying — getting off the pad should be the first thing you
    // do, not something that has already happened.
    //
    // Matches where the docked view parks it — same pad-surface constant, same
    // standoff off the ship's own landing gear — plus half a unit, so undocking
    // lifts you clear of the plate and no further. It used to release you four
    // hull depths in the air, which meant undocking began with a jump you did
    // not ask for and were not flying.
    if (pad && geometry) {
      start.position.set(
        pad.position.x,
        pad.position.y,
        padSurface(geometry, pad) +
          (SHIP_DEPTH / 2 + shipLanded.standoff + UNDOCK_LIFT) / GAME_SCALE,
      );
      deck?.add(start);
    } else {
      // No berth in this system. The origin is the star, which is where an
      // arrival is measured from anyway.
      worldRoot.add(start);
    }

    /*
     * The shell you cross inside.
     *
     * An inside-out sphere around the ship: clear while the drive winds up, and
     * by the time it reaches jump speed the system it is leaving is gone. That is
     * what makes the swap survivable — the *new* system's geometry would
     * otherwise be visible the instant the scene was rebuilt, and the hitch of
     * generating one becomes a cut to somewhere else rather than a still frame.
     *
     * ── It erases rather than paints ────────────────────────────────────────
     *
     * It used to be black paint, which was right when the crossing gas was a
     * canvas *over* this one: black was what the gas needed behind it. The gas is
     * underneath now, so painting the world out would paint the gas out with it —
     * the very layer this is meant to be revealing.
     *
     * So the blend is `zero × source, (1 − source alpha) × destination` on colour
     * and alpha alike: the shell contributes nothing of its own and takes the
     * framebuffer down towards transparent instead. Same ramp and same opacity
     * curve as before; what changes is that it leaves a hole showing the gas
     * rather than a black wall.
     *
     * `depthWrite` off, and depth *testing* on, which is what protects the ship:
     * the hull is opaque and has already written depth, so the shell's fragments
     * fail the test across its silhouette and never erase it. Drawn over what is
     * behind it and under what is in front, which is exactly a room.
     */
    const shell = new THREE.Mesh(
      new THREE.SphereGeometry(CROSSING_SHELL, 24, 16),
      new THREE.MeshBasicMaterial({
        color: 0x000000,
        side: THREE.BackSide,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.CustomBlending,
        blendSrc: THREE.ZeroFactor,
        blendDst: THREE.OneMinusSrcAlphaFactor,
        blendSrcAlpha: THREE.ZeroFactor,
        blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
      }),
    );
    shell.visible = false;
    shell.renderOrder = 8;
    scene.add(shell);

    built.animate(flightTime());

    /**
     * Where the ship really is.
     *
     * True coordinates, not scene ones. `hullGroup.position` is where it is *drawn*,
     * which is within a frame's travel of the origin — see the note at the top
     * of the file.
     */
    const shipAt = start.getWorldPosition(new THREE.Vector3());

    // A tutorial start is three thousand units off the station rather than
    // sitting on its pad — the introduction is an approach, and you cannot be
    // talked through one you have already completed.
    if (spawnOffset) shipAt.add(new THREE.Vector3(spawnOffset.x, spawnOffset.y, spawnOffset.z));

    /** Where the ship was when we last left this view, if it was in here. */
    const restored = state.current?.systemId === systemId ? state.current : null;

    if (restored) {
      shipAt.set(...restored.offset);

      // The offset is measured from whatever had hold of us, because that thing
      // has kept orbiting while the chart was open. Adding it back to where the
      // anchor is *now* is what makes checking the map not cost you your berth.
      if (restored.anchor) {
        const anchor = built.focusables.find((f) => f.label === restored.anchor);
        if (anchor) shipAt.add(anchor.node.getWorldPosition(new THREE.Vector3()));
      }
    }

    /**
     * The render origin, and the conversion between the two frames.
     *
     * Rebased at the top of every frame to wherever the ship was at the end of
     * the last one, so within a frame the two are a few units apart at most.
     */
    const origin = new THREE.Vector3();

    /**
     * Placing other people's ships: pads, and the bodies they hold station by.
     *
     * Shared with the docked view, which does the same job from a camera parked
     * on a deck. The only difference between them is this origin — the cockpit
     * shifts the world, a berth does not — and coordinate conversions are the
     * one thing in this project that must not exist in two copies.
     */
    const { padAt, anchorAt } = sceneLookups(built, origin);
    const trueOf = (node: THREE.Object3D, out: THREE.Vector3): THREE.Vector3 =>
      node.getWorldPosition(out).add(origin);


    // ── State ────────────────────────────────────────────────────────────────
    // Where a fresh spawn looks. A *restored* flight overrides both with the
    // rotation it saved — see `heading` — so these only matter the first time.
    let startYaw = 0;
    let startPitch = 0;

    /*
     * A fresh spawn wakes up looking roughly at the station.
     *
     * Zero yaw is north, which relative to a spawn on a random bearing is a
     * random direction — so a first-time player's opening move was to hunt for
     * the thing they had just been told to fly to, with no way of knowing which
     * way to turn. `tutorialSpawnFacing` puts it within fifteen degrees: off
     * centre enough that finding it is a real (small) act of flying, close
     * enough that it is already on screen.
     *
     * Only when there is nothing restored — a returning ship keeps the heading
     * it was left on.
     */
    /*
     * Arriving from a jump.
     *
     * Measured from the star's *surface*, not its centre: stars run from 1,700
     * to 5,200 world units across, so a thousand units from the middle of one
     * would put the ship inside it. From the surface it is a close arrival with
     * the local sun filling the canopy, which is the point — you have never
     * seen this system before and the star is the one thing that tells you
     * which it is.
     *
     * The bearing is arbitrary but seeded off nothing in particular; what
     * matters is that the nose is pointed at the star, which the block below
     * handles along with a tutorial spawn.
     */
    /**
     * The run in from the edge, if we are arriving.
     *
     * Null unless a jump put us here. Both ends are computed now, while the
     * star's position is to hand; the loop eases between them over the first
     * couple of seconds — see where `arriving` is spent.
     */
    let approach: { from: THREE.Vector3; to: THREE.Vector3; left: number } | null = null;

    if (arriveAtStar && !restored) {
      const star = built.spheres.find((sphere) => sphere.label === system.name);
      const radius = star?.radius ?? 0;

      /*
       * A bearing to come in on that does not run through anything.
       *
       * The approach is a straight line ninety thousand units long, aimed at
       * the one point in the system everything else orbits — so it crosses the
       * whole planetary disc, and a bearing picked blind will sooner or later
       * pick one that ends inside a planet. It did.
       *
       * Tried rather than solved: pick, test the segment against every sphere,
       * and pick again. A dozen attempts is plenty when most bearings are
       * clear, and the last one is taken regardless — arriving on a poor
       * bearing is far better than not arriving.
       */
      const clearBearing = (): THREE.Vector3 => {
        const from = new THREE.Vector3();
        const at = new THREE.Vector3();
        const centre = new THREE.Vector3();

        for (let attempt = 0; attempt < 12; attempt++) {
          const bearing = Math.random() * Math.PI * 2;
          const lift = (Math.random() * 2 - 1) * 0.2;
          const heading = new THREE.Vector3(Math.cos(bearing), Math.sin(bearing), lift).normalize();

          if (!star || attempt === 11) return heading;

          const standoff = (star.radius ?? 0) + ARRIVAL_STANDOFF;
          star.node.getWorldPosition(at).addScaledVector(heading, standoff);
          star.node.getWorldPosition(from).addScaledVector(heading, standoff + ARRIVAL_ENTRY);

          const blocked = built.spheres.some((sphere) => {
            if (sphere === star) return false;

            sphere.node.getWorldPosition(centre);
            // Same segment-versus-sphere test the markers use for occlusion,
            // with the hull's own extent added so a graze still counts.
            return sightBlocked(from, at, 0, centre, sphere.radius + shipExtent.plan);
          });

          if (!blocked) return heading;
        }
        return new THREE.Vector3(1, 0, 0);
      };

      const out = clearBearing();

      if (star) {
        const standoff = radius + ARRIVAL_STANDOFF;

        star.node.getWorldPosition(shipAt);
        shipAt.addScaledVector(out, standoff);

        approach = {
          from: star
            .node.getWorldPosition(new THREE.Vector3())
            .addScaledVector(out, standoff + ARRIVAL_ENTRY),
          to: shipAt.clone(),
          left: ARRIVAL_GLIDE,
        };

        // Started out there rather than at the destination, so the very first
        // frame drawn is already the approach.
        shipAt.copy(approach.from);
      }
    }

    if (spawnOffset && !restored) {
      const facing = tutorialSpawnFacing(spawnOffset, { float: Math.random });

      // Forward at zero is +Y, and the loop composes as Z-then-X — see where
      // `aimPoint` is built, which is the same convention read backwards.
      startYaw = Math.atan2(-facing.x, facing.y);
      startPitch = Math.asin(THREE.MathUtils.clamp(facing.z, -1, 1));
    }

    // Facing the star you have just arrived beside. Same conversion as above.
    if (arriveAtStar && !restored) {
      const star = built.spheres.find((sphere) => sphere.label === system.name);
      if (star) {
        const toStar = star.node.getWorldPosition(new THREE.Vector3()).sub(shipAt).normalize();
        startYaw = Math.atan2(-toStar.x, toStar.y);
        startPitch = Math.asin(THREE.MathUtils.clamp(toStar.z, -1, 1));
      }
    }

    /*
     * Where the pilot is asking the ship to point, as a rotation.
     *
     * A pair of angles until now, which is what put a wall just short of
     * vertical: yaw and pitch have a pole and this does not. Turned about its
     * *own* axes by the mouse, so the frame the input is read in comes out of
     * the orientation itself — which is what `aimDelta` was hand-computing from
     * the roll, and why that function is no longer called.
     */
    let aim: Quat = restored?.orientation
      ? quatNormalise(restored.orientation)
      : headingFrom(startYaw, startPitch);

    // Carried between frames so the turn can ease in and out rather than
    // starting and stopping at full rate.
    /*
     * Where the hull is pointing, as a rotation rather than as two angles.
     *
     * ── Roll is not in here ────────────────────────────────────────────────
     *
     * This is the *heading* — the part that chases the aim and eases. Roll is
     * applied on top of it, unchanged and instantaneous, because that is what it
     * has always been: turning fights the momentum you already have, rolling
     * about the nose does not, and a rolled response that lagged would be a
     * change to the feel rather than to the maths.
     *
     * Keeping it out also means the extraction below is exact. A pure yaw/pitch
     * rotation can be read straight back off its own forward vector; one with a
     * roll folded in cannot.
     */
    let heading: OrientationState = {
      // A saved rotation if there is one, and the spawn facing otherwise. A
      // flight stored by an older build has no rotation, and resuming level is
      // a better answer than converting a yaw and a pitch that cannot describe
      // where the ship might have been.
      orientation: restored?.orientation
        ? quatNormalise(restored.orientation)
        : headingFrom(startYaw, startPitch),
      rate: 0,
    };



    /**
     * Where the head is looking, *relative to the hull*.
     *
     * Absolute before, which is what caused the snap: the loop reset it to the
     * hull's heading on every frame it was not being dragged, so releasing the
     * middle mouse yanked the view straight back to the nose. As an offset it
     * simply persists — look left, keep flying, and the view stays left.
     */
    const headOffset = { yaw: 0, pitch: 0 };

    const velocity = new THREE.Vector3(...(restored?.velocity ?? [0, 0, 0]));
    let throttle = restored?.throttle ?? 0;
    let heat = restored?.heat ?? 0;
    let freeLook = false;
    let warping = restored?.warping ?? false;

    /** Whichever destination the reticle is on, refreshed each frame. */
    let aimedJump: string | null = null;

    /** Where the hull points, and a scratch vector for drawing it. Per frame. */
    const noseAxis = new THREE.Vector3();
    const nosePlace = new THREE.Vector3();

    /** Where each destination lies, for the autopilot to point the hull at. */
    const jumpBearings = new Map(
      jumpable.map((target) => [
        target.systemId,
        new THREE.Vector3(target.direction.x, target.direction.y, target.direction.z),
      ]),
    );

    /**
     * Speed *along the nose*, carried from the frame before.
     *
     * The warp rules used the velocity's magnitude, and that is why the drive
     * sometimes refused to stall: the throttle floors the forward component at
     * a hundred, but lateral drift is not part of that, so the total stayed a
     * whisker above the floor for ever and the stall condition never fired. The
     * drive cares about how fast you are going *forward*, which cannot be known
     * until the hull has been oriented — hence a frame of lag, which at sixty
     * hertz is nothing next to being wrong.
     */
    let along = 0;

    /**
     * The hull, taking damage line by line.
     *
     * A working copy: the flight loop is the only thing that writes it, and it
     * is handed back up on every hit so it survives docking and a refresh. The
     * blueprint it was built to never changes, which is what makes a wreck
     * repairable instead of a lost design.
     */
    let hull_ = design;
    let integrity = hullIntegrity(hull_, blueprint);
    let lastImpact:
      | { body: string; severity: Impact['severity']; speed: number; at: number }
      | null = null;

    /** Where the last collision left the hull, so one hit is reported once. */
    /*
     * Brief immunity after any collision, in seconds.
     *
     * Damage only — the shove, the position fix and the cleared throttle all
     * still happen on every frame they are needed, because those are what stop
     * the ship being inside something. What a run of frames in contact must not
     * do is bill for each one separately: a scrape along a deck is one accident,
     * not forty, and at sixty frames a second the difference is the ship.
     */
    let damageCooldown = 0;
    /** Bumped per charged hit, so the screen can tell two apart. */
    let impactSeq = 0;
    const wreckRandom = Rng.forEntity(seed, systemId, 'wreck');

    /*
     * Let the hull come apart, if anything has given out.
     *
     * Called wherever damage lands rather than on a timer, and safe to call when
     * nothing is wrong — `collapse` settles a hull that has already settled to
     * itself. What comes back is the cascade: the walls that gave way, the parts
     * that were in the rooms they were holding up, and whether there is still a
     * ship at the end of it.
     */
    const settle = () => {
      // Held on to for the effect: whatever gives way is gone from the result.
      const was = hull_;
      const result = collapse(hull_, wreckRandom);
      if (result.broke.length > 0 || result.lost.length > 0) hullDirty = true;
      hull_ = result.hull;

      /*
       * Nothing came apart, so there is nothing to redraw.
       *
       * `collapse` hands back the same graph when a step takes nothing, and
       * `rebuild` is two whole ship meshes and a fresh collision outline -- the
       * expensive thing, written for something that happens a handful of times
       * in a hull's life. Heat calls this every frame, and doing the expensive
       * thing sixty times a second is exactly the stall it looked like.
       *
       * Health still changed; that is not something the mesh draws. A dented
       * wall and a fresh one are the same geometry in the same paint.
       */
      if (result.hull === was) {
        integrity = hullIntegrity(hull_, blueprint);
        return;
      }

      // Whatever came off stops being drawn and stops being collided with.
      rebuild(hull_);
      integrity = hullIntegrity(hull_, blueprint);


      /*
       * Parts that survived coming off are left where the ship was, carrying a
       * fraction of its momentum. A collector picks them up; the rest is scrap.
       *
       * Nudged apart so several parts out of one compartment do not stack into a
       * single object you can only collect all of at once.
       */
      for (const { component, fate } of result.lost) {
        if (fate !== 'dropped') continue;

        const scatter = new THREE.Vector3(
          wreckRandom.float() - 0.5,
          wreckRandom.float() - 0.5,
          wreckRandom.float() - 0.5,
        ).multiplyScalar(6);

        wreckage.drop(
          component.type,
          shipAt.clone().add(scatter),
          velocity.clone().multiplyScalar(0.4).add(scatter.multiplyScalar(0.3)),
        );
      }

      /*
       * A wall failing, thrown outward along its own normal.
       *
       * Read off the ship as it was a moment ago, because the wall is not on the
       * one that came back — `collapse` reports what it took, and what it took
       * is no longer there to be measured. Each wall can only be reported once,
       * for the same reason.
       */
      for (const id of result.broke) {
        const line = was.lines.find((l) => l.id === id);
        const a = line && was.joints.find((j) => j.id === line.a);
        const b = line && was.joints.find((j) => j.id === line.b);
        if (!line || !a || !b) continue;

        breakAt
          .set((a.x + b.x) / 2, (a.y + b.y) / 2, 0)
          .applyQuaternion(hullQuaternion)
          .add(shipAt);

        // The wall's own outward direction: perpendicular to it, in the ship's
        // plane, pointing away from the middle of the hull.
        breakOut
          .set(-(b.y - a.y), b.x - a.x, 0)
          .normalize()
          .applyQuaternion(hullQuaternion);

        if (breakOut.lengthSq() < 1e-6) breakOut.set(0, 0, 1);
        wreckage.spark(breakAt, breakOut);
      }

      /*
       * Nothing left that flies.
       *
       * A different death from running out of hull and a much faster one: lose
       * the compartment with the seat in it, or the last engine, and the rest of
       * the ship is debris that happens to be holding together.
       */
      if (result.destroyed || isHullLost(hull_)) {
        integrity = 0;
        velocity.set(0, 0, 0);
        throttle = 0;
        warping = false;
        cruising = null;

        lastImpact = {
          body: result.destroyed ? 'structural failure' : 'hull temperature',
          severity: 'destroyed',
          speed: 0,
          at: ++impactSeq,
        };
      }
    };

    /**
     * A feature has just been used, by name.
     *
     * Deduped per lesson, the way `note` is deduped per channel, and for the
     * same reason: the block these are called from runs ten times a second and
     * the loud half of a lesson is a round trip. An empty state means "not
     * happening", so raising something, stowing it and raising it again asks
     * twice — the account is what stops the second one being a lesson.
     */
    const featureState = new Map<string, string>();
    const feature = (id: string, state: string): void => {
      if (featureState.get(id) === state) return;
      featureState.set(id, state);
      if (state) live.current.onFeatureUsed?.(id);
    };

    /** Set when the hull has changed and the tree above has not been told. */
    let hullDirty = false;

    /*
     * Rocks that are no longer there, by field.
     *
     * Empty until mining lands — a rock only leaves a belt by being broken, and
     * nothing breaks one yet. Threaded through now because the collision test
     * already takes it, and a set that is always empty costs one lookup.
     */
    const brokenRocks = new Map<string, Set<number>>();

    /**
     * What a rock has left, once something has hit it.
     *
     * Filled lazily: a rock nobody has touched is not in here, and reads its
     * full health from the seed. Only the damage is worth remembering, which is
     * the same rule the stored version will follow — see `mining-plan.md`.
     *
     * Scene-local for now. When the server owns rock health this becomes the
     * client's optimistic copy of it, and the shape does not change.
     */
    const rockHealth = new Map<string, Map<number, number>>();

    /**
     * Take a bite out of a rock, and say whether that finished it.
     *
     * Damage is whatever the impact was worth. A ship flying into a belt at
     * speed genuinely does break rocks, which is the point — it is a slow and
     * expensive way to mine, and it should be, because the hull takes the same
     * hit.
     */
    const damageRock = (fieldId: string, index: number, amount: number, was: number): boolean => {
      let field = rockHealth.get(fieldId);
      if (!field) {
        field = new Map();
        rockHealth.set(fieldId, field);
      }

      const left = (field.get(index) ?? was) - amount;
      field.set(index, left);
      if (left > 0) return false;

      let broken = brokenRocks.get(fieldId);
      if (!broken) {
        broken = new Set();
        brokenRocks.set(fieldId, broken);
      }
      broken.add(index);
      return true;
    };

    const breakAt = new THREE.Vector3();
    const breakOut = new THREE.Vector3();

    /** What cruise is flying to, and the target it hands the steering. */
    let cruising: string | null = null;
    /** What the reticle is currently over — what cruise would take if asked. */
    let aimedAt: string | null = null;
    const toTarget = new THREE.Vector3();
    const projected = new THREE.Vector3();

    /** Last frame's position for every body, so their motion can be inherited. */
    const anchorMemory = new Map<string, THREE.Vector3>();

    // ── Berths ───────────────────────────────────────────────────────────────
    //
    // Everything about a berth is in its deck's own frame, which is in generator
    // units — the scene group carries `GAME_SCALE` as a whole. So the hull's
    // radius has to come *down* into that frame to be compared with a pad.
    const localExtent = {
      plan: shipExtent.plan / GAME_SCALE,
      depth: shipExtent.depth / GAME_SCALE,
    };

    // Berths this hull could not physically be captured on are dropped here
    // rather than teased on approach: a hologram that can never turn green is
    // worse than no hologram.
    const berths = built.berths
      .map((berth) => ({ ...berth, volume: dockVolume(berth.extent) }))
      .filter((berth) => fitsBerth(berth.volume, localExtent));

    /** Berths grouped by deck, so a station's world matrix is walked once. */
    const decks = new Map<THREE.Object3D, (typeof berths)[number][]>();
    for (const berth of berths) {
      const existing = decks.get(berth.deck);
      if (existing) existing.push(berth);
      else decks.set(berth.deck, [berth]);
    }

    /**
     * The capture volume, drawn.
     *
     * One mesh moved to whichever berth is live, rather than one per berth: a
     * system has upwards of fifty pads and only ever one of them matters.
     * Invisible until something is in range, which is the whole point — it is a
     * hologram that comes up as you arrive, not scaffolding hanging over every
     * station in the system.
     */
    const volumeGroup = new THREE.Group();
    volumeGroup.visible = false;

    const volumeBox = new THREE.BoxGeometry(1, 1, 1);
    const volumeFaces = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0.07,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const volumeEdges = new THREE.LineBasicMaterial({ transparent: true, opacity: 0.85 });

    volumeGroup.add(new THREE.Mesh(volumeBox, volumeFaces));
    volumeGroup.add(new THREE.LineSegments(new THREE.EdgesGeometry(volumeBox), volumeEdges));
    scene.add(volumeGroup);

    const APPROACH_COLOR = { entering: 0x7fd4ff, captured: 0x7fb46f };

    const deckPosition = new THREE.Vector3();
    const berthLocal = new THREE.Vector3();
    /** The ship, in a deck's own frame. Reused, since collision runs per frame. */
    const berthCentre = new THREE.Vector3();
    const berthWorld = new THREE.Vector3();

    const held = new Set<string>();

    /** The stand-in pointer used while Alt is held. See `onKeyDown`. */
    /**
     * The stand-in pointer.
     *
     * `pinned` is the difference between reaching for a switch and settling
     * into a conversation. A switch is a tap — hold alt, click, let go, fly on.
     * Typing is not: you need both hands and an unknown amount of time, and
     * having to keep a modifier held down throughout made the radio unusable
     * for the one thing it exists for. So clicking into anything that asks to
     * keep the cursor latches it on, and it stays on — through alt being
     * released — until you click somewhere that is not the chat.
     */
    const cursor = { active: false, pinned: false, x: 0, y: 0 };

    // ── Input ────────────────────────────────────────────────────────────────

    /**
     * Turn the head in the seat.
     *
     * The head still moves in angles rather than as a rotation, and that is the
     * right shape for it: it is looking *within* the cockpit, so a limit is a
     * neck rather than a singularity. Shared by every way of asking — the middle
     * mouse button, two fingers on a screen, two on a trackpad — so they cannot
     * drift into disagreeing about which way is up.
     */
    const look = (dx: number, dy: number, sensitivity: number) => {
      headOffset.yaw -= dx * sensitivity;
      headOffset.pitch = THREE.MathUtils.clamp(
        headOffset.pitch - dy * sensitivity,
        -HEAD_LIMIT,
        HEAD_LIMIT,
      );
    };

    const onMouseMove = (event: MouseEvent) => {
      if (document.pointerLockElement !== renderer.domElement) return;
      if (covered.current) return;

      // The autopilot owns the aim from the lock onward. Letting the mouse
      // still move it would have the pilot and the ship fighting over a heading
      // only one of them can set.
      if ((!pilotHasControl(crossing.current) || approach) && !cursor.active) return;

      if (cursor.active) {
        cursor.x = THREE.MathUtils.clamp(cursor.x + event.movementX, 0, width);
        cursor.y = THREE.MathUtils.clamp(cursor.y + event.movementY, 0, height);
        return;
      }

      /*
       * Read in the pilot's frame, which is now free rather than computed.
       *
       * The mouse turns the target about the target's *own* axes: sideways about
       * the deck normal, up and down about the wings. That is what `aimDelta`
       * was doing by hand — rotating a screen delta by the roll — and doing it
       * as a rotation means it holds at any attitude rather than at any roll,
       * with no pole to stop short of.
       *
       * There is no clamp. Pulling up far enough goes over the top and keeps
       * going, which is the whole point of the exercise.
       */
      if (freeLook) {
        look(event.movementX, event.movementY, SENSITIVITY);
        return;
      }

      const yawStep = quatFromAxisAngle(DECK, -event.movementX * SENSITIVITY);
      const pitchStep = quatFromAxisAngle(STARBOARD, -event.movementY * SENSITIVITY);

      aim = quatNormalise(quatMultiply(quatMultiply(aim, pitchStep), yawStep));
    };

    const onPointerDown = (event: PointerEvent) => {
      if (covered.current) return;

      /*
       * A finger never asks for the pointer lock.
       *
       * Everything below is about borrowing a cursor back from a lock that hides
       * it — which is a problem touch does not have, and a request it cannot
       * satisfy. Left in, the first tap on a phone would fire a lock request
       * that either fails or, worse, succeeds and hides a pointer nobody was
       * using.
       */
      if (event.pointerType === 'touch') return;

      // However we got here, a click with no pointer lock is a request for one.
      // Without this the cursor state alone could wedge the controls.
      if (cursor.active && document.pointerLockElement !== renderer.domElement) {
        cursor.active = false;
        void renderer.domElement.requestPointerLock();
        return;
      }

      if (cursor.active) {
        // Hand the click to whatever is really under our stand-in pointer, so
        // the HUD stays ordinary HTML rather than needing its own hit-testing.
        const target = document.elementFromPoint(cursor.x, cursor.y);

        // Marked by the panel itself rather than matched on a class name here,
        // so a second panel that wants the same behaviour just says so.
        const holdsCursor =
          target instanceof Element && target.closest('[data-holds-cursor]') !== null;

        if (target instanceof HTMLElement) {
          // Text fields need the focus as well as the click, or the radio would
          // be a box you can see and cannot type into without dropping the
          // pointer lock — and the lock is expensive to retake.
          if (target instanceof HTMLInputElement) target.focus();
          target.click();
        }

        // Clicking into the chat keeps the cursor out; clicking anywhere else
        // while it is latched is how you say you are done and want the ship
        // back. The click still lands either way — leaving the chat and
        // throwing a switch are one action, not two.
        if (holdsCursor) cursor.pinned = true;
        else if (cursor.pinned) stowCursor();

        return;
      }
      if (event.button === 1) {
        event.preventDefault();
        freeLook = true;
        return;
      }
      if (document.pointerLockElement !== renderer.domElement) {
        void renderer.domElement.requestPointerLock();
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      if (event.button === 1) freeLook = false;
    };

    /*
     * ── Flying with fingers ────────────────────────────────────────────────
     *
     * One finger turns the ship; two look around. The same two things the mouse
     * does, split by how many fingers are down rather than by holding a button —
     * there is no button to hold.
     *
     * It feeds the *same* rotation the mouse does, which is only possible
     * because the aim is an orientation now: a drag is a delta about the
     * target's own axes, exactly like a mouse movement, with no pole to stop
     * short of and no roll to compensate for.
     */
    let finger: { x: number; y: number } | null = null;

    /*
     * ── `targetTouches`, never `touches` ───────────────────────────────────
     *
     * `touches` is every finger on the glass, wherever it landed. These handlers
     * are on the canvas, so reading it meant a thumb parked on the throttle cell
     * counted as a finger on the cockpit — and it broke all three things at
     * once:
     *
     *   - it was `touches[0]`, so the *aim* was measured from the thumb holding
     *     the throttle, which is not moving. Aiming died the moment any control
     *     was held.
     *   - one held cell plus one aiming finger read as `length >= 2`, so aiming
     *     silently became free look.
     *   - and free look therefore needed both fingers off the controls, because
     *     any control already counted as one of the two.
     *
     * `targetTouches` is the fingers whose `touchstart` landed on this element —
     * the cockpit itself. A cell's touches dispatch to the cell and never reach
     * here at all, which is what makes holding a control and flying at the same
     * time work rather than being two features taking turns.
     */
    const onTouchStart = (event: TouchEvent) => {
      if (covered.current) return;
      const first = event.targetTouches[0];
      finger = first ? { x: first.clientX, y: first.clientY } : null;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (covered.current) return;

      const first = event.targetTouches[0];
      if (!first || !finger) return;

      // Or the page scrolls, rubber-bands and pinch-zooms under the cockpit.
      event.preventDefault();

      const dx = first.clientX - finger.x;
      const dy = first.clientY - finger.y;
      finger = { x: first.clientX, y: first.clientY };

      /*
       * Two fingers is the head, not the ship. Same limit as the mouse: a neck
       * rather than a singularity.
       *
       * Negated, which the mouse is not. A mouse *steers* the view — push right,
       * look right — but a finger on the thing it is moving drags it, the way
       * every map and every photo on the device does, so pulling two fingers
       * right brings what is on the right into view. Feeding `look` the raw
       * delta gave the mouse's convention to a gesture that reads as direct
       * manipulation, which is the one place it is wrong.
       *
       * The single-finger aim keeps the mouse's sign: that one is steering, not
       * dragging — it is pointing the ship rather than moving the picture.
       */
      if (event.targetTouches.length >= 2) {
        look(-dx, -dy, TOUCH_SENSITIVITY);
        return;
      }

      if (!pilotHasControl(crossing.current) || approach) return;

      aim = quatNormalise(
        quatMultiply(
          quatMultiply(aim, quatFromAxisAngle(STARBOARD, -dy * TOUCH_SENSITIVITY)),
          quatFromAxisAngle(DECK, -dx * TOUCH_SENSITIVITY),
        ),
      );
    };

    /*
     * Re-seeded from whichever finger is left.
     *
     * Lifting one of two would otherwise leave the anchor on a finger that is no
     * longer there, and the next move would be measured from it — a jump of
     * however far apart the two were, which on a phone is most of the screen.
     */
    const onTouchEnd = (event: TouchEvent) => {
      const first = event.targetTouches[0];
      finger = first ? { x: first.clientX, y: first.clientY } : null;
    };

    /*
     * ── Two fingers on a trackpad ──────────────────────────────────────────
     *
     * A trackpad reports two fingers as a *wheel*, not as touches — `touchmove`
     * only ever fires on a real screen. So a laptop without a mouse had no way
     * to look around at all: free look is the middle button, and most trackpads
     * do not have one. That gap predates any of the touch work and is what this
     * closes.
     *
     * Nothing else in flight uses the wheel, so it costs nothing to spend.
     */
    const onWheel = (event: WheelEvent) => {
      if (covered.current) return;

      /*
       * Left alone when a modifier is down.
       *
       * Ctrl and wheel is how a browser is asked to zoom, and taking it would be
       * taking somebody's magnification away to look at a starfield.
       */
      if (event.ctrlKey || event.metaKey) return;

      // Same gate as the mouse. From the lock onward the autopilot owns the
      // view and the cockpit is dark; there is nothing to look at.
      if (!pilotHasControl(crossing.current) || approach) return;

      event.preventDefault();

      /*
       * Normalised, because a wheel does not measure in pixels the way a pointer
       * does. A trackpad reports pixels, a mouse wheel usually reports *lines*
       * and occasionally pages — and without this a notch of a real wheel would
       * throw the view across the cockpit while a trackpad barely moved it.
       */
      const scale =
        event.deltaMode === 1 ? WHEEL_LINE : event.deltaMode === 2 ? WHEEL_PAGE : 1;

      look(event.deltaX * scale, event.deltaY * scale, SENSITIVITY);
    };

    /**
     * Alt reaches for a control without letting go of the mouse.
     *
     * Releasing pointer lock and re-taking it was the obvious approach and is
     * unusable: browsers impose a cooldown of over a second before a lock may
     * be retaken, so every trip to a switch cost a noticeable pause coming back.
     *
     * So the lock is never released. Alt freezes the aim and starts moving a
     * cursor of our own from the same movement deltas, drawn over the HUD, and
     * a click is dispatched to whatever real element sits under it. Instant in
     * both directions, because nothing has actually changed hands.
     */
    const onKeyDown = (event: KeyboardEvent) => {
      // Typing at a map should not also fly the ship.
      if (covered.current) return;

      if (event.key === 'Alt') {
        event.preventDefault();
        if (!cursor.active) {
          cursor.active = true;
          cursor.x = width / 2;
          cursor.y = height / 2;
          // Anything held stays held otherwise, since its key-up will be eaten
          // while the cursor has focus.
          held.clear();
        }
        return;
      }
      held.add(event.key.toLowerCase());
    };

    /**
     * Put the stand-in cursor away and take the keyboard back.
     *
     * The blur is the important half. Clicking the radio focuses a text field,
     * and a focused text field swallows every keystroke before the flight loop
     * sees it — so dismissing the cursor without dropping focus left a ship
     * that ignored the controls while the player typed W into a chat box.
     */
    const stowCursor = () => {
      cursor.pinned = false;
      cursor.active = false;

      const focused = document.activeElement;
      if (
        focused instanceof HTMLElement &&
        focused !== renderer.domElement &&
        focused !== document.body
      ) {
        focused.blur();
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (covered.current) return;

      if (event.key === 'Alt') {
        event.preventDefault();
        // Latched into the chat: letting go of alt is not letting go of the
        // conversation. A click outside is.
        if (!cursor.pinned) stowCursor();
        return;
      }
      held.delete(event.key.toLowerCase());
    };
    const onLockChange = () => setLocked(document.pointerLockElement === renderer.domElement);

    /**
     * Let go of everything when the window does.
     *
     * Alt-tabbing away while the cursor was up left `cursor.active` true with no
     * key-up ever arriving to clear it — and with the cursor up, a click is
     * forwarded to whatever is under it instead of retaking the pointer lock.
     * The result was a window you could not click back into. Anything held goes
     * the same way, or the ship carries on accelerating in a tab you are not
     * looking at.
     */
    const onBlur = () => {
      // Unconditional, latch and all: a window you have tabbed away from should
      // not be holding a cursor open on your behalf.
      stowCursor();
      freeLook = false;
      held.clear();
    };

    const blockMenu = (event: Event) => event.preventDefault();

    /*
     * Take the mouse back after a rebuild that took it away.
     *
     * Only when *we* released it — `hadPointer` is set in the teardown, never
     * by the player pressing escape — so this cannot re-grab a pointer somebody
     * deliberately let go of. Browsers allow re-locking after a programmatic
     * exit; if this one refuses, the click prompt is still there and nothing is
     * worse than it was.
     */
    if (hadPointer.current) {
      hadPointer.current = false;
      void renderer.domElement.requestPointerLock()?.catch?.(() => undefined);
    }

    releaseControls.current = () => {
      held.clear();
      freeLook = false;
      stowCursor();
      if (document.pointerLockElement === renderer.domElement) document.exitPointerLock();
    };

    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    // `passive: false` on the move, because it has to be able to stop the page
    // scrolling; the other two never call `preventDefault` and stay passive.
    // Not passive: it has to be able to stop the page scrolling under the game.
    renderer.domElement.addEventListener('wheel', onWheel, { passive: false });
    renderer.domElement.addEventListener('touchstart', onTouchStart, { passive: true });
    renderer.domElement.addEventListener('touchmove', onTouchMove, { passive: false });
    renderer.domElement.addEventListener('touchend', onTouchEnd, { passive: true });
    renderer.domElement.addEventListener('touchcancel', onTouchEnd, { passive: true });
    renderer.domElement.addEventListener('contextmenu', blockMenu);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onBlur);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('pointerlockchange', onLockChange);

    // Names on what is in front of you, with how far away it is. Same brackets
    // the chart uses, so a station looks like itself in both places.
    const markers = createTargetMarkers(labelLayer, built.labels);

    /*
     * Jump destinations, as directions on the sky.
     *
     * The direction is the galactic offset, which is the same arithmetic
     * `systemSky` uses to place the point of light this marker sits on — so the
     * label lands on the star it names rather than near it.
     */
    const markerTargets = (list: typeof jumpable) =>
      list.map((target) => ({
        systemId: target.systemId,
        name: target.name,
        clusterName: target.clusterName,
        sameCluster: target.sameCluster,
        reach: target.verdict.reach,
        detail: target.verdict.detail,
        distance: target.distance,
        direction: new THREE.Vector3(
          target.direction.x,
          target.direction.y,
          target.direction.z,
        ),
      }));

    let jumpMarkers = createJumpMarkers(jumpLayer, markerTargets(jumpable));
    // Whatever the route is right now, rather than waiting for it to change.
    jumpMarkers.setRoute(route.current);
    /** Which list the markers on screen were built from. See the loop. */
    let markersFrom = jumpable;

    // The keybind card, on the console rather than pinned over the sky.
    const card = createControlsCard(CONTROLS, CARD_WIDTH);
    hullGroup.add(card.mesh);

    // ── Loop ─────────────────────────────────────────────────────────────────
    const clock = new THREE.Clock();
    const forward = new THREE.Vector3();
    const right = new THREE.Vector3();
    const up = new THREE.Vector3();
    const eye = new THREE.Vector3();
    const pivot = new THREE.Vector3();
    const relative = new THREE.Vector3();
    const drift = new THREE.Vector3();
    const away = new THREE.Vector3();
    const contactLocal = new THREE.Vector3();
    const facingInverse = new THREE.Quaternion();
    const hullQuaternion = new THREE.Quaternion();
    const inverse = new THREE.Quaternion();

    let publish = 0;
    let frame = 0;

    const tick = () => {
      frame = requestAnimationFrame(tick);
      const step = Math.min(clock.getDelta(), 0.1);

      // ── Rebase ───────────────────────────────────────────────────────────
      // Shift the world so the ship is at the origin. Done first, so everything
      // below reads a consistent set of scene-space world matrices — and the
      // shaders, which see only single precision, get numbers small enough to
      // hold still.
      origin.copy(shipAt);
      worldRoot.position.copy(origin).negate();
      built.lightPosition.copy(origin).negate();
      hullGroup.position.set(0, 0, 0);

      built.animate(flightTime());

      // ── Mass lock ────────────────────────────────────────────────────────
      // Whatever has the firmest grip on us. Checked before anything else,
      // because it gates the warp drive and supplies the motion we inherit.
      let rockHit = false;
      let lockedOn:
        | { label: string; strength: number; node: THREE.Object3D; kind: MarkerKind }
        | null = null;

      // Purely for the readout — what the panel in the corner should be talking
      // about. Deliberately not the same thing as the anchor: you want to be
      // told what you are approaching long before it has hold of you.
      let closest: { label: string; kind: string; surface: number } | null = null;

      /** The body cruise is flying to, resolved fresh each frame. */
      let destination: (typeof built.focusables)[number] | null = null;

      for (const focus of built.focusables) {
        const distance = trueOf(focus.node, relative).distanceTo(shipAt);

        // Nothing grips a ship in warp. Dropping out on contact was the first
        // cut and it is the wrong shape: it means every transit that passes
        // near a world ends by itself, and arriving is something you should
        // choose. In warp you fly straight through.
        // The scene's kind is passed straight through — `MassLockKind` is the
        // same union, so a body the renderer knows about and the rules do not
        // is a compile error rather than a silent fallback to some default.
        const strength = warping ? 0 : massLockStrength(distance, focus.radius, focus.kind);
        if (strength > 0 && (!lockedOn || strength > lockedOn.strength)) {
          lockedOn = { label: focus.label, strength, node: focus.node, kind: focus.kind };
        }

        // Surface distance, not centre distance: being told you are two
        // thousand units from a planet you are parked beside is the kind of
        // number that makes a readout untrustworthy.
        const surface = distance - focus.radius;
        if (!closest || surface < closest.surface) {
          closest = { label: focus.label, kind: focus.kind, surface };
        }

        if (focus.label === cruising) destination = focus;

      }

      // Ride along with it. Without this, holding position beside a pad means
      // continuously flying to stay level with something that is itself in
      // orbit — formation flying, not docking, and one glance away from having
      // drifted off.
      // Inherited whole, not in proportion to the grip.
      //
      // Scaling the drift by `strength` looked like a softer hold and was not:
      // inheriting nine tenths of a body's motion leaves you moving at a tenth
      // of it for ever. Beside a moon that is a slide of some twenty-five units
      // a second — fifteen hundred a minute — with the lamp lit and the throttle
      // at zero, which is exactly the drift that kept being reported.
      //
      // So a lock is a lock: inside the band you are in the body's frame. The
      // strength curve still earns its keep gating the warp drive and driving
      // the lamp, where a fade is what you want.
      if (lockedOn) {
        trueOf(lockedOn.node, relative);
        const previous = anchorMemory.get(lockedOn.label);
        if (previous) shipAt.add(drift.subVectors(relative, previous));
      }
      anchorMemory.clear();
      for (const focus of built.focusables) {
        anchorMemory.set(focus.label, trueOf(focus.node, relative).clone());
      }

      // ── Cruise ───────────────────────────────────────────────────────────
      //
      // Flies straight at the thing and stops inside its mass lock. Not a
      // solver — nothing here avoids anything, and if a moon is on the line you
      // will meet it. The point is that you can take your hands off the stick,
      // not that you can stop looking out of the window.
      // The same gate the warp drive uses, minus the speed. Cruise wants warp
      // on any leg worth engaging it for, so letting it start on the precision
      // set or with the gear down only produced an autopilot that set off at
      // fourteen units a second and never said why.
      const cruiseGate = canEngageCruise(
        controls.current.set,
        controls.current.gear,
        lockedOn?.strength ?? 0,
        aimedAt !== null,
      );

      if (controls.current.toggleCruise) {
        controls.current.toggleCruise = false;
        cruising = cruising ? null : cruiseGate.engaged ? aimedAt : null;

        // Resolved again here, and this is not redundant. The sweep above ran
        // before the toggle, so on the frame the button is pressed it looked up
        // the *old* destination — which was nothing — and the check below then
        // cancelled the cruise on the same frame it started. It could never
        // engage at all.
        destination = cruising
          ? (built.focusables.find((focus) => focus.label === cruising) ?? null)
          : null;
      }

      // Arrival, as opposed to being called off. A lock is what cruise was
      // aiming for, so reaching one is the trip finishing — and it should leave
      // the ship stopped rather than still asking for whatever speed it was
      // holding a moment ago.
      if (cruising && (lockedOn?.strength ?? 0) > 0.05) {
        cruising = null;
        throttle = 0;
      }
      if (cruising && !destination) cruising = null;

      let cruiseThrottleWanted: number | null = null;
      let cruiseWarpWanted = false;

      if (cruising && destination) {
        trueOf(destination.node, relative);
        toTarget.subVectors(relative, shipAt);

        const remaining =
          toTarget.length() - cruiseStandoff(destination.radius, MASS_LOCK_REACH[destination.kind]);

        // Measured against where the ship is actually *going*, not where its
        // nose is pointing — a hull sliding sideways is off course however
        // neatly it is aimed. This is what the pilot's hand on the stick trips.
        const heading = velocity.length() > 0.001 ? velocity.clone().normalize() : null;
        const bearing = toTarget.clone().normalize();

        if (heading && cruiseHasStrayed(heading.angleTo(bearing), velocity.length())) {
          cruising = null;
        } else {
          const driving = handlingOf(performance, controls.current.set, warping);

          // Planned against the *braking* rate, which is the one that decides
          // how early the approach has to start.
          cruiseThrottleWanted = cruiseThrottle(
            remaining,
            driving.deceleration,
            driving.maxSpeed,
          );

          // Compared as whole trips rather than by a rule of thumb — the rule of
          // thumb this replaced held out for two hundred thousand units when
          // warp starts paying at ten.
          cruiseWarpWanted = cruiseShouldWarp(
            remaining,
            handlingOf(performance, 'main', false),
            handlingOf(performance, 'main', true),
          );

          // Close enough to be finished. The mass lock usually gets there
          // first, but relying on it is fragile: the throttle only approaches
          // zero, and the main drive's dead band brings the ship to a stop a
          // couple of units short of the mark — at which point nothing is left
          // to close the gap and cruise would hold on for ever.
          if (cruiseArrived(remaining, driving.deceleration)) {
            cruising = null;
            cruiseThrottleWanted = 0;
            throttle = 0;
          }

          /*
           * Steer. Blended rather than snapped, so a nudge of the mouse moves
           * the ship and is then gently undone instead of being fought for the
           * frame it lands on.
           *
           * `pointNose` rather than a heading built from the bearing: an
           * autopilot should turn the hull onto a course, not also decide which
           * way up the pilot is sitting. Levelling somebody out because they
           * asked to fly somewhere would be a surprise every time.
           */
          const onCourse = pointNose(aim, { x: bearing.x, y: bearing.y, z: bearing.z });
          const blend = Math.min(1, step * CRUISE_STEER_RATE);

          aim = rotateToward(aim, onCourse, angleBetween(aim, onCourse) * blend);
        }
      }

      // ── Warp ─────────────────────────────────────────────────────────────
      // Forward speed, not total. See the note where `along` is declared.
      const speedNow = along;
      const engage = canEngageWarp(
        controls.current.set,
        speedNow,
        lockedOn?.strength ?? 0,
        controls.current.gear,
      );

      const wasWarping = warping;

      // Cruise drives the same switch the pilot does, so everything downstream
      // — the stall, the throttle handoff, the lamp — behaves identically
      // whoever threw it.
      /*
       * The jump module.
       *
       * Stowed the moment its footing goes — dropping the gear or falling back
       * to the precision set while a chart of destinations hung on the sky
       * would leave the sky claiming you could still go to them.
       */
      const jumpGate = canEngageJump(controls.current.set, controls.current.gear);

      if (controls.current.toggleJump) {
        controls.current.toggleJump = false;
        if (jumpGate.engaged || controls.current.jump) setJump((current) => !current);
      }
      if (controls.current.jump && !jumpGate.engaged) setJump(false);

      /*
       * A destination pinned since the scene was built.
       *
       * `setRoute` can only recolour a marker that exists, and the list the
       * markers were built from was the one in force at the time. Pin a
       * destination from a mission board while flying and the next hop may be a
       * system that was outside the cap — so there was nothing to turn the route
       * colour, and accepting a mission appeared to do nothing to the sky until
       * the next jump rebuilt the scene.
       *
       * Rebuilt rather than patched: `jumpable` also changes reach and detail as
       * the drive changes, and the marker elements are plain HTML in a layer —
       * cheap to make, and nothing else in the scene is touched.
       *
       * Identity is the test, which is exactly right here: `jumpable` is a
       * `useMemo`, so a new array *is* the signal that one of its inputs moved.
       */
      if (markersFrom !== jumpableRef.current) {
        markersFrom = jumpableRef.current;
        jumpMarkers.dispose();
        jumpMarkers = createJumpMarkers(jumpLayer, markerTargets(markersFrom));
      }

      jumpMarkers.setVisible(
        controls.current.jump && jumpGate.engaged && pilotHasControl(crossing.current),
      );

      /*
       * The route, applied every frame.
       *
       * ── Why this is not guarded ────────────────────────────────────────────
       *
       * It was, on a ref holding the last route painted — and the guard was the
       * bug. `setRoute` paints *markers*, and the markers are rebuilt whenever
       * the scene is: undocking clears the berth, the berth is a dependency of
       * the scene effect, and so a fresh set of markers appeared knowing nothing
       * about the route while the ref still said "already done". The result was
       * a destination drawn in plain reachable amber with a route live in the
       * HUD beside it.
       *
       * There is nothing to save by guarding. `setRoute` already skips every
       * marker whose state has not changed, so the whole call is a dozen
       * comparisons on a frame where nothing moved — and it can no longer be out
       * of step with markers it did not create.
       */
      jumpMarkers.setRoute(route.current);

      const pilotAsked = controls.current.toggleWarp;
      controls.current.toggleWarp = false;

      // Cruise only ever asks to *light* the drive. Letting it ask to shut one
      // down meant engaging cruise while already in warp dropped you out of it
      // on the spot — the autopilot had not yet worked out it wanted warp, saw
      // a mismatch, and threw the switch. Warp already ends itself the moment
      // the throttle lets it stall, which is the same decision arrived at from
      // the physics rather than from a disagreement about intent.
      const cruiseAsks = cruising !== null && cruiseWarpWanted && !warping;

      if (pilotAsked || cruiseAsks) {
        if (warping) {
          if (canDisengageWarp(speedNow)) warping = false;
        } else if (engage.engaged) {
          warping = true;
        }
      }

      // Two ways out on their own: losing the drive, or running out of the
      // speed that sustains it. The second is the ordinary one — the throttle
      // cannot command below the floor, so easing off in warp decelerates you
      // to exactly it and arriving there ends the drive.
      //
      // Proximity used to end it too, which meant a transit that happened to
      // pass a planet dropped you out beside it. See the mass lock note above.
      if (warping && (controls.current.set !== 'main' || warpHasStalled(speedNow))) {
        warping = false;
      }

      const flying = handlingOf(performance, controls.current.set, warping);

      /*
       * A locked jump lifts the ceiling past what warp can hold.
       *
       * The drive is doing something it cannot sustain, and the gauge running
       * past its own red line is the plainest way to say so — it also removes
       * the oddity of a *jump* that topped out at exactly the speed you can
       * reach on any ordinary crossing of a system.
       */
      const handling =
        crossing.current.phase === 'running'
          ? { ...flying, maxSpeed: JUMP_MAX_SPEED }
          : flying;

      // Dropping out rescales the throttle, because the throttle is a fraction
      // of a top speed and that top speed just fell from 800 to 120. Leaving it
      // alone meant arriving at a hundred units a second with the lever asking
      // for fifteen, and the ship standing on its brakes the instant it
      // returned to normal space. Handing it the speed you actually have keeps
      // the transition silent.
      if (wasWarping && !warping && handling.maxSpeed > 0) {
        throttle = THREE.MathUtils.clamp(speedNow / handling.maxSpeed, -1, 1);
      }

      // Recentre, when asked. Consumed here rather than in the handler so it
      // lands on a frame boundary alongside everything else.
      if (controls.current.recentre) {
        controls.current.recentre = false;
        freeLook = false;
        headOffset.yaw = 0;
        headOffset.pitch = 0;
      }

      // The hull chases the aim, winding up and settling out. The gap between
      // the two is the handling.
      //
      // Turn authority falls away logarithmically with speed, so a ship at
      // cruise is a barge and one nudging onto a pad is not. Roll is left at
      // full rate: turning fights the momentum you already have, where rolling
      // about the nose does not, and a warp transit you cannot even level out
      // in would be miserable rather than weighty.
      const turning = { turnRate: handling.turnRate * turnAuthority(velocity.length()) };

      /*
       * ── The reticle cannot be thrown away ──────────────────────────────────
       *
       * The aim is a free rotation with no stop in it, which is the whole point
       * of the 6DOF work — but it means a flick of the wrist can put the thing
       * you are steering with three full turns behind your head, and the only
       * way back is to drag all of it out again in the dark. That is not a
       * skill; it is losing the mouse.
       *
       * So the aim is tethered to the hull rather than to the world. Beyond the
       * limit the reticle stops running and the ship keeps turning toward it, so
       * a long drag is still a long turn — you can spin the ship as far as you
       * like, at the rate it can actually fly. What you cannot do is get further
       * ahead of it than one screen and a half.
       *
       * Measured against the hull and not the camera, deliberately: free look
       * turns the head, and the head has no business deciding how far the ship
       * may be asked to turn.
       */
      const halfHorizontal = Math.atan(
        Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) * camera.aspect,
      );

      /*
       * A radius of one and a half screen widths, as an angle at this camera.
       *
       * In half-widths that is three, and half a width is the tangent of half
       * the horizontal field of view — so the tolerance is stated in the units
       * it was judged in and still holds on a phone, on an ultrawide, and at
       * whatever field of view the game ends up with.
       */
      const aimReach = Math.atan(AIM_RADIUS * 2 * Math.tan(halfHorizontal));

      if (angleBetween(heading.orientation, aim) > aimReach) {
        aim = rotateToward(heading.orientation, aim, aimReach);
      }

      heading = stepOrientation(heading, aim, turning, step);

      /*
       * Q rolls to port, E to starboard, from the pilot's seat.
       *
       * ── Turned on both, so it stays instant ────────────────────────────────
       *
       * Roll is part of the orientation now rather than a number applied on top,
       * and easing it would be a change to the feel: turning fights the momentum
       * you already have where rolling about the nose does not, and a warp
       * transit you cannot even level out in would be miserable rather than
       * weighty.
       *
       * So the roll is applied to the *target and the hull together*. Neither
       * gains on the other, there is no error for `stepOrientation` to work off,
       * and the ship rolls at full rate exactly as it always did.
       */
      const rolling = (action: Action) =>
        controls.current.touch.has(action) ||
        BINDINGS[action].keys.some((key) => held.has(key));
      const rollInput = (rolling('rollRight') ? 1 : 0) - (rolling('rollLeft') ? 1 : 0);

      if (rollInput !== 0) {
        const rolled = quatFromAxisAngle(NOSE_AXIS, rollInput * handling.rollRate * step);
        aim = quatNormalise(quatMultiply(aim, rolled));
        heading = {
          ...heading,
          orientation: quatNormalise(quatMultiply(heading.orientation, rolled)),
        };
      }

      // The head is an offset, so it stays where it was left. Nothing resets it
      // but `V`.
      controls.current.offCentre =
        Math.abs(headOffset.yaw) > 0.01 || Math.abs(headOffset.pitch) > 0.01;

      /*
       * The heading *is* the orientation, roll and all.
       *
       * It was a heading with a roll composed on top, because roll used to be a
       * number the keys moved directly. Roll is now applied to the target and
       * the hull together — see where the keys are read — so by the time it
       * reaches here it is already part of the rotation, and adding it again
       * would double it.
       */
      hullQuaternion.set(
        heading.orientation.x,
        heading.orientation.y,
        heading.orientation.z,
        heading.orientation.w,
      );
      hullGroup.quaternion.copy(hullQuaternion);

      // On the main drive the throttle is a *setting*: W and S move it and the
      // ship works toward it, so a cruise can be held. On the precision set it
      // is a push — let go and you stop, which is what makes manoeuvring
      // thrusters safe to dock on. A throttle that kept its setting would drift
      // you into the pad the moment your attention moved.
      /*
       * Keys, unless the ship is flying itself.
       *
       * A locked crossing takes every input, not just the throttle: the run-up
       * is a fixed heading at a fixed setting, and a pilot who could still
       * strafe or roll would be able to fight an autopilot that will not give
       * way. `dock` is the exception — it is the key being *held* to commit, so
       * reading it has to keep working.
       */
      const piloted = pilotHasControl(crossing.current) && !approach;
      const down = (action: Action) =>
        (piloted || action === 'dock') &&
        (controls.current.touch.has(action) ||
          BINDINGS[action].keys.some((key) => held.has(key)));
      const forwardInput = (down('throttleUp') ? 1 : 0) - (down('throttleDown') ? 1 : 0);

      if (cruiseThrottleWanted !== null && forwardInput === 0) {
        throttle = cruiseThrottleWanted;
      } else if (handling.holdsThrottle) {
        if (forwardInput > 0) throttle = Math.min(1, throttle + step * 0.9);
        if (forwardInput < 0) throttle = Math.max(-0.4, throttle - step * 0.9);
      } else if (forwardInput !== 0) {
        throttle = THREE.MathUtils.clamp(throttle + forwardInput * step * 2.5, -1, 1);
      } else {
        // Falls back to rest rather than snapping, so letting go coasts to a
        // halt instead of stopping dead.
        throttle -= Math.sign(throttle) * Math.min(Math.abs(throttle), step * 3);
      }

      // Warp has a speed floor, so a negative setting cannot mean anything —
      // the lever would sit below a stop the ship can never go under, and the
      // gauge would show a reverse that is not happening.
      if (warping) throttle = Math.max(0, throttle);

      /*
       * The run-up.
       *
       * Throttle pinned and the nose swung onto the destination, because the
       * stretch between committing and the tunnel is not a piece of flying — it
       * is the ship going. Aiming rather than snapping so the hull visibly
       * comes about, which is the only thing that makes the commitment look
       * like it did something.
       */
      if (crossing.current.phase === 'running') {
        throttle = 1;

        const bearing = crossing.current.systemId ? jumpBearings.get(crossing.current.systemId) : null;
        // Hard-set, because the run is a fixed heading and not a suggestion —
        // but on the nose alone, so a pilot who committed while rolled crosses
        // the way they were flying rather than being righted by the autopilot.
        if (bearing) aim = pointNose(aim, { x: bearing.x, y: bearing.y, z: bearing.z });
      }

      forward.set(0, 1, 0).applyQuaternion(hullQuaternion);
      right.set(1, 0, 0).applyQuaternion(hullQuaternion);
      up.set(0, 0, 1).applyQuaternion(hullQuaternion);

      along = velocity.dot(forward);
      // Warp cannot idle, and nothing else has a floor. Doing this by hand with
      // a `max` was what quietly deleted reverse — see `commandedSpeed`.
      const wanted = commandedSpeed(throttle, handling);

      // Accelerated toward the wanted speed at the hull's own rate, not lerped
      // to it. A lerp reaches any target in the same time whatever the ship is
      // made of, which is precisely the weightlessness we are trying to lose:
      // thrust has to be what decides how long getting up to speed takes.
      //
      // Winding up and slowing down are separate rates. Outside warp they are
      // the same number, but in warp they are not — and using one for both is
      // what tied a quicker departure to a later commitment on arrival.
      const gap = wanted - along;
      const slowing = along !== 0 && Math.sign(gap) !== Math.sign(along);
      /*
       * Multiplied during the run-up, and again past the surge speed.
       *
       * The stretch between committing and the tunnel is scripted, so the only
       * thing its length can do is bore — and the second doubling lands after
       * the console has gone dark, which is what makes the blackout read as a
       * threshold being crossed rather than a wait being endured.
       */
      const rate =
        (slowing ? handling.deceleration : handling.acceleration) *
        runAcceleration(crossing.current, along);

      const push = Math.sign(gap) * Math.min(Math.abs(gap), rate * step);
      velocity.addScaledVector(forward, push);

      // Strafing fades out with speed, so it is a docking control rather than
      // a second way to travel.
      const authority = strafeAuthority(along, handling.maxSpeed);
      const sideways = (down('strafeRight') ? 1 : 0) - (down('strafeLeft') ? 1 : 0);
      const vertical = (down('up') ? 1 : 0) - (down('down') ? 1 : 0);

      if (sideways !== 0) {
        velocity.addScaledVector(right, sideways * handling.strafeAcceleration * authority * step);
      }
      if (vertical !== 0) {
        velocity.addScaledVector(up, vertical * handling.strafeAcceleration * authority * step);
      }

      // Sideways drift bleeds off on its own; there is no flight-assist toggle
      // yet and a ship that slid forever would be unlandable.
      const lateral = velocity.clone().addScaledVector(forward, -along);
      velocity.addScaledVector(lateral, -Math.min(1, step * 1.5));

      shipAt.addScaledVector(velocity, step);

      /*
       * Coming in from the edge.
       *
       * Overrides the position outright rather than pushing the ship along with
       * a velocity, because the speeds involved are nothing the drive could
       * produce and pretending otherwise would put a number on the gauge that
       * made no sense. It is the tail of a jump, not a piece of flying.
       *
       * Cubic rather than a sharper ease, and that is tuned against the fade
       * rather than chosen for its own sake. The streaks thin over the same
       * 2.6 seconds, so the first half of the run in is hidden and only the
       * second half is ever seen — under a quintic the ship had already covered
       * 97% of the distance by then and the visible part was a ship sitting
       * still. A cubic leaves it a thousand-odd units out and still slowing
       * when the view clears, which is the bit worth watching.
       */
      /*
       * Held at the entry point until the crossing actually ends.
       *
       * The scene is now rebuilt part-way *into* the tunnel, so this exists —
       * and the ship is sitting at the far edge of the new system — for the
       * thirteen seconds that are left of it. Spending it early would have the
       * arrival play out behind a black screen and finish before anyone could
       * see it.
       */
      if (approach && approach.left > 0 && !inTunnel(crossing.current)) {
        approach.left = Math.max(0, approach.left - step);

        const t = 1 - approach.left / ARRIVAL_GLIDE;
        const eased = 1 - Math.pow(1 - t, 3);

        shipAt.lerpVectors(approach.from, approach.to, eased);
        velocity.set(0, 0, 0);

        if (approach.left === 0) approach = null;
      }

      // ── Hitting something ────────────────────────────────────────────────
      //
      // A plain distance check, which is all a sphere needs: the smallest moon
      // in the galaxy is over four hundred units across and the ship covers
      // thirteen in a frame at warp, so there is nothing here to tunnel through.
      // Decks and other ships are a different problem and are not in this list.
      damageCooldown = Math.max(0, damageCooldown - step);

      /*
       * Nothing to hit only once the tunnel is up.
       *
       * The run-up still collides, deliberately. Committing to a heading that
       * goes through a planet is a decision the player made with five seconds
       * to think about it, and taking the consequence away would make the aim
       * not matter. Inside the tunnel there is nothing to hit — the ship is
       * covering twelve thousand units of a system it has already left.
       */
      if (integrity > 0 && !inTunnel(crossing.current)) {
        for (const sphere of built.spheres) {
          trueOf(sphere.node, relative);

          // Out from the centre to the ship — the direction it gets pushed, and
          // the axis the collision is measured along.
          away.subVectors(shipAt, relative);
          const surface = sphere.radius + shipExtent.plan;
          const distance = away.length();
          if (distance >= surface || distance === 0) continue;

          away.divideScalar(distance);
          const impact = impactAt(velocity.length());

          // Put the hull on the surface rather than leaving it embedded, or the
          // next frame collides again from further in and the ship sinks.
          shipAt.copy(relative).addScaledVector(away, surface);

          // A severe hit takes everything; a glancing one only takes the part
          // that was going into the surface, so a shallow pass stays a pass.
          if (impact.killsMomentum) velocity.set(0, 0, 0);
          else velocity.addScaledVector(away, -Math.min(0, velocity.dot(away)));

          velocity.addScaledVector(away, impact.pushback);

          // Every line, reduced by its own strength — see `damageHull`. A
          // fatal hit is simply more damage than any hull carries.
          //
          // Behind the same brief immunity a deck strike is: the shove happens
          // every frame it is needed, the bill does not.
          if (damageCooldown <= 0) {
            hull_ = damageHull(hull_, impact.damage, struckLines(away));
            integrity = hullIntegrity(hull_, blueprint);
            if (impact.severity === 'destroyed') {
              integrity = 0;
              velocity.set(0, 0, 0);
            }
            hullDirty = true;
            // Walls that went to zero take whatever they were bracing with them.
            settle();

            lastImpact = {
              body: sphere.label,
              severity: impact.severity,
              speed: velocity.length() || impact.pushback,
              at: ++impactSeq,
            };
            damageCooldown = DAMAGE_IFRAMES;
          }

          // Whatever you were asking the ship to do, you are not asking it any
          // more. Leaving the lever where it was had the hull grinding back
          // into the surface it had just been shoved off.
          throttle = 0;
          warping = false;
          cruising = null;

          // One surface per frame. Two spheres cannot overlap, so a second hit
          // in the same step would only be the response from the first.
          break;
        }

        /*
         * And the rocks.
         *
         * ── The conversion is the whole risk ──────────────────────────────────
         *
         * `asteroidHit` works entirely in a belt's own frame, because that frame
         * turns as the belt orbits and everything inside it is therefore static.
         * Which means the one thing this has to get right is putting the ship
         * *into* that frame — `worldToLocal` on the node the rocks are children
         * of, which carries both the belt's rotation and the system group's
         * scale.
         *
         * Do it by hand and the ship is measured in world units against rocks in
         * generator units, twenty times too far out, and nothing ever collides.
         * That is silent, which is why it is one call and not arithmetic.
         *
         * `shipExtent.plan` is divided for the same reason: it is a world
         * measurement and everything it is compared against is not.
         */
        if (!rockHit) {
          for (const belt of built.fields) {
            /*
             * Refreshed before it is read.
             *
             * `worldToLocal` inverts `matrixWorld`, and three.js only recomputes
             * that during render — which happens *after* this. On the first
             * frame of a scene it has never been computed at all, so the matrix
             * is the identity and the ship converts to somewhere near the star;
             * on every frame after, it is one belt-rotation stale. Neither is
             * visible as anything except collisions that do not happen.
             */
            belt.node.updateMatrixWorld();

            shipInField.copy(shipAt).sub(origin);
            belt.node.worldToLocal(shipInField);

            const hit = asteroidHit(
              belt.field,
              belt.rocks,
              belt.positions,
              shipInField,
              shipExtent.plan / GAME_SCALE,
              belt.basis,
              brokenRocks.get(belt.field.id),
            );
            if (!hit) continue;

            /*
             * Out of the rock, in world terms.
             *
             * The normal comes back in the belt's frame, so it is rotated into
             * the world the same way the ship was rotated out of it — direction
             * only, hence `transformDirection`, which drops the translation and
             * renormalises.
             */
            away
              .set(hit.normal.x, hit.normal.y, hit.normal.z)
              .transformDirection(belt.node.matrixWorld)
              .normalize();

            // Lifted clear along that normal, or the next frame collides again
            // from further in and the ship sinks into the rock.
            shipAt.addScaledVector(away, hit.depth * GAME_SCALE);

            const closing = Math.max(0, -velocity.dot(away));
            const impact = stationImpactAt(closing);

            // Only the part of the motion going into the rock. A shallow pass
            // stays a pass — you are meant to be able to fly through a belt.
            velocity.addScaledVector(away, -Math.min(0, velocity.dot(away)));
            velocity.addScaledVector(away, impact.pushback);

            if (damageCooldown <= 0) {
              hull_ = damageHull(hull_, impact.damage, struckLines(away));
              integrity = hullIntegrity(hull_, blueprint);
              hullDirty = true;
              settle();

              /*
               * The rock takes it as well.
               *
               * Both ends of the collision are real: the hull is dented and the
               * rock is chipped, and a heavy enough run at a small rock breaks
               * it. Behind the same immunity as the hull damage, so a run of
               * frames in contact is one bite rather than sixty.
               */
              damageRock(belt.field.id, hit.index, impact.damage, hit.rock.health);

              lastImpact = {
                body: 'asteroid',
                severity: impact.severity,
                speed: closing,
                at: ++impactSeq,
              };
              damageCooldown = DAMAGE_IFRAMES;
            }

            throttle = 0;
            warping = false;
            cruising = null;

            /*
             * One rock per frame.
             *
             * The deepest overlap already won inside `asteroidHit`, and being
             * shoved out of two rocks in one step is how a ship gets flung.
             */
            rockHit = true;
            break;
          }
        }

        /*
         * And the stations.
         *
         * Separate from the spheres because a station is not one: it is a slab
         * with things bolted to the top, and the deck and its clutter are two
         * different shapes tested two different ways — see `stationHit`.
         *
         * Everything happens in the deck's own frame, which already carries the
         * station's orbit, spin and scale. That turns "where is the ship
         * relative to this rotating thing" into one `worldToLocal` rather than
         * transforming every volume on it every frame.
         */
        // Which space each vector is in is the whole difficulty here, and it is
        // not something a type can hold — so it lives in one place that the
        // test calls too. See `stationCollision`.
        const hit = stationImpact(
          solidStations(),
          shipAt,
          origin,
          hullQuaternion,
          footprint,
          velocity,
        );

        if (hit) {
          const struck = hit.struck;
          away.copy(hit.normal);

          /*
           * How fast the ship was going *into* the surface, rather than how fast
           * it was going.
           *
           * This is what `impactAt` documents itself as taking, and against a
           * deck the difference is the whole character of the thing: a hull
           * running the length of a station at two hundred, barely touching it,
           * is not arriving at two hundred. Spheres get away with total speed
           * because you meet a planet head on or not at all.
           */
          const closing = Math.max(0, -velocity.dot(away));
          const impact = stationImpactAt(closing);

          /*
           * Put back where the frame started, rather than shoved out.
           *
           * The frame opened with the ship clear — that is what `origin` is — so
           * the whole of the intersection happened during this step, and undoing
           * the step undoes the intersection. It moves the ship by at most one
           * frame of travel, which is not something you can see.
           *
           * Pushing out along the normal instead meant moving by however deep
           * the hull had got, and against a slab several units thick that is a
           * visible jump backwards — sometimes into the next thing along, which
           * collided in turn. Cancelling the velocity is what actually stops the
           * ship entering; the position only has to stop being wrong.
           */
          shipAt.copy(origin);

          if (velocity.length() > STATION_DRIFT_SPEED) {
            if (impact.killsMomentum) velocity.set(0, 0, 0);
            else velocity.addScaledVector(away, -Math.min(0, velocity.dot(away)));

            // Out at half what came in — see `stationImpactAt`. Applied after
            // the inward part is cancelled, so this *is* the outward speed
            // rather than something added to whatever was left.
            velocity.addScaledVector(away, impact.pushback);
          } else {
            /*
             * Too slow for any of that to work.
             *
             * Undoing the step is only a resolution because the step was worth
             * undoing: at a drift it is a fraction of a unit, so a hull already
             * overlapping stays overlapping. And the bounce is proportional, so
             * a slow contact bounces slowly — which is not enough to separate,
             * and the ship sits in the surface collecting a fresh scrape every
             * frame until it dies of parking.
             *
             * So below a drift the ship is simply set moving out, at a fixed
             * speed, with everything else cleared. It is the one case where the
             * response should not be physical: nothing interesting is happening,
             * the ship just needs to not be inside a wall.
             */
            velocity.copy(away).multiplyScalar(impact.pushback);
          }

          if (damageCooldown <= 0) {
            hull_ = damageHull(hull_, impact.damage, struckLines(away));
            integrity = hullIntegrity(hull_, blueprint);
            if (impact.severity === 'destroyed') {
              integrity = 0;
              velocity.set(0, 0, 0);
            }
            hullDirty = true;
            settle();

            lastImpact = {
              body: struck.kind === 'deck' ? 'station deck' : 'station fitting',
              severity: impact.severity,
              speed: velocity.length() || impact.pushback,
              at: ++impactSeq,
            };
            damageCooldown = DAMAGE_IFRAMES;
          }

          throttle = 0;
          warping = false;
          cruising = null;
        }
      }

      // Drawn where it has got to since the rebase — a few units at most, which
      // is the whole point.
      hullGroup.position.copy(shipAt).sub(origin);

      /*
       * And everybody else, against the same origin, in the same frame.
       *
       * `peerClock` rather than the scene's `THREE.Clock`: that one starts when
       * the scene is built and so restarts on every jump, while the feed's
       * timestamps are written elsewhere and do not. Interpolating one against
       * the other would put every peer's sample hours in the future.
       *
       * Read through the ref so a room arriving mid-flight is picked up without
       * rebuilding the scene.
       */
      /*
       * Fire the thrusters that are actually running.
       *
       * Only the set in use emits: precision burns the small nozzles and main
       * burns the rest, the same rule the flight model already applies to
       * thrust. A ship showing plumes from engines it is not using would be
       * lying about what it is doing.
       *
       * The colour says what the *drive* is doing rather than how fast the ship
       * is going — thrust, warp spun up, or the crossing itself.
       */
      plume.setColors(
        crossing.current.phase === 'tunnel'
          ? TUNNEL_FLAME
          : warping
            ? WARP_FLAME
            : THRUST_FLAME,
      );

      // Backwards in the world: the hull's own −Y, turned the way it faces.
      nozzleBack.set(0, -1, 0).applyQuaternion(hullQuaternion);

      /*
       * Throttle open *and* actually going somewhere.
       *
       * Throttle alone was wrong in both directions. Held against a mass lock, or
       * against a station's gravity locks, the drive burned a full plume while the
       * ship sat still — thrust with nothing to show for it. And backing off to
       * coast cut the plume dead at the instant of release, while the hull carried
       * on at speed, which reads as the engines having failed rather than as the
       * pilot having eased off.
       *
       * So it is the forward component of velocity, not the speed: reversing or
       * strafing across the nose is not this drive doing work, and a plume that
       * fires while sliding sideways points the wrong way about what is pushing.
       */
      const forwardSpeed = -velocity.dot(nozzleBack);

      /*
       * ── The crossing is the exception, and it has to be ────────────────────
       *
       * Inside the tunnel the ship is not in the system: the flight model stops
       * integrating, `velocity` is zero, and by the speed gate above the drive is
       * idling. It is of course doing the exact opposite — a crossing is the
       * hardest the engines ever work — so the one moment `TUNNEL_FLAME` exists
       * for was the one moment nothing came out of the nozzles.
       *
       * There is nothing to measure here, because the motion is not simulated.
       * So it is asserted instead: in the tunnel the drive is at full burn, and
       * the plume says so.
       */
      const crossingNow = inTunnel(crossing.current);
      const burn = crossingNow ? 1 : Math.min(1, throttle);

      if (crossingNow || (throttle > 0.01 && forwardSpeed > PLUME_MIN_SPEED)) {
        // The main drive is what crosses; the manoeuvring set has no part in it.
        const precision = !crossingNow && controls.current.set === 'precision';

        for (const part of hull_.components) {
          const small = part.type === 'thrusterSmall';
          const main = part.type === 'thruster' || part.type === 'thrusterLarge';
          if (precision ? !small : !main) continue;

          /*
           * Asked of the mesh, rather than worked out.
           *
           * The first version placed nozzles by rotating the component's own
           * coordinates and adding the hull's position, which assumed the ship
           * mesh sits exactly on its graph's origin. It does not — the geometry
           * is built around its own centre — so every plume came out offset
           * backwards by however far those two disagree.
           *
           * `localToWorld` asks the thing that was actually drawn where that
           * point ended up, which cannot disagree with itself.
           */
          nozzleAt.set(part.position.x, part.position.y, 0);
          ship.group.localToWorld(nozzleAt);

          /*
           * How big this thruster's plume is, relative to a main one.
           *
           * A small engine gets a shorter, tighter plume rather than a thinner
           * one: it is a smaller engine, not a quieter one.
           */
          const size = small ? 0.55 : part.type === 'thrusterLarge' ? 1.25 : 1;

          /*
           * Back to the mouth of the cone, by as much as this nozzle is long.
           *
           * A thruster's placement box is deliberately not its visible size, so
           * its origin sits inside the part rather than at the nozzle. Nothing
           * in the data says how far — it is a property of the model — so the
           * distance is a measured constant. See `NOZZLE_DEPTH`.
           *
           * Scaled with the part, because the models are the same shape at
           * different sizes: a fixed offset sits at the mouth of a main thruster,
           * a long way past the end of a small one, and inside the bell of a
           * large one. The plume detached from the little nozzles and buried
           * itself in the big ones.
           */
          nozzleAt.addScaledVector(nozzleBack, NOZZLE_DEPTH * size);

          /*
           * And out of the render frame, into the real one.
           *
           * `localToWorld` gives a *scene*-space point, and this view rebases
           * the scene onto the ship every frame — so a scene-space point is a
           * ship-space point, and a plume stored in one is welded to the hull.
           * Exhaust is left behind in the world, so it is stored in absolutes
           * like `shipAt` and like wreckage, and the origin goes back on here.
           */
          nozzleAt.add(origin);

          plume.emit(
            nozzleAt,
            nozzleBack,
            velocity,
            burn,
            step,
            size,
            // In the tunnel the exhaust moves because the ship cannot. See
            // `TUNNEL_EXHAUST`.
            crossingNow ? TUNNEL_EXHAUST : undefined,
          );
        }
      }

      plume.step(step, origin);

      others.update(live.current.peers ?? null, origin, peerClock(), padAt, anchorAt);

      // Who is parked where: which pads are spoken for, and what is solid.
      takenPads.clear();
      for (const peer of live.current.peers?.peers.values() ?? []) {
        if (peer.docked && peer.stationId) takenPads.add(`${peer.stationId}/${peer.padId}`);
      }
      readParked();

      /*
       * Wreckage, and sweeping it up.
       *
       * A collector is what makes a dropped part retrievable, and until now it
       * was the one thing on the parts list with no job. Read off the hull in
       * service rather than the blueprint: losing your collector to the same
       * compartment that threw the parts out is a real outcome and the right one.
       */
      const collecting = hull_.components.some((component) => component.type === 'collector');
      for (const type of wreckage.step(step, shipAt, origin, collecting)) {
        const stowed = stow(hold.current.cargo, partCargo(type), hold.current.capacity);
        // Silently left drifting when the hold is full, rather than destroyed —
        // it is still out there, and the hold is a thing you can go and empty.
        if (stowed) onCargoChange?.(stowed);
        else wreckage.drop(type, shipAt.clone(), velocity.clone().multiplyScalar(0.2));
      }
      /*
       * Heat, from the engines and from whatever is nearby.
       *
       * Only the hottest thing in reach counts rather than the sum of them: two
       * volcanic worlds either side of you is not twice a sun, and adding
       * sources would make a crowded system quietly lethal for reasons nothing
       * on screen explains. The engines add on top, which is the one interaction
       * worth having — burning hard past a star is worse than drifting past it.
       */
      let radiant = 0;
      for (const body of built.hotBodies) {
        trueOf(body.node, relative);
        radiant = Math.max(radiant, radiantHeat(relative.distanceTo(shipAt), body.radius, body.kind));
      }

      heat = stepHeat(heat, heatTarget({ throttle, set: controls.current.set, radiant }), step);

      /*
       * Cooking.
       *
       * Heat does not kill the pilot; it kills the ship, a little at a time, and
       * the ship dying is what kills the pilot. Applied to every line rather than
       * the side facing the star — there is nowhere on a hull sitting in a corona
       * that is not in it — and raw, so plating is worth something against the
       * one hazard you cannot fly around.
       *
       * Outside the impact immunity on purpose. That exists so a run of frames
       * in contact is billed once; this is not contact, it is an environment,
       * and ticking is the whole point of it.
       */
      const cooking = heatDamage(heat);
      if (cooking > 0 && integrity > 0) {
        hull_ = damageHull(hull_, cooking * step);
        integrity = hullIntegrity(hull_, blueprint);
        hullDirty = true;
        settle();
      }

      // ── The berth we are flying into ─────────────────────────────────────
      //
      // Checked against every pad rather than the one we launched from: docking
      // is arriving somewhere, and the berth you left is only special until you
      // have left it.
      let berthing: {
        berth: (typeof berths)[number];
        approach: DockApproach;
        /** World units from the hull, for choosing between two in range. */
        range: number;
      } | null = null;

      for (const [deck, candidates] of decks) {
        deck.getWorldPosition(deckPosition);
        if (deckPosition.distanceTo(hullGroup.position) > BERTH_SEARCH_RANGE) continue;


        // Into the deck's own frame, which handles the station's orbit, its
        // rotation and the scene's scale in one step.
        berthLocal.copy(hullGroup.position);
        deck.worldToLocal(berthLocal);

        for (const berth of candidates) {
          berthCentre.set(
            berth.local.x,
            berth.local.y,
            berth.local.z + berth.volume.centreHeight,
          );

          /*
           * Somebody is already on it.
           *
           * Skipped outright rather than offered and refused: a pad with a ship
           * on it is not a berth, and drawing the capture hologram over one
           * would invite an approach that could not end in a landing.
           */
          if (takenPads.has(`${berth.stationId}/${berth.padId}`)) continue;

          const approach = dockApproach(
            {
              x: berthLocal.x - berthCentre.x,
              y: berthLocal.y - berthCentre.y,
              z: berthLocal.z - berthCentre.z,
            },
            berth.volume,
            localExtent,
          );
          if (approach === 'clear') continue;

          // Measured in world units rather than in the deck's own frame, so two
          // stations with different scales are compared on the same footing.
          berthWorld.copy(berthCentre);
          deck.localToWorld(berthWorld);
          const range = berthWorld.distanceTo(hullGroup.position);

          /*
           * Captured beats entering, and after that it is simply the nearest.
           *
           * It used to be whichever pad the loop happened to reach first, which
           * is generator order — so on a station with several berths in range
           * the hologram would settle on one that was not the one under you,
           * and lining up moved you *away* from the box you were being shown.
           * Nearest is the only rule that matches what a pilot is doing.
           */
          const better =
            !berthing ||
            (approach === 'captured' && berthing.approach === 'entering') ||
            (approach === berthing.approach && range < berthing.range);

          if (better) berthing = { berth, approach, range };
        }
      }

      volumeGroup.visible = berthing !== null;
      if (berthing) {
        const { berth, approach } = berthing;

        berthCentre.set(berth.local.x, berth.local.y, berth.local.z + berth.volume.centreHeight);
        berth.deck.localToWorld(berthCentre);

        volumeGroup.position.copy(berthCentre);
        berth.deck.getWorldQuaternion(volumeGroup.quaternion);
        volumeGroup.scale.set(
          berth.volume.half.x * 2 * GAME_SCALE,
          berth.volume.half.y * 2 * GAME_SCALE,
          berth.volume.half.z * 2 * GAME_SCALE,
        );

        const colour = APPROACH_COLOR[approach === 'captured' ? 'captured' : 'entering'];
        volumeFaces.color.setHex(colour);
        volumeEdges.color.setHex(colour);
      }

      /*
       * ── Camera ───────────────────────────────────────────────────────────
       *
       * The head hangs off the hull rather than being built from the same
       * angles. It used to add its offset into a yaw and a pitch and rebuild the
       * whole rotation, which cannot be done any more — a free orientation has
       * no yaw and pitch to add to — and hanging it off the hull is what it
       * always meant anyway: looking around is looking around *from the seat*.
       *
       * Which also fixes something the old form got wrong. Free look was
       * world-referenced, so a rolled ship looked "up" out of the top of the
       * screen rather than up over its own canopy.
       */
      const looking = quatMultiply(
        {
          x: hullQuaternion.x,
          y: hullQuaternion.y,
          z: hullQuaternion.z,
          w: hullQuaternion.w,
        },
        quatMultiply(
          quatFromAxisAngle(DECK, headOffset.yaw),
          quatFromAxisAngle(STARBOARD, headOffset.pitch),
        ),
      );
      const headQuaternion = new THREE.Quaternion(looking.x, looking.y, looking.z, looking.w);
      const facing = new THREE.Vector3(0, 1, 0).applyQuaternion(headQuaternion);

      const seatLocal = seat
        ? new THREE.Vector3(
            seat.position.x,
            seat.position.y,
            -SHIP_DEPTH / 2 + boundsOf('pilotSeat').up.min + EYE_HEIGHT * drawScaleOf('pilotSeat'),
          )
        : new THREE.Vector3();

      const first = controls.current.mode === 'first';

      const deployed = controls.current.gear;
      ship.group.visible = !deployed;
      shipLanded.group.visible = deployed;

      // On the console below the eyeline, canted back to face a pilot looking
      // down at it. Deliberately steeper than half the field of view, so it is
      // off the bottom of the screen while you are flying and you have to
      // actually look down — which is the whole point of it being an object in
      // the cockpit rather than a panel pinned over the sky.
      const reach = drawScaleOf('pilotSeat');
      card.mesh.visible = first;
      card.mesh.position.set(
        seatLocal.x,
        seatLocal.y + CARD_AHEAD * reach,
        seatLocal.z - CARD_BELOW * reach,
      );
      card.mesh.rotation.set(CARD_TILT, 0, 0);
      // From a seat the hull is around you, so its skin is what stops you
      // seeing anything. Only the outward plating goes.
      ship.setSkinVisible(!first);
      shipLanded.setSkinVisible(!first);

      if (first) {
        eye.copy(seatLocal).applyQuaternion(hullQuaternion).add(hullGroup.position);
        camera.position.copy(eye);
        camera.up.set(0, 0, 1).applyQuaternion(headQuaternion);
        camera.lookAt(eye.clone().add(facing));
      } else {
        pivot.copy(seatLocal);
        pivot.z = SHIP_DEPTH / 2 + CHASE_LIFT * drawScaleOf('pilotSeat');
        pivot.applyQuaternion(hullQuaternion).add(hullGroup.position);

        camera.position.copy(pivot).addScaledVector(facing, -CHASE_DISTANCE * SHIP_DEPTH);
        camera.up.set(0, 0, 1).applyQuaternion(headQuaternion);
        camera.lookAt(pivot);
      }

      /*
       * The shell, and how dark it is.
       *
       * Two sources, and they cannot both apply: `crossingVeil` owns the way in
       * and the arrival owns the way out.
       *
       * The way in is the tunnel's own clock, on the same `JUMP_SWAP_AT` the
       * streaks and gas wind up on — because they are one event. It used to
       * track *speed* through the run-up, which made entering a jump a gradual
       * dimming as the drive built up. A jump is not a dimming; it is the
       * moment everything changes at once, and nothing should start before it.
       *
       * The way out clears over the first *third* of the run in, and that has
       * to lead the streaks rather than follow them. The streaks now let go in
       * a second; a shell still dark at that point would leave a beat of plain
       * black with the arrival happening behind it, which is the one thing the
       * whole sequence exists to avoid.
       */
      shell.position.copy(camera.position);

      const shellOpacity =
        approach && !inTunnel(crossing.current)
          ? Math.max(0, (approach.left / ARRIVAL_GLIDE - 0.7) / 0.3)
          : crossingVeil(crossing.current);

      shell.visible = shellOpacity > 0.001;
      (shell.material as THREE.MeshBasicMaterial).opacity = shellOpacity;

      // ── The reticle ────────────────────────────────────────────────────────
      // Drawn where the *aim* points, not the nose, so the control reads as
      // immediate while the ship visibly works to catch up.
      const marker = reticleRef.current;
      if (marker) {
        const aimPoint = new THREE.Vector3(0, 1, 0)
          .applyQuaternion(new THREE.Quaternion(aim.x, aim.y, aim.z, aim.w))
          .multiplyScalar(SHIP_DEPTH * 40)
          .add(hullGroup.position)
          .project(camera);

        const onScreen = aimPoint.z < 1;
        marker.style.display = onScreen ? '' : 'none';
        marker.style.left = `${(aimPoint.x * 0.5 + 0.5) * 100}%`;
        marker.style.top = `${(-aimPoint.y * 0.5 + 0.5) * 100}%`;
      }

      /*
       * Where the nose actually is.
       *
       * Projected from the *hull* rather than pinned to the middle of the
       * screen. Those are the same point only while the camera is looking
       * straight ahead — so under free look the marker used to stay welded to
       * the centre of the view and claim the ship was pointing wherever the
       * player happened to be looking, which is the one thing it exists to
       * contradict.
       *
       * Straight off the hull's own quaternion, so it cannot drift from the
       * mesh: roll turns about this axis and therefore does not move it.
       */
      // Held as a direction as well as drawn, because the jump markers measure
      // against it — see `nose` in `JumpMarkerUpdate`.
      noseAxis.set(0, 1, 0).applyQuaternion(hullGroup.quaternion);

      const nose = noseRef.current;
      if (nose) {
        const nosePoint = nosePlace
          .copy(noseAxis)
          .multiplyScalar(SHIP_DEPTH * 40)
          .add(hullGroup.position)
          .project(camera);

        const ahead = nosePoint.z < 1;
        nose.style.display = ahead ? '' : 'none';
        nose.style.left = `${(nosePoint.x * 0.5 + 0.5) * 100}%`;
        nose.style.top = `${(-nosePoint.y * 0.5 + 0.5) * 100}%`;
      }

      const pointer = cursorRef.current;
      if (pointer) {
        pointer.style.display = cursor.active ? '' : 'none';
        pointer.style.left = `${cursor.x}px`;
        pointer.style.top = `${cursor.y}px`;
      }

      background.follow(camera.position);
      sky.follow(camera.position);
      // After the camera is where it is going to be for this frame: the streaks
      // are hung in front of it, so a stale camera puts them behind you.
      streaks.step(step, camera, velocity.length(), inTunnel(crossing.current));
      renderer.render(scene, camera);

      // Named after the render, so the brackets are placed against the frame
      // that was actually drawn rather than the previous one.
      //
      // The pivot is the middle of the screen, so the bracket that fills is the
      // body you are looking straight at — and that is the same body cruise
      // would fly to, which is the point.
      // Markers off from the lock onward: the sky is streaks by then, and a
      // bracket naming a moon in a system you are leaving is drawing attention
      // to the one thing that no longer matters.
      const committed = !pilotHasControl(crossing.current);
      labelLayer.style.display = committed ? 'none' : '';

      aimedJump = jumpMarkers.update({
        camera,
        width,
        height,
        nose: noseAxis,
        confirming:
          crossing.current.phase === 'confirming' && crossing.current.systemId
            ? {
                systemId: crossing.current.systemId,
                progress: confirmProgress(crossing.current),
              }
            : null,
      });

      markers.update({
        camera,
        width,
        height,
        from: hullGroup.position,
        speed: Math.abs(along),
        locked: cruising,
        pivot: { x: width / 2, y: height / 2, within: AIM_SNAP_PIXELS },
        // The same spheres collision tests against, so a bracket cannot claim
        // you can see something the hull would run into on the way to it.
        occluders: built.spheres,
        routed: routedBody.current,
      });

      // What the reticle is on, for the next frame's cruise button. Measured
      // the same way the markers are, so the two can never disagree.
      aimedAt = null;
      let aimedDistance = AIM_SNAP_PIXELS;

      for (const focus of built.focusables) {
        focus.node.getWorldPosition(relative);
        projected.copy(relative).project(camera);
        if (projected.z > 1) continue;

        const offset = Math.hypot(
          (projected.x * 0.5 + 0.5) * width - width / 2,
          (-projected.y * 0.5 + 0.5) * height - height / 2,
        );
        if (offset >= aimedDistance) continue;
        aimedDistance = offset;
        aimedAt = focus.label;
      }

      // ── Where we are, for whoever asks next ────────────────────────────────
      //
      // Written every frame rather than on unmount: the view can go away with
      // the tab, and a position that only survives a tidy exit is not a saved
      // position.
      // Only what has hold of us. A ship that is merely *near* something is
      // still flying free, and pinning its saved position to a body it is not
      // locked to would have the world tow it about while you read a chart.
      const anchorNode = lockedOn;

      // True coordinates. With an anchor the difference is the same in either
      // frame, but without one the scene-space position is *the origin* — so
      // saving that would have parked every unlocked ship at the star.
      relative.set(0, 0, 0);
      if (anchorNode) trueOf(anchorNode.node, relative);

      state.current = {
        systemId,
        anchor: anchorNode?.label ?? null,
        offset: shipAt.clone().sub(relative).toArray() as Triple,
        velocity: velocity.toArray() as Triple,
        orientation: {
          x: heading.orientation.x,
          y: heading.orientation.y,
          z: heading.orientation.z,
          w: heading.orientation.w,
        },
        throttle,
        heat,
        warping,
        set: controls.current.set,
        gear: controls.current.gear,
      };

      // ── The introduction ──────────────────────────────────────────────────
      //
      // Every condition it turns on is already computed above, so this is a
      // lookup rather than a second simulation. Distance is measured to the
      // berth's own station: the nearest body would have Control talking about
      // whatever moon happened to be closest.
      if (tutorialStep.current && !tutorialIsOver(tutorialStep.current)) {
        const home = station
      ? built.focusables.find((focus) => focus.label === station.name)
      : null;
        const range = home
          ? Math.max(0, trueOf(home.node, relative).distanceTo(shipAt) - home.radius)
          : Infinity;

        const next = advanceTutorial(tutorialStep.current, {
          distance: range,
          speed: Math.abs(along),
          massLocked: (lockedOn?.strength ?? 0) > 0.05,
          precision: controls.current.set === 'precision',
          gearDown: controls.current.gear || onItsBelly(),
          docked: false,
          reply: lastReply.current,

          /*
           * The run out, reported from the same frame that decides it.
           *
           * Every one of these is something the loop already computes to fly the
           * ship — the module switch, what the reticle is over, whether the
           * drive is lit — so the introduction is reading the cockpit rather
           * than keeping a second idea of what is happening.
           */
          carryingJob: cargo.some(
            (item) => item.mission !== undefined && isTutorialMission(item.mission),
          ),
          jumpUp: controls.current.jump && jumpGate.engaged,
          // The same `aimed` the jump sequence itself takes, so being told to
          // aim and being able to commit are the same condition rather than two
          // that can disagree.
          jumpAimed: aimedJump !== null,
          warping,
          // Still carrying it, and standing in the system it is addressed to.
          atDestination: cargo.some(
            (item) =>
              item.mission !== undefined &&
              isTutorialMission(item.mission) &&
              item.mission.destination.systemId === systemId,
          ),
        });

        // Consumed either way: an answer is for the question that was asked,
        // and leaving it set would have it satisfy the next prompt too.
        lastReply.current = null;

        if (next !== tutorialStep.current) {
          tutorialStep.current = next;

          /*
           * Said from here rather than left to the prop, because giving up is
           * retired upstream the instant it is reported: the account is marked
           * onboarded and the step is cleared, so this view never re-renders
           * holding `abandoned` and the effect that normally speaks a beat
           * never sees it. Control would simply go quiet, which is the one
           * thing a sign-off must not do.
           */
          if (next === 'abandoned') say.current('abandoned');

          onTutorialStep?.(next);
        }
      }

      underWarp.current = warping;

      /*
       * The crossing.
       *
       * ── Every frame, and that is not incidental ────────────────────────────
       *
       * This sat below the gauge gate, which returns early on five frames out
       * of six. It still ran with a *frame's* `step`, so its clocks advanced at
       * a sixth of real time: the five-second commit took thirty seconds and
       * the fifteen-second crossing took ninety. Anything integrating `step`
       * has to be above that gate — the gate is for how often the HUD is
       * re-rendered, not for how fast time passes.
       *
       * `armed` is both switches, checked every frame rather than only at the
       * start: letting the drive drop out mid-hold should abandon the hold, and
       * it does, because the rule is a condition rather than an event.
       */
      {
        const before = crossing.current;

        crossing.current = stepJump(before, {
          step,
          aimed: aimedJump,
          confirming: down('dock'),
          armed: controls.current.jump && jumpGate.engaged && warping,
          speed: Math.abs(along),
          loaded: live.current.jumpReady,
          // Leaving the cluster is the long crossing. Read off the target list
          // rather than tracked separately, so it cannot disagree with what the
          // marker said when it was chosen.
          crossingCluster: crossing.current.systemId
            ? !(jumpable.find((t) => t.systemId === crossing.current.systemId)?.sameCluster ?? true)
            : false,
        });

        // Reported on change only. The caller starts generating the destination
        // when the tunnel opens and performs the move when it closes, and both
        // are things that must happen exactly once.
        if (before.phase !== crossing.current.phase) {
          live.current.onJumpPhase?.(
            crossing.current.phase,
            crossing.current.systemId ?? before.systemId,
          );

          /*
           * The run's own starting line.
           *
           * Caught on the transition rather than read later, because by the next
           * frame the ship is already faster and the figure every percentage is
           * measured against would drift with it.
           */
          if (crossing.current.phase === 'running') {
            runStartSpeed = Math.abs(along);
            calibrationAt = 0;
            calibrationSaid = null;
          } else if (before.phase === 'running') {
            runStartSpeed = null;
            calibrationSaid = null;
          }
        }

        // The view is dark; change the world behind it. Reported once, on the
        // frame it becomes true, because the caller acts on it exactly once.
        if (!readyToSwap(before) && readyToSwap(crossing.current) && crossing.current.systemId) {
          live.current.onJumpSwap?.(crossing.current.systemId);
        }
      }

      /*
       * The module reports once a second, and the same words go to both places.
       *
       * Recomputed on a tick rather than per frame so the console does not
       * flicker through percentages faster than anybody can read one — and held
       * in a variable so the caption and the log are literally the same string
       * rather than two calls that could disagree about when the second elapsed.
       */
      {
        // `window.`, because `performance` in this scope is the *ship's* — the
        // parts list and its thrust figures. Two very different clocks.
        const now_ = window.performance.now();
        if (crossing.current.phase === 'running') {
          if (now_ - calibrationAt >= CALIBRATION_TICK) {
            calibrationAt = now_;
            calibrationSaid = jumpCalibration(crossing.current, runStartSpeed, along);
          }
        } else {
          calibrationSaid = null;
        }
      }

      // ── Gauges ─────────────────────────────────────────────────────────────
      // Ten times a second, not sixty: these are numbers you glance at, and
      // re-rendering the HUD every frame would cost more than it shows.
      publish += step;
      if (publish < 0.1) return;
      publish = 0;

      /*
       * Hand the hull up, at most ten times a second.
       *
       * `onDamage` is a React state update that re-renders everything above this
       * view. Collisions are rare enough to have called it directly; heat is not
       * — it ticks every frame, and a hull cooking in a corona would have fired
       * sixty state updates a second. Same reason the gauges are published here
       * rather than per frame.
       */
      if (hullDirty) {
        hullDirty = false;
        onDamage(hull_);
      }

      /*
       * Tell the room where we are, on the same beat as the gauges.
       *
       * `shipAt` is already absolute — it is the thing scene space is shifted
       * *by* — so this is a copy and no transform. Velocity rides along so a
       * receiver can carry the ship between updates rather than stepping it
       * six frames at a time.
       *
       * Sent while docked-adjacent, sent while stationary, sent every beat: the
       * cost is a handful of numbers, and the alternative is a ship that stops
       * existing for anyone who joins while its owner is sitting still.
       */
      /*
       * Measured from whatever has hold of the ship.
       *
       * A mass-locked ship inherits the body's motion, so beside an orbiting
       * station it is travelling at orbital speed in absolute terms. Sending
       * that as an absolute point meant ten samples a second had to carry the
       * orbit, while every receiver's own scene moved the same station sixty
       * times a second -- so the ship was drawn against a body that had already
       * moved on, and shook at the beat between the two rates.
       *
       * The offset is small, changes slowly, and interpolates cleanly. The orbit
       * is resolved by the receiver, at frame rate, from the body it already
       * has.
       */
      const anchor = lockedOn?.label ?? '';
      if (anchor) trueOf(lockedOn!.node, poseFrame);
      else poseFrame.set(0, 0, 0);

      live.current.onPose?.({
        anchor,
        x: shipAt.x - poseFrame.x,
        y: shipAt.y - poseFrame.y,
        z: shipAt.z - poseFrame.z,
        qx: hullQuaternion.x,
        qy: hullQuaternion.y,
        qz: hullQuaternion.z,
        qw: hullQuaternion.w,
        vx: velocity.x,
        vy: velocity.y,
        vz: velocity.z,
        // Only the tunnel is out of the game. A ship confirming or running a
        // jump is still in the system, still visible and still hittable — it is
        // the crossing itself that removes it, not the decision to make one.
        hidden: crossing.current.phase === 'tunnel',
      });

      inverse.copy(hullQuaternion).invert();

      /**
       * Where we are going, as the console needs it.
       *
       * Three cases, in the order they take precedence:
       *
       *   1. a body pinned in *this* system — point at the body;
       *   2. a route that leaves — point at the next jump's bearing on the sky;
       *   3. nothing pinned — name the system and point at its star, which is
       *      the one landmark every system is guaranteed to have and a far
       *      better answer than an arrow that means nothing.
       */
      const readNav = (): NavReadout => {
        // Where the ship is, on every branch below. Read once here rather than
        // repeated into four return statements, one of which would eventually
        // be missed.
        const here = { system: system.name, cluster: clusterName };

        // Into the hull's own frame, then the bearing on its deck plane. Zero
        // is dead ahead because forward is +Y — the same convention the aim and
        // the jump bearings already use.
        const toShip = (direction: THREE.Vector3): number => {
          const local = direction.clone().applyQuaternion(inverse);
          return Math.atan2(local.x, local.y);
        };

        const pinned = routedBody.current;
        if (pinned) {
          const body = built.focusables.find((focus) => focus.label === pinned);
          if (body) {
            trueOf(body.node, relative);
            return {
              ...here,
              target: pinned,
              heading: pinned,
              bearing: toShip(relative.sub(shipAt).normalize()),
              jump: false,
            };
          }
        }

        const hop = routeHopRef.current;
        if (hop) {
          return {
            ...here,
            target: routeEnd.current ?? hop.name,
            heading: hop.name,
            bearing: toShip(
              new THREE.Vector3(hop.direction.x, hop.direction.y, hop.direction.z),
            ),
            jump: true,
          };
        }

        const star = built.focusables.find((focus) => focus.label === system.name);
        if (!star) return { ...here, target: null, heading: system.name, bearing: null, jump: false };

        trueOf(star.node, relative);
        return {
          ...here,
          target: null,
          heading: system.name,
          bearing: toShip(relative.sub(shipAt).normalize()),
          jump: false,
        };
      };

      const contacts: Contact[] = [];

      /** Something at a point in scene space, put on the scanner. */
      const addAt = (id: string, at: THREE.Vector3, kind: Contact['kind']) => {
        relative.copy(at).sub(hullGroup.position);
        if (relative.length() > SCANNER_RANGE) return;

        relative.applyQuaternion(inverse).divideScalar(SCANNER_RANGE);
        contacts.push({ id, x: relative.x, y: relative.y, z: relative.z, kind });
      };

      const addContact = (id: string, node: THREE.Object3D, kind: Contact['kind']) =>
        addAt(id, node.getWorldPosition(contactAt), kind);

      for (const focus of built.focusables) {
        addContact(
          focus.label,
          focus.node,
          focus.radius > 400 ? 'planet' : focus.radius > 60 ? 'station' : 'anomaly',
        );
      }

      /*
       * Other pilots.
       *
       * Read from what is actually *drawn* rather than from the feed, so a ship
       * the scanner shows is a ship you can see — a peer this client cannot
       * place is not a contact it should be inventing a position for.
       *
       * Docked ships are not tracked. A hull sitting on a plate is part of the
       * station as far as the scanner is concerned, and a berth full of parked
       * traffic would bury the one contact that is actually moving.
       */
      for (const ship of others.flying()) {
        addAt(ship.callsign || ship.id, ship.at, 'ship');
      }


      /*
       * ── The ship's own account of itself ──────────────────────────────────
       *
       * Written here, on the gauge beat, rather than at each event's own site:
       * everything worth reporting is already gathered at this point, and
       * scattering `note` calls through the loop would put a UI concern inside
       * the collision resolver.
       *
       * Every call runs ten times a second and almost all of them do nothing —
       * `note` writes only when a channel's *state* changes. So the second
       * argument must never carry a distance or a speed; those change constantly
       * and would make every line an event. See `shipLog`.
       */
      {
        const held = lockedOn && lockedOn.strength > 0.05 ? lockedOn.label : null;
        note('lock', held ?? '', held ? `Mass lock acquired: ${held}.` : 'Mass lock released.');

        /*
         * Docking, said before it is needed rather than after it has failed.
         *
         * Landing gear is the one requirement a player cannot discover by
         * trying: an approach without it simply never captures, with nothing to
         * say why. Being held by a station is the moment the question becomes
         * live, so that is when it is answered.
         *
         * Keyed on the station *and* whether the gear is already down, so
         * lowering it while being reminded acknowledges the reminder rather than
         * leaving it standing.
         */
        const atStation = lockedOn && lockedOn.strength > 0.05 && lockedOn.kind === 'station';
        note(
          'docking',
          atStation ? `${lockedOn!.label}:${controls.current.gear ? 'ready' : 'stowed'}` : '',
          atStation
            ? controls.current.gear
              ? `Gear down. Cleared to approach ${lockedOn!.label}.`
              : `${lockedOn!.label} in range. Engage landing gear before docking.`
            : '',
        );

        // ── Switches ──────────────────────────────────────────────────────
        // Every one of them, because a switch is the one kind of event a player
        // caused deliberately and may not have seen take effect.
        note(
          'gear',
          controls.current.gear ? 'down' : 'up',
          controls.current.gear ? 'Landing gear: engaged' : 'Landing gear: disengaged',
        );

        /*
         * Both positions of the switch, and both lead back to the same card.
         *
         * A thruster set is not a thing that is on or off — it is a choice
         * between two ways the ship flies, and coming back to the main drive is
         * as much a decision as reaching for precision. One line each, from the
         * lesson so they cannot drift from what the card says.
         */
        note(
          'set',
          controls.current.set,
          controls.current.set === 'precision'
            ? LESSONS['thrusters']!.brief
            : LESSONS['thrusters']!.briefOff!,
          { lesson: 'thrusters' },
        );

        /*
         * Taught on the switch *to* precision, not on any change.
         *
         * The main drive is where every ship starts, so keying on the set at all
         * would fire the moment a scene is built and teach a switch nobody
         * threw. Reaching for the manoeuvring set is a deliberate act and the
         * one moment the difference between the two is worth explaining.
         */
        feature('thrusters', controls.current.set === 'precision' ? 'on' : '');

        /*
         * The module, and the quiet half of its lesson in the same line.
         *
         * The reminder replaces "Jump module: active" rather than joining it:
         * two lines saying a thing is on is one line and a repetition, and the
         * lesson's own sentence already says which it is. Read out of `LESSONS`
         * so the loud version and this one cannot drift apart.
         */
        // Both positions again, and both readable — somebody who stowed the
        // module by accident is exactly who wants to know what it was for.
        note(
          'jumpmodule',
          controls.current.jump ? 'on' : 'off',
          controls.current.jump
            ? LESSONS['jump-targets']!.brief
            : LESSONS['jump-targets']!.briefOff!,
          { lesson: 'jump-targets' },
        );
        feature('jump-targets', controls.current.jump ? 'on' : '');

        note(
          'cruise',
          cruising ?? '',
          cruising ? `Cruise engaged for ${cruising}.` : 'Cruise disengaged.',
        );

        note('warp', warping ? 'on' : '', warping ? 'Warp drive online' : 'Warp drive idle');

        // Phase rather than the whole crossing: `elapsed` moves every frame.
        note(
          'crossing',
          crossing.current.phase,
          crossing.current.phase === 'tunnel'
            ? 'In transit. Systems nominal'
            : crossing.current.phase === 'running'
              ? 'Jump sequence started'
              : '',
        );

        /*
         * The calibration, said as well as shown — the same string, so the switch
         * and the log cannot disagree.
         *
         * Keyed on the message itself, which already carries the percentage, so
         * each tick lands exactly once and a repeated figure says nothing.
         */
        note(
          'jumpcal',
          calibrationSaid ?? '',
          calibrationSaid ? `jmp_mod: ${calibrationSaid}.` : '',
        );

        /*
         * And the handover, which is the *autopilot* speaking rather than the
         * module. The module calibrates itself; shutting the ship down for the
         * crossing is a decision about every system aboard, so it is said once,
         * by the thing that owns them.
         */
        note(
          'shutdown',
          crossing.current.phase === 'running' && !systemsPowered(crossing.current, along)
            ? 'ready'
            : '',
          crossing.current.phase === 'running' && !systemsPowered(crossing.current, along)
            ? 'Ready for system shutdown for jump.'
            : '',
        );

        /*
         * Damage, keyed on the strike rather than on the state.
         *
         * `impactSeq` counts hits, so every collision is a new state and each one
         * gets a line — which is right here and wrong everywhere else above.
         */
        if (lastImpact) {
          note(
            'impact',
            String(lastImpact.at),
            lastImpact.severity === 'destroyed'
              ? `Hull failure — ${lastImpact.body}.`
              : `Impact: ${lastImpact.body}.`,
          );

          /*
           * Rocks, which are the one thing out here that is solid and does not
           * look it.
           *
           * The reminder is on its own channel rather than on the strike, so it
           * lands once per system rather than once per hit — a player bouncing
           * through a belt is being told something they have just been shown,
           * and forty times is a fire hose. The lesson itself is per account and
           * fires on the first rock either way.
           */
          if (lastImpact.body === 'asteroid') {
            note('rocks', 'hit', LESSONS['asteroid']!.brief, { lesson: 'asteroid' });
            feature('asteroid', 'hit');
          }
        }

        // ── Advice ────────────────────────────────────────────────────────
        // Unprompted, and only when it is actionable. A console that comments on
        // everything is one nobody reads.
        const load = cargoWeight(cargo);
        note(
          'hold',
          load > capacity ? 'over' : load > capacity * 0.9 ? 'nearly' : '',
          load > capacity
            ? 'Hold over capacity. Manoeuvring compromised.'
            : load > capacity * 0.9
              ? 'Hold nearly full.'
              : '',
        );

        // Two bands, and the lower one carries the lesson's own sentence: the
        // first band is where it is still advice, and the second is where the
        // only useful thing to say is shorter.
        note(
          'heat',
          heat > 0.85 ? 'critical' : heat > 0.6 ? 'high' : '',
          heat > 0.85
            ? 'Hull temperature critical. Recommend withdrawing.'
            : heat > 0.6
              ? LESSONS['heat']!.brief
              : '',
          // Both bands lead back to the same card: somebody reading the critical
          // line is *more* likely to want the explanation, not less.
          heat > 0.6 ? { lesson: 'heat' } : {},
        );
        feature('heat', heat > 0.6 ? 'hot' : '');
      }

      setCruise(cruising);
      setGauges({
        speed: along,
        maxSpeed: handling.maxSpeed,
        throttle,
        heat,
        contacts,
        massLock: lockedOn && lockedOn.strength > 0.05 ? lockedOn.label : null,
        jumpCalibration: calibrationSaid,
        warp: warping,
        warpBlocked: warping ? null : engage.blocked,
        jumpBlocked: jumpGate.blocked,
        crossing: crossing.current,
        /*
         * Down from the moment a locked run passes the blackout speed until the
         * ship has actually stopped on the far side.
         *
         * Held through the run in from the system edge as well, because coming
         * back up while still moving would put the console on before the ship
         * had arrived — and the whole point is that the reconfiguration happens
         * out of sight.
         */
        powered: systemsPowered(crossing.current, along) && !approach,
        jumpAim: aimedJump
          ? (jumpable.find((target) => target.systemId === aimedJump)?.name ?? null)
          : null,
        nav: readNav(),
        target: cruising ?? (cruiseGate.engaged ? aimedAt : null),
        cruiseBlocked: cruising ? null : cruiseGate.blocked,
        cruising: cruising !== null,
        integrity,
        collision: lastImpact,
        local: closest
          ? {
              name: closest.label,
              kind: closest.kind,
              detail: detail.get(closest.label) ?? '',
              distance: Math.max(0, closest.surface),
              locked: lockedOn?.label === closest.label,
            }
          : null,
        dock:
          berthing && berthing.approach !== 'clear'
            ? {
                stationId: berthing.berth.stationId,
                stationName: berthing.berth.stationName,
                padId: berthing.berth.padId,
                approach: berthing.approach,
                // Charged for taking a bigger berth than the hull needs. A big
                // pad is a scarce thing a bigger ship wants; using one for a
                // shuttle should cost something without ever being refused.
                fee: dockingFee(
                  berthing.berth.size,
                  smallestBerthFor(
                    shipExtent,
                    (size) => PAD_SIZES[size] * STATION_SCALE * GAME_SCALE,
                  ),
                ),
                /*
                 * How this would touch down, asked of the gear as well as of
                 * the hull.
                 *
                 * This used to be `belly: onItsBelly()` -- whether the ship had
                 * any legs -- and nothing anywhere asked whether they were
                 * down. A ship with a full set could dock with them stowed, for
                 * nothing, and be told it had docked properly.
                 */
                how: setDown(hull_.components.map((c) => c.type), controls.current.gear),
                belly: onItsBelly(),
              }
            : null,
      });
    };
    tick();

    return () => {
      releaseControls.current = null;
      plume.dispose();
      streaks.dispose();
      others.dispose();
      jumpMarkers.dispose();
      cancelAnimationFrame(frame);
      /*
       * Remember whether we had the mouse, so the rebuild can take it back.
       *
       * A jump swaps the system, which tears down this whole scene including
       * its canvas — and pointer lock belongs to a canvas, so it goes with it.
       * The player had done nothing to give it up, and being dumped back to
       * "click to take the controls" on arrival is the game asking permission
       * for something it interrupted.
       */
      if (document.pointerLockElement === renderer.domElement) {
        hadPointer.current = true;
        document.exitPointerLock();
      }
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      renderer.domElement.removeEventListener('wheel', onWheel);
      renderer.domElement.removeEventListener('touchstart', onTouchStart);
      renderer.domElement.removeEventListener('touchmove', onTouchMove);
      renderer.domElement.removeEventListener('touchend', onTouchEnd);
      renderer.domElement.removeEventListener('touchcancel', onTouchEnd);
      renderer.domElement.removeEventListener('contextmenu', blockMenu);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onBlur);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('pointerlockchange', onLockChange);
      markers.dispose();
      shipLanded.dispose();
      volumeBox.dispose();
      volumeFaces.dispose();
      volumeEdges.dispose();
      shell.geometry.dispose();
      (shell.material as THREE.Material).dispose();
      card.dispose();
      sky.dispose();
      ship.dispose();
      wreckage.dispose();
      built.dispose();
      background.dispose();
      view.dispose();
    };
    // `design` is deliberately absent: it changes on every collision, and
    // rebuilding the whole scene mid-impact would tear down the ship that was
    // being hit. The mesh is built from the hull at mount and the damage model
    // lives inside the loop, so the two only need to meet again on a remount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed, systemId, stationId, padId, seat, performance, blueprint]);

  /**
   * `V` does the thing you want next.
   *
   * After looking around, what you reach for is "put me back", not "change
   * camera" — so while the view is off the nose it recentres, and only once it
   * is centred does it switch view. One key, two jobs, in the order they are
   * actually wanted.
   */
  /*
   * The berth `F` would take, or nothing.
   *
   * Centred over the plate is necessary and not sufficient: a ship that has gear
   * has to have lowered it. `mayDock` is the whole rule, and it lives in shared
   * next to the type it reads.
   */
  const dockable =
    gauges.dock?.approach === 'captured' && mayDock(gauges.dock.how) ? gauges.dock : null;

  /**
   * Whether a jump is there to be committed, over both halves of committing it.
   *
   * One condition rather than two because the touch button that holds it has to
   * outlive the change of phase — the hold is what causes that change, so a
   * control scoped to either side of it would vanish under the finger doing the
   * holding. `confirming` on its own is the tail of the same gesture.
   */
  const jumpHold =
    (Boolean(gauges.jumpAim) && gauges.crossing.phase === 'idle' && gauges.warp) ||
    gauges.crossing.phase === 'confirming';

  /** Where the console's top edge is, so the hold button sits level with the grid. */
  const consoleClearance = useConsoleClearance();

  /*
   * Released when the button goes, whether or not the finger did.
   *
   * Committing the jump is exactly what stops offering it: the phase moves on,
   * the button unmounts, and the pointer-up that would have released `dock`
   * lands on nothing. Without this the key stays down for the rest of the
   * session, and the first thing it reaches on the far side is the tap that sets
   * a ship on a pad.
   */
  useEffect(() => {
    if (!jumpHold) controls.current.touch.delete('dock');
  }, [jumpHold]);

  const onView = () => {
    if (controls.current.offCentre) {
      controls.current.recentre = true;
      return;
    }
    setMode((current) => (current === 'first' ? 'external' : 'first'));
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey) return;
      const key = event.key.toLowerCase();

      if (isAction('view', key)) onView();
      if (isAction('nextSeat', key)) {
        setSeatIndex((i) => (seats.length ? (i + 1) % seats.length : 0));
      }
      if (isAction('warp', key)) controls.current.toggleWarp = true;
      if (isAction('jump', key)) controls.current.toggleJump = true;
      if (isAction('landingGear', key)) lowerGear();
      if (isAction('cruise', key)) controls.current.toggleCruise = true;
      // F is two things: set down on a plate, or commit to a jump. They cannot
      // both be true — docking wants you stopped over a berth and a crossing
      // wants you at warp — but the key handler fires regardless of which the
      // ship is actually doing, so the drive settles it.
      if (isAction('dock', key) && dockable && !underWarp.current) {
        onDock({
          stationId: dockable.stationId,
          padId: dockable.padId,
          fee: dockable.fee,
          belly: dockable.belly,
        });
      }
      if (isAction('thrusterSet', key) && hasPrecision) {
        setSet((current) => (current === 'main' ? 'precision' : 'main'));
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [seats.length, hasPrecision, dockable, onDock]);

  return (
    <>
      {/* Underneath the window, not over it.

          The gas is what you are flying *through*, so the ship has to be in
          front of it — and it cannot be in the flight scene, because that scene
          is torn down mid-crossing and covering that is the whole reason it
          exists. So it stays its own canvas and goes below: the flight renderer
          clears transparent, and the crossing shell erases the system down to
          this rather than painting black over it.

          Mounted the whole time: it owns its own fade, and cutting it away on
          arrival is the one thing that would give the swap away. */}
      <WarpGas crossing={inTunnel(gauges.crossing)} />

      {/* `touchAction: none` so the browser never claims a gesture that starts
          here — see the note in `index.html`. It also stops the page rubber-
          banding under a drag that is meant to be turning the ship. */}
      <div ref={mountRef} style={{ position: 'absolute', inset: 0, touchAction: 'none' }} />
      <div
        ref={labelLayerRef}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: lit ? 1 : 0 }}
      />
      {/* Its own layer, so stowing the jump module is one call rather than a
          pass over every marker deciding which kind it is. */}
      <div
        ref={jumpLayerRef}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: lit ? 1 : 0 }}
      />

      {/* The hold, on a toggle. A delivery has a clock on it, and being unable
          to see that without landing made the deadline something you either
          remembered or lost. */}

      {/* Where you are asking the ship to point. */}
      {seats.length > 0 && (
        <div
          ref={reticleRef}
          style={{
            ...dark,
            position: 'absolute',
            width: 26,
            height: 26,
            marginLeft: -13,
            marginTop: -13,
            // Orange, like the cursor and for the same reason: the starfield,
            // the target brackets and the whole HUD are blues and greens, and a
            // pale blue reticle sat in the middle of all of it was the one thing
            // on screen you had to hunt for. The nose marker matches, because
            // the gap between the two *is* the handling and it only reads as a
            // gap if both ends are the same colour.
            border: '1px solid #ff8a3d',
            boxShadow: '0 0 5px rgba(255,138,61,0.5)',
            borderRadius: '50%',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* The stand-in pointer, for reaching a switch without dropping the lock. */}
      <div
        ref={cursorRef}
        style={{
          ...dark,
          position: 'absolute',
          display: 'none',
          width: 14,
          height: 14,
          marginLeft: -7,
          marginTop: -7,
          // Orange, because nothing else on screen is: the starfield and the
          // whole HUD are blues and greens, so a pale cursor disappeared into
          // them exactly when you were hunting for it.
          border: '2px solid #ff8a3d',
          borderRadius: '50%',
          background: 'rgba(255,138,61,0.35)',
          boxShadow: '0 0 6px rgba(255,138,61,0.9)',
          pointerEvents: 'none',
          /*
           * Above everything, including a lesson card.
           *
           * This is the pointer. It was at 10 and the card is at 40, so reaching
           * for the one button on a card meant aiming a cursor you could not see
           * — which is the exact failure the stand-in cursor exists to avoid.
           * Nothing should ever be drawn over it.
           */
          zIndex: 60,
        }}
      />

      {/* Where the nose actually is.
          A cross rather than a dot: the gap between this and the ring *is* the
          handling, and two shapes that differ in kind are easier to tell apart
          at a glance than two that differ only in size. Smaller than the ring,
          because this is the fact and the ring is the request. */}
      <div
        ref={noseRef}
        style={{
          ...dark,
          position: 'absolute',
          display: 'none',
          width: NOSE_CROSS,
          height: NOSE_CROSS,
          marginLeft: -NOSE_CROSS / 2,
          marginTop: -NOSE_CROSS / 2,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            height: 1,
            marginTop: -0.5,
            background: '#ff8a3d',
            boxShadow: '0 0 4px rgba(255,138,61,0.7)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '50%',
            width: 1,
            marginLeft: -0.5,
            background: '#ff8a3d',
            boxShadow: '0 0 4px rgba(255,138,61,0.7)',
          }}
        />
      </div>

      {/*
        Where you are, and what has hold of you.

        Only while something does. It used to name whatever happened to be
        nearest and report its range, which at flight speeds is a card of
        numbers changing constantly about a moon you are not going to — and
        during a crossing it was reporting a body in a system the ship had
        already left. Mass lock is the honest test: it is the one relationship
        with a body that is a fact about the ship rather than about the geometry
        of the moment, and it is already the thing the console has a lamp for.
      */}
      {/* `lit` and nothing else.
          This carried a `pilotHasControl` gate inherited from the body card it
          replaced — written before the blackout existed, when something had to
          hide the console during a crossing by hand. The blackout does that job
          now, and doing it twice meant the console vanished the instant the run
          began rather than fading with everything else. */}
      {/* The thumb grid, on devices flown with thumbs.
          Only while the instruments are lit: with the power out there is nothing
          for it to drive, and a live-looking control over a dead cockpit is a
          worse lie than no control at all. */}
      {lit && touchFlying && !suspended && (
        <TouchControls
          onHold={(action, down) => {
            const held = controls.current.touch;
            if (down) held.add(action);
            else held.delete(action);
          }}
        />
      )}

      {lit && (
        <ShipConsole
          power={dark}
          sections={consoleSections}
          onOpenLesson={(id) => live.current.onOpenLesson?.(id)}
        />
      )}

      {/* Kept out of the card above, which now comes and goes — a hull with no
          seat is a permanent condition and should be said permanently. */}
      {seats.length === 0 && (
        <div style={{ ...PANEL, top: 16, left: 16, width: 236, color: '#ffb347', fontSize: 11 }}>
          No pilot seat on this hull. Build one in the ship editor.
        </div>
      )}

      {/* Traffic. The keybinds that used to live here are on the console now —
          look down in the cockpit to read them. */}
      {lit && (
      <FlightRadio
        power={dark}
        messages={radio}
        onSend={(channel, text) => {
          // Anything typed is a candidate answer. The tutorial decides whether
          // it was the one it asked for; everything else is just chat.
          lastReply.current = text.trim().toUpperCase();

          /*
           * Said out loud if there is anybody to hear it.
           *
           * No local echo: the room broadcasts to the sender too, so echoing
           * here would print everything twice. Without a room there is nobody
           * listening and the line stays local, which is also what the tutorial
           * needs — it is a conversation with a station, not with the system.
           */
          if (onChat) {
            onChat(channel, text);
            return;
          }

          setRadio((log) => [
            ...log.slice(-60),
            { id: `${Date.now()}`, channel, from: callsign, text, own: true },
          ]);
        }}
      />
      )}

      {/* Somebody hailing you. Queued, transient, never blocking — see
          `RadioAlert`. */}
      {lit && (
        <RadioAlert
          power={dark}
          alerts={alerts}
          onDone={(id) => setAlerts((queue) => queue.filter((alert) => alert.id !== id))}
        />
      )}

      {/* The commitment, said in words as well as drawn on the sky — the dial
          out there is on the destination, which is not where your eyes are when
          you are holding a key down. */}
      {/* What to do about the thing under the reticle. The sky says where you
          could go; nothing said how, and a marker you have to guess at is a
          marker that does not work. */}
      {/*
        Holding the jump, for a hand with no keyboard.

        Deliberately outside both prompt panels rather than inside either. The
        aim panel is drawn while the crossing is `idle` and the progress panel
        while it is `confirming`, and the hold is exactly what moves it from one
        to the other — so a button living in the first would unmount underneath
        the finger holding it, taking its pointer capture with it and leaving
        `dock` stuck down in the control set with nothing left to release it.

        One element spanning both states, on the right, where the hand that is
        not holding the grid already is.
      */}
      {lit && touchFlying && jumpHold && !suspended && (
        <button
          type="button"
          onPointerDown={(event) => {
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            controls.current.touch.add('dock');
          }}
          onPointerUp={(event) => {
            event.preventDefault();
            controls.current.touch.delete('dock');
          }}
          onPointerCancel={() => controls.current.touch.delete('dock')}
          style={{
            position: 'absolute',
            right: 12,
            bottom: consoleClearance + 10,
            width: 92,
            height: 92,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            background: 'rgba(255,138,61,0.16)',
            border: `2px solid ${HUD_ORANGE}`,
            color: HUD_ORANGE,
            font: 'inherit',
            fontSize: 11,
            letterSpacing: 1,
            textTransform: 'uppercase',
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            pointerEvents: 'auto',
            zIndex: 30,
          }}
        >
          <span>hold</span>
          <span style={{ fontSize: 13 }}>jump</span>
        </button>
      )}

      {gauges.jumpAim && gauges.crossing.phase === 'idle' && (
        <div style={{ ...PANEL, bottom: 210, left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: HUD_ORANGE }}>{gauges.jumpAim}</div>

          {/* The drive is what crosses the gap; the module only says where to.
              Offering the key without it would be offering something the hold
              would refuse the moment it was taken up. */}
          <div style={{ fontSize: 10, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 1.4, marginTop: 3 }}>
            {gauges.warp
              ? SHOW_KEY_HINTS
                ? `hold ${keyLabel('dock')} to jump`
                : 'hold the jump button'
              : SHOW_KEY_HINTS
                ? `warp drive required (${keyLabel('warp')})`
                : 'warp drive required'}
          </div>
        </div>
      )}

      {gauges.crossing.phase === 'confirming' && (
        <div style={{ ...PANEL, bottom: 210, left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
          <div style={{ fontSize: 10, opacity: 0.55, textTransform: 'uppercase', letterSpacing: 1.4 }}>
            {SHOW_KEY_HINTS ? `hold ${keyLabel('dock')} to commit` : 'hold to commit'}
          </div>
          <div style={{ width: 180, height: 3, marginTop: 6, background: 'rgba(255,216,189,0.16)' }}>
            <div
              style={{
                height: '100%',
                width: `${confirmProgress(gauges.crossing) * 100}%`,
                background: HUD_ORANGE,
              }}
            />
          </div>
        </div>
      )}
      {lit && (
      <FlightHud
        power={dark}
        speed={gauges.speed}
        maxSpeed={gauges.maxSpeed}
        throttle={gauges.throttle}
        heat={gauges.heat}
        massLock={gauges.massLock !== null}
        integrity={gauges.integrity}
        cruise={gauges.cruising}
        cruiseTarget={gauges.target}
        cruiseBlocked={gauges.cruiseBlocked}
        onToggleCruise={() => {
          controls.current.toggleCruise = true;
        }}
        gear={gear}
        onToggleGear={lowerGear}
        warp={gauges.warp}
        warpBlocked={gauges.warpBlocked}
        onToggleWarp={() => {
          controls.current.toggleWarp = true;
        }}
        jump={jump}
        jumpBlocked={gauges.jumpBlocked}
        jumpStatus={gauges.jumpCalibration}
        jumpTargets={jumpable.length}
        onToggleJump={() => {
          controls.current.toggleJump = true;
        }}
        precision={set === 'precision'}
        hasPrecision={hasPrecision}
        onTogglePrecision={() => setSet(set === 'main' ? 'precision' : 'main')}
        contacts={gauges.contacts}
        external={mode === 'external'}
        onToggleView={onView}
        hold={{
          open: holdOpen,
          weight: cargoWeight(cargo),
          capacity,
          urgent: holdUrgent(cargo, clockTick),
        }}
        onToggleHold={() => {
          setHoldOpen((up) => !up);
          setHullOpen(false);
        }}
        structure={{ open: hullOpen, urgent: hullUrgent(design, blueprint) }}
        onToggleStructure={() => {
          setHullOpen((up) => !up);
          setHoldOpen(false);
        }}
        nav={gauges.nav}
        onOpenStarmap={onOpenStarmap}
        seatIndex={seatIndex}
        seatCount={seats.length}
        onNextSeat={() => setSeatIndex((i) => (seats.length ? (i + 1) % seats.length : 0))}
      />
      )}

      {/* Whether the window has the mouse — the one thing the console cannot
          be, since it is a fact about the page rather than about the ship. Only
          shown when the answer is *no*: telling a pilot who is already flying
          how to start flying is a permanent label for a transient state. */}
      {/* Only where there is a pointer to lock. A phone has no click to give
          and no lock to take, so this would be an instruction that cannot be
          followed sitting over the one thing it is covering. */}
      {!locked && !touchFlying && (
        <div
          style={{
            ...PANEL,
            bottom: 178,
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: 11,
            opacity: 0.7,
          }}
        >
          click to take the controls
        </div>
      )}

      {/* What you just hit, at the edges of the view rather than as a card.
          The card sat where the docking prompt, the jump lock and the radio all
          sit, so the one message that arrives unannounced was the one most
          likely to be underneath something. See `DamageVignette`. */}
      {gauges.integrity > 0 && <DamageVignette hit={gauges.collision} />}

      {/* The hull is gone.
          There is no rebuy yet — that is the insurance mechanic from the design
          doc and it needs a station and a balance to charge against. Until then
          this says plainly what happened and puts you back in a berth, rather
          than pretending a destroyed ship is a flyable one. */}
      {hullLost && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            // Opaque. Half the time a hull is lost it is lost *inside* the
            // thing that killed it, and watching yourself drift through a
            // planet's crust undercuts the moment rather badly.
            background: '#0b0403',
            color: HUD_TEXT,
          }}
        >
          <div style={{ fontSize: 26, letterSpacing: 2, color: '#ff5f6b' }}>HULL LOST</div>
          <div style={{ fontSize: 13, opacity: 0.6 }}>
            {/* Heat is not a collision, and saying "collision with hull
                temperature" reads as a bug rather than as a death. */}
            {!gauges.collision
              ? 'Ship destroyed'
              : gauges.collision.body === 'hull temperature'
                ? 'Hull temperature exceeded'
                : `Collision with ${gauges.collision.body}`}
          </div>
          {/*
              What it actually cost, in full, here.
              This was a lesson card — the same text, fired on the latch and
              drawn over the top of this screen. Two centred panels saying
              related things about the same event, one of them covering the
              buttons of the other, which is a worse way to read one paragraph.

              So the screen says it, and it says it every time rather than once
              per account: a player losing their second ship is being told what
              they lost, not being taught a system, and there is no version of
              this where the information is unwelcome.
          */}
          <div
            style={{
              maxWidth: 420,
              textAlign: 'center',
              fontSize: 11.5,
              lineHeight: 1.55,
              opacity: 0.55,
              display: 'flex',
              flexDirection: 'column',
              gap: 7,
            }}
          >
            {LESSONS['hull-lost']!.lines.map((line, index) => (
              <p key={index} style={{ margin: 0 }}>
                {line}
              </p>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              /*
               * Dismissed before the tow, and dismissed *here* rather than by
               * the integrity going back up.
               *
               * The replacement ship arrives asynchronously — the wreck is a
               * server round trip — so waiting for a healthy hull to clear this
               * would leave the screen up over a game that had already carried
               * on. The effect above only fires when `integrity` changes value,
               * so clearing it cannot immediately re-latch.
               */
              setHullLost(false);
              /*
               * Towed in after a loss, and told where to. Nobody is charged for
               * the berth they are dragged to, and the ship they arrive in is a
               * starter — see the wreck action.
               *
               * Empty means nowhere to tow to, which is a legitimate answer
               * rather than a missing one: lose a hull in one of the three in ten
               * systems with no station and there is no berth to drag you to. The
               * wreck action already reads it that way — it leaves the berth null
               * and hands back a starter in the same system.
               */
              onHullLost(stationId);
            }}
            style={{ ...ACTION, marginTop: 6, padding: '9px 18px' }}
          >
            return to dock
          </button>

          {/* ── Starting over ──────────────────────────────────────────────
              Two clicks, and the second one is the only place the word
              "permanently" appears. This wipes an account rather than a ship,
              there is no undo, and it sits one button away from the one
              everybody presses — so it asks twice and says plainly what goes.

              Offered here because losing a first hull is exactly when somebody
              wants to begin properly. */}
          {onResetAccount && (
            <div style={{ marginTop: 18, textAlign: 'center' }}>
              {resetArmed ? (
                <>
                  <div style={{ fontSize: 11, color: '#ff5f6b', maxWidth: 340, marginBottom: 7 }}>
                    This clears the account permanently — ship, blueprint, cargo,
                    banked linelits, faction standing and missions. You will start
                    again from the introduction.
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setResetArmed(false);
                        setHullLost(false);
                        onResetAccount();
                      }}
                      style={{
                        ...ACTION,
                        padding: '7px 14px',
                        borderColor: '#ff5f6b',
                        color: '#ff5f6b',
                      }}
                    >
                      erase everything
                    </button>
                    <button
                      type="button"
                      onClick={() => setResetArmed(false)}
                      style={{ ...ACTION, padding: '7px 14px' }}
                    >
                      cancel
                    </button>
                  </div>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setResetArmed(true)}
                  style={{
                    ...ACTION,
                    padding: '6px 12px',
                    fontSize: 11,
                    opacity: 0.65,
                  }}
                >
                  reset account &amp; replay the introduction
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Docking, offered rather than always available.
          A button that put you back in a berth from anywhere in the system made
          the berth a menu item. This one only exists once the ship is actually
          sitting in the capture volume over a plate, which is why the volume is
          drawn at all. */}
      {lit && gauges.dock && (
        <div
          style={{
            ...PANEL,
            ...dark,
            // Clear of the console, which stands 136 tall off a 16 margin. At
            // 128 this sat on top of it.
            bottom: 170,
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            borderColor:
              gauges.dock.approach === 'captured'
                ? 'rgba(127,180,111,0.8)'
                : 'rgba(127,212,255,0.35)',
          }}
        >
          <div style={{ fontSize: 10, opacity: 0.5, textTransform: 'uppercase', letterSpacing: 1.4 }}>
            {gauges.dock.stationName} · pad {gauges.dock.padId}
            {gauges.dock.fee > 0 && (
              // Said before the button, not after the money has gone.
              <span style={{ color: '#ffb347' }}> · {gauges.dock.fee}L oversize fee</span>
            )}
          </div>

          {/* A hull with legs has to put them down. One with none left is allowed
              down anyway and pays for it — see `bellyLand`. */}
          {gauges.dock.approach === 'captured' && !gear && !gauges.dock.belly ? (
            <div style={{ marginTop: 4, fontSize: 12, color: '#ffb347' }}>
              lower the landing gear{SHOW_KEY_HINTS ? ' (G)' : ''}
            </div>
          ) : gauges.dock.approach === 'captured' && gauges.dock.how === 'raised' ? (
            /* Over the plate with the legs up.
               The button used to appear and work, so there was nothing to
               notice; now it says the one thing that is missing, because a
               control that quietly does nothing is worse than no control. */
            <div style={{ marginTop: 6, fontSize: 11.5, color: '#ff8f6b' }}>
              lower the landing gear to set down
            </div>
          ) : gauges.dock.approach === 'captured' ? (
            <>
              {gauges.dock.belly && (
                <div style={{ marginTop: 4, fontSize: 11.5, color: '#ff8f6b' }}>
                  no landing gear · emergency set-down will cost{' '}
                  {Math.round(BELLY_LANDING_TOLL * 100)}% of every wall
                </div>
              )}
            <button
              type="button"
              {...tapProps(() =>
                onDock({
                  stationId: gauges.dock!.stationId,
                  padId: gauges.dock!.padId,
                  fee: gauges.dock!.fee,
                  belly: gauges.dock!.belly,
                }),
              )}
              style={{
                ...ACTION,
                marginTop: 6,
                borderColor: gauges.dock.belly ? 'rgba(255,143,107,0.8)' : 'rgba(127,180,111,0.8)',
                color: gauges.dock.belly ? '#ff8f6b' : '#7fb46f',
              }}
            >
              {gauges.dock.belly
                ? `set down on the hull${SHOW_KEY_HINTS ? ' (F)' : ''}`
                : `dock${SHOW_KEY_HINTS ? ' (F)' : ''}`}
            </button>
            </>
          ) : (
            <div style={{ marginTop: 4, fontSize: 12, opacity: 0.7 }}>
              line up over the pad
            </div>
          )}
        </div>
      )}


      {/* The one thing that survives the blackout, because it is the thing
          explaining it. Outside `SystemsPower` deliberately: a console that
          went dark with no word about why would read as a fault, which is the
          exact impression the blackout exists to avoid. */}
      {powerVisible(cardPower) && (gauges.crossing.phase === 'running' || welcome) && (
        <div
          style={{
            ...PANEL,
            ...powerStyle(cardPower),
            bottom: 210,
            left: '50%',
            // Its own centring has to survive the tube's own transform, so the
            // two are composed rather than one overwriting the other.
            transform: `translateX(-50%) ${powerStyle(cardPower).transform ?? ''}`,
            textAlign: 'center',
          }}
        >
          {welcome ? (
            <>
              <div style={{ fontSize: 11, color: HUD_ORANGE, letterSpacing: 1.4 }}>
                WELCOME TO {welcome.toUpperCase()}
              </div>
              <div style={{ fontSize: 10, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 1.4, marginTop: 3 }}>
                systems restored
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 11, color: HUD_ORANGE, letterSpacing: 1.4 }}>
                JUMP LOCKED · CONTROLS RELEASED
              </div>
              <div style={{ fontSize: 10, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 1.4, marginTop: 3 }}>
                {gauges.powered
                  ? 'accelerating to jump speed'
                  : 'electrical systems down for the crossing · fly safe'}
              </div>
            </>
          )}
        </div>
      )}

    </>
  );
}

/**
 * What the jump module says while it is committing to a crossing.
 *
 * ── Why the module keeps working instead of being switched off ──────────────
 *
 * Confirming a jump used to take the module away — reasonable when the console
 * stayed up, and redundant now that the whole console goes dark for the crossing
 * anyway. Two things hiding the same panel meant the module went blank *before*
 * the blackout that explains blankness, so for a second or so it read as a
 * failure rather than as a sequence.
 *
 * So it runs instead. The hold is the calibration, the steps are what it is
 * doing while you hold, and the last line hands over to the blackout the
 * console is about to perform — "systems down for the crossing" is already what
 * the card says a moment later, and this is the module agreeing to it rather
 * than being cut off mid-sentence.
 *
 * Null whenever the module is a switch you could throw. The instrument only
 * exists once the decision has been made.
 */
/** The speed the calibration counts up to. Just under the blackout at 700. */
const CALIBRATION_TARGET = 650;

/** How often the module reports, in ms. */
const CALIBRATION_TICK = 500;

/**
 * Too small a run to measure against, in world units a second.
 *
 * A jump needs warp lit and warp reaches 800, so a pilot who confirms while
 * travelling can begin the run *above* 680 — leaving no speed range at all to
 * count through. Below this the sequence is paced by the powered window instead,
 * so it always plays rather than opening on its own last line.
 */
const CALIBRATION_MIN_SPAN = 60;

const CALIBRATION_STEPS = [
  'aligning trajectory',
  'resolving vector',
  'charging jump drive',
  'priming bing module',
  'validating entry vector',
  'shutting down electrical systems',
];

/**
 * What the module says it is doing, and how far through it is.
 *
 * ── The percentage is measured, not counted ─────────────────────────────────
 *
 * Each of the four steps owns a quarter of the distance between the speed the
 * run started at and `CALIBRATION_TARGET`, and the figure is that step's share
 * of actual progress. Nothing decides in advance how many times a step reports
 * or how far it moves each time: a long run climbs slowly and prints many small
 * increments, a short one takes bigger strides and prints fewer. The pacing is a
 * consequence of the ship rather than of a script.
 */
function jumpCalibration(
  crossing: JumpSequence,
  startSpeed: number | null,
  speed: number,
): string | null {
  /*
   * Nothing while the target is being locked.
   *
   * The hold is the *decision*, and it already has a readout of its own — the
   * bar counting down to commitment. A calibration running underneath it would
   * be describing work the ship has not agreed to do yet, and the module would
   * finish calibrating at the exact moment the jump actually began.
   */
  if (crossing.phase !== 'running') return null;

  const from = startSpeed ?? 0;
  const span = CALIBRATION_TARGET - from;

  // Enough room to measure against, or fall back to the window that keeps the
  // console alive — see `CALIBRATION_MIN_SPAN`.
  const through =
    span >= CALIBRATION_MIN_SPAN
      ? Math.min(1, Math.max(0, (Math.abs(speed) - from) / span))
      : Math.min(1, Math.max(0, crossing.elapsed / JUMP_CALIBRATION));

  const at = Math.min(CALIBRATION_STEPS.length - 1, Math.floor(through * CALIBRATION_STEPS.length));

  // This step's own share of the progress, so each one counts itself from
  // nothing to full rather than reporting a slice of the whole run.
  const within = through * CALIBRATION_STEPS.length - at;

  // Capped below a hundred: a step reading 100% has finished, and the thing to
  // show then is the next one.
  const pct = Math.min(99, Math.floor(within * 100));

  return `${CALIBRATION_STEPS[at]!} (${pct}%)`;
}

const PANEL: React.CSSProperties = {
  position: 'absolute',
  background: HUD_BACKING,
  border: `1px solid ${HUD_EDGE}`,
  color: HUD_TEXT,
  padding: '10px 14px',
  fontSize: 13,
  lineHeight: 1.5,
};

const LEGEND: React.CSSProperties = {
  fontSize: 10,
  opacity: 0.45,
  textTransform: 'uppercase',
  letterSpacing: 1.4,
};

const ACTION: React.CSSProperties = {
  background: 'rgba(20, 12, 7, 0.7)',
  border: `1px solid ${HUD_EDGE}`,
  color: HUD_TEXT,
  font: 'inherit',
  fontSize: 12,
  padding: '6px 12px',
  cursor: 'pointer',
};
